import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Link, useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Building2, FileText, Package, Settings, BarChart3, DollarSign,
  Calendar, FileCheck, Menu, X, BookOpen, Download, Receipt
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Toaster, toast } from "sonner";
import {
  catalogoItemsAPI,
  categoriasAPI,
  rubrosAPI,
  obrasAPI,
  presupuestosAPI,
  presupuestoItemsAPI,
  cotizacionesAPI,
  cronogramasAPI,
  cronogramaTareasAPI,
  dashboardAPI,
  gastosAPI,
  conceptosFinancierosAPI,
  movimientosFinancierosAPI
} from "@/services/api";
import axios from 'axios';

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
});
import { formatCurrency, formatDate } from "@/utils/formatters";
import { UNIDADES_MEDIDA } from "@/constants/unidades";
import Layout from "@/components/Layout";
import Pagination from "@/components/Pagination";

// ==================== DASHBOARD ====================
function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    obras_activas: 0,
    presupuestos_borrador: 0,
    cotizaciones_pendientes: 0
  });
  const [obras, setObras] = useState([]);
  const [presupuestos, setPresupuestos] = useState([]);
  const [presupuestosAprobados, setPresupuestosAprobados] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [resStats, resObras, resPresupuestos] = await Promise.all([
        dashboardAPI.getStats(),
        obrasAPI.getAll(),
        presupuestosAPI.getAll()
      ]);

      setStats(resStats.data);

      // Ordenar obras por fecha más reciente y tomar las últimas 5
      const sortedObras = resObras.data.sort((a, b) =>
        new Date(b.fecha_creacion) - new Date(a.fecha_creacion)
      ).slice(0, 5);
      setObras(sortedObras);

      // Filtrar presupuestos en borrador y ordenar
      const borradores = resPresupuestos.data
        .filter(p => p.estado === "Borrador")
        .sort((a, b) => new Date(b.fecha_creacion) - new Date(a.fecha_creacion))
        .slice(0, 5);
      setPresupuestos(borradores);

      // Filtrar presupuestos aprobados - ÚLTIMOS por fecha
      const aprobados = resPresupuestos.data
        .filter(p => (p.estado === "Listo" || p.estado === "Cerrado") && p.total_general > 0)
        .sort((a, b) => new Date(b.fecha_creacion) - new Date(a.fecha_creacion))
        .slice(0, 5);
      setPresupuestosAprobados(aprobados);
    } catch (error) {
      toast.error("Error al cargar datos del dashboard");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-slate-900">Dashboard</h1>
        <div className="py-12 text-center text-slate-600">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-slate-900">Dashboard</h1>
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-full font-bold text-sm shadow-lg animate-pulse">
          v2.14.12 🔥
        </div>
      </div>

      {/* Stats Cards + Accesos Rápidos con títulos - Diseño Profesional Corporativo */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
        {/* Grupo 1: Resumen General */}
        <div className="md:col-span-3 space-y-3">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-slate-600" />
            <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Resumen General</h2>
          </div>
          <div className="grid grid-cols-3 gap-4">
            {/* Obras Activas */}
            <Card className="relative overflow-hidden bg-gradient-to-br from-orange-500 to-orange-600 hover:shadow-2xl transition-all cursor-pointer h-[140px] border-0 group" onClick={() => navigate('/obras')}>
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-black/10 rounded-full -ml-12 -mb-12"></div>
              <CardContent className="relative h-full flex items-center justify-between p-5">
                <div className="flex-1 z-10">
                  <p className="text-xs font-medium text-orange-100 uppercase tracking-wide mb-2">Obras Activas</p>
                  <div className="text-4xl font-bold text-white mb-1">{stats.obras_activas}</div>
                  <p className="text-xs text-orange-100">Proyectos en curso</p>
                </div>
                <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform z-10">
                  <Building2 className="w-7 h-7 text-white" />
                </div>
              </CardContent>
            </Card>

            {/* Presupuestos Borrador */}
            <Card className="relative overflow-hidden bg-gradient-to-br from-blue-500 to-blue-600 hover:shadow-2xl transition-all cursor-pointer h-[140px] border-0 group" onClick={() => navigate('/presupuestos')}>
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-black/10 rounded-full -ml-12 -mb-12"></div>
              <CardContent className="relative h-full flex items-center justify-between p-5">
                <div className="flex-1 z-10">
                  <p className="text-xs font-medium text-blue-100 uppercase tracking-wide mb-2">Presupuestos</p>
                  <div className="text-4xl font-bold text-white mb-1">{stats.presupuestos_borrador}</div>
                  <p className="text-xs text-blue-100">En borrador</p>
                </div>
                <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform z-10">
                  <DollarSign className="w-7 h-7 text-white" />
                </div>
              </CardContent>
            </Card>

            {/* Cotizaciones Pendientes */}
            <Card className="relative overflow-hidden bg-gradient-to-br from-amber-500 to-amber-600 hover:shadow-2xl transition-all cursor-pointer h-[140px] border-0 group" onClick={() => navigate('/cotizaciones')}>
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-black/10 rounded-full -ml-12 -mb-12"></div>
              <CardContent className="relative h-full flex items-center justify-between p-5">
                <div className="flex-1 z-10">
                  <p className="text-xs font-medium text-amber-100 uppercase tracking-wide mb-2">Cotizaciones</p>
                  <div className="text-4xl font-bold text-white mb-1">{stats.cotizaciones_pendientes}</div>
                  <p className="text-xs text-amber-100">Pendientes</p>
                </div>
                <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform z-10">
                  <FileCheck className="w-7 h-7 text-white" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Grupo 2: Accesos Rápidos - Diseño Corporativo Minimalista */}
        <div className="md:col-span-3 space-y-3">
          <div className="flex items-center gap-2">
            <Menu className="w-4 h-4 text-slate-600" />
            <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Accesos Rápidos</h2>
          </div>
          <div className="grid grid-cols-3 gap-4">
            {/* Nueva Obra */}
            <Card
              className="bg-white hover:shadow-xl transition-all cursor-pointer h-[140px] border border-slate-200 hover:border-orange-400 group"
              onClick={() => navigate('/obras')}
            >
              <CardContent className="h-full flex flex-col justify-between p-4">
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center group-hover:bg-orange-500 transition-colors">
                    <Building2 className="w-5 h-5 text-orange-600 group-hover:text-white transition-colors" />
                  </div>
                  <div className="text-xs text-slate-400 group-hover:text-orange-500 transition-colors">+</div>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-800 mb-1">Nueva Obra</h3>
                  <p className="text-xs text-slate-500">Crear proyecto</p>
                </div>
              </CardContent>
            </Card>

            {/* Nuevo Presupuesto */}
            <Card
              className="bg-white hover:shadow-xl transition-all cursor-pointer h-[140px] border border-slate-200 hover:border-blue-400 group"
              onClick={() => navigate('/presupuestos')}
            >
              <CardContent className="h-full flex flex-col justify-between p-4">
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-500 transition-colors">
                    <DollarSign className="w-5 h-5 text-blue-600 group-hover:text-white transition-colors" />
                  </div>
                  <div className="text-xs text-slate-400 group-hover:text-blue-500 transition-colors">+</div>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-800 mb-1">Nuevo Presup.</h3>
                  <p className="text-xs text-slate-500">Crear cotización</p>
                </div>
              </CardContent>
            </Card>

            {/* Ver Catálogo */}
            <Card
              className="bg-white hover:shadow-xl transition-all cursor-pointer h-[140px] border border-slate-200 hover:border-purple-400 group"
              onClick={() => navigate('/catalogo')}
            >
              <CardContent className="h-full flex flex-col justify-between p-4">
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center group-hover:bg-purple-500 transition-colors">
                    <Package className="w-5 h-5 text-purple-600 group-hover:text-white transition-colors" />
                  </div>
                  <div className="text-xs text-slate-400 group-hover:text-purple-500 transition-colors">→</div>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-800 mb-1">Ver Catálogo</h3>
                  <p className="text-xs text-slate-500">Items y precios</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Análisis de Costos vs Presupuesto - Tarjetas Compactas */}
      {presupuestosAprobados.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Análisis de Costos vs Presupuesto</CardTitle>
            <CardDescription>Últimas 4 obras - Comparación de costos y márgenes</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              {presupuestosAprobados.slice(0, 4).map((presupuesto) => {
                const obra = obras.find(o => o.id === presupuesto.obra_id);

                // CÁLCULO CORRECTO: Usar los porcentajes REALES del presupuesto
                const manoObraConLeyes = presupuesto.total_mano_obra * (1 + (presupuesto.porcentaje_leyes_sociales || 0) / 100);
                const costoBase = presupuesto.total_materiales + manoObraConLeyes;
                const margen = presupuesto.total_general - costoBase;
                const porcentajeMargen = costoBase > 0 ? ((margen / presupuesto.total_general) * 100) : 0;
                const porcentajeMateriales = (presupuesto.total_materiales / presupuesto.total_general) * 100;
                const porcentajeManoObra = (presupuesto.total_mano_obra / presupuesto.total_general) * 100;

                // Indicador de salud del margen
                const saludMargen = porcentajeMargen > 25 ? '✓' : porcentajeMargen > 15 ? '⚠' : '✗';
                const colorMargen = porcentajeMargen > 25 ? 'text-green-600' : porcentajeMargen > 15 ? 'text-yellow-600' : 'text-red-600';

                return (
                  <div
                    key={presupuesto.id}
                    className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow bg-white"
                  >
                    {/* Header Compacto */}
                    <div className="mb-3 pb-3 border-b border-slate-100">
                      <h4 className="font-semibold text-slate-900 text-sm truncate">
                        {obra?.nombre || "Obra"}
                      </h4>
                      <p className="text-xs text-slate-600 truncate mt-0.5">
                        {presupuesto.nombre}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-xs text-slate-600">
                          v{presupuesto.version}
                        </p>
                        <Badge variant="outline" className="text-xs">{presupuesto.estado}</Badge>
                      </div>
                      {presupuesto.version_de_numero && (
                        <div className="mt-1.5 px-2 py-0.5 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700 inline-block">
                          📋 Basado en: {presupuesto.version_de_numero}
                        </div>
                      )}
                    </div>

                    {/* Total General */}
                    <div className="text-center mb-4 bg-slate-50 rounded-lg py-2">
                      <div className="text-xl font-bold text-slate-900">
                        {formatCurrency(presupuesto.total_general)}
                      </div>
                      <div className="text-xs text-slate-500">Total General</div>
                    </div>

                    {/* Materiales */}
                    <div className="mb-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-slate-700">Materiales</span>
                        <span className="text-xs font-semibold text-blue-600">
                          {formatCurrency(presupuesto.total_materiales / 1000)}k
                        </span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                        <div
                          className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all"
                          style={{ width: `${porcentajeMateriales}%` }}
                        ></div>
                      </div>
                      <div className="text-right text-xs text-slate-500 mt-0.5">
                        {porcentajeMateriales.toFixed(1)}%
                      </div>
                    </div>

                    {/* Mano de Obra */}
                    <div className="mb-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-slate-700">M. Obra</span>
                        <span className="text-xs font-semibold text-purple-600">
                          {formatCurrency(presupuesto.total_mano_obra / 1000)}k
                        </span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                        <div
                          className="bg-gradient-to-r from-purple-500 to-purple-600 h-2 rounded-full transition-all"
                          style={{ width: `${porcentajeManoObra}%` }}
                        ></div>
                      </div>
                      <div className="text-right text-xs text-slate-500 mt-0.5">
                        {porcentajeManoObra.toFixed(1)}%
                      </div>
                    </div>

                    {/* Margen - Destacado */}
                    <div className="mb-2 bg-green-50 border border-green-200 rounded-lg p-2">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-1">
                          <span className="text-xs font-semibold text-slate-800">Margen</span>
                          <span className={`text-sm ${colorMargen}`}>{saludMargen}</span>
                        </div>
                        <span className="text-xs font-bold text-green-700">
                          {formatCurrency(margen / 1000)}k
                        </span>
                      </div>
                      <div className="w-full bg-green-200 rounded-full h-2 overflow-hidden">
                        <div
                          className="bg-gradient-to-r from-green-500 to-green-600 h-2 rounded-full transition-all"
                          style={{ width: `${porcentajeMargen}%` }}
                        ></div>
                      </div>
                      <div className="text-right text-xs text-green-700 font-medium mt-0.5">
                        {porcentajeMargen.toFixed(1)}%
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Obras Recientes */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Obras Recientes</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/obras')}
              >
                Ver todas →
              </Button>
            </div>
            <CardDescription>Últimas obras creadas o actualizadas</CardDescription>
          </CardHeader>
          <CardContent>
            {obras.length === 0 ? (
              <div className="py-8 text-center text-slate-500">
                No hay obras creadas aún
              </div>
            ) : (
              <div className="space-y-3">
                {obras.map((obra) => (
                  <div
                    key={obra.id}
                    onClick={() => navigate(`/obras`)}
                    className="p-3 border border-slate-200 rounded-lg hover:border-deflorencia hover:bg-orange-50 transition-colors cursor-pointer"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-slate-900 truncate">{obra.nombre}</h4>
                        <p className="text-sm text-slate-600">{obra.cliente}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant={obra.estado === 'Activa' ? 'default' : 'secondary'} className="text-xs">
                            {obra.estado}
                          </Badge>
                          {obra.ubicacion && (
                            <span className="text-xs text-slate-500">{obra.ubicacion}</span>
                          )}
                        </div>
                      </div>
                      <div className="text-right ml-2">
                        <div className="text-xs text-slate-500">
                          {formatDate(obra.fecha_creacion)}
                        </div>
                        {obra.m2 && (
                          <div className="text-xs font-medium text-deflorencia mt-1">
                            {obra.m2} m²
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Presupuestos en Borrador */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Presupuestos en Borrador</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/presupuestos')}
              >
                Ver todos →
              </Button>
            </div>
            <CardDescription>Presupuestos pendientes de aprobar</CardDescription>
          </CardHeader>
          <CardContent>
            {presupuestos.length === 0 ? (
              <div className="py-8 text-center text-slate-500">
                No hay presupuestos en borrador
              </div>
            ) : (
              <div className="space-y-3">
                {presupuestos.map((presupuesto) => {
                  const obra = obras.find(o => o.id === presupuesto.obra_id);
                  return (
                    <div
                      key={presupuesto.id}
                      onClick={() => navigate(`/presupuestos`)}
                      className="p-3 border border-slate-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors cursor-pointer"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-slate-900 truncate">
                            {presupuesto.nombre} <span className="text-sm text-slate-500">v{presupuesto.version}</span>
                          </h4>
                          {obra && (
                            <p className="text-sm text-slate-600">{obra.nombre}</p>
                          )}
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs">
                              {presupuesto.estado}
                            </Badge>
                          </div>
                        </div>
                        <div className="text-right ml-2">
                          <div className="text-xs text-slate-500">
                            {formatDate(presupuesto.fecha_creacion)}
                          </div>
                          {presupuesto.total_general > 0 && (
                            <div className="text-sm font-medium text-blue-600 mt-1">
                              {formatCurrency(presupuesto.total_general)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Accesos Rápidos</CardTitle>
          <CardDescription>Acciones frecuentes para agilizar tu trabajo</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Button
              variant="outline"
              className="h-20 flex flex-col items-center justify-center gap-2 hover:border-deflorencia hover:bg-orange-50"
              onClick={() => navigate('/obras')}
            >
              <Building2 className="w-6 h-6 text-deflorencia" />
              <span className="text-sm font-medium">Nueva Obra</span>
            </Button>

            <Button
              variant="outline"
              className="h-20 flex flex-col items-center justify-center gap-2 hover:border-blue-500 hover:bg-blue-50"
              onClick={() => navigate('/presupuestos')}
            >
              <DollarSign className="w-6 h-6 text-blue-600" />
              <span className="text-sm font-medium">Nuevo Presupuesto</span>
            </Button>

            <Button
              variant="outline"
              className="h-20 flex flex-col items-center justify-center gap-2 hover:border-green-500 hover:bg-green-50"
              onClick={() => navigate('/catalogo')}
            >
              <Package className="w-6 h-6 text-green-600" />
              <span className="text-sm font-medium">Ver Catálogo</span>
            </Button>

            <Button
              variant="outline"
              className="h-20 flex flex-col items-center justify-center gap-2 hover:border-purple-500 hover:bg-purple-50"
              onClick={() => navigate('/configuracion')}
            >
              <Settings className="w-6 h-6 text-purple-600" />
              <span className="text-sm font-medium">Configuración</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== OBRAS ====================
function Obras() {
  const [obras, setObras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingObra, setEditingObra] = useState(null);
  const [filterEstado, setFilterEstado] = useState("Todos");
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 25;

  useEffect(() => {
    setCurrentPage(1);
  }, [filterEstado]);


  useEffect(() => {
    loadObras();
  }, []);

  const loadObras = async () => {
    try {
      const res = await obrasAPI.getAll();
      // Ordenar por fecha de creación descendente (más recientes primero)
      const sortedObras = res.data.sort((a, b) => {
        return new Date(b.fecha_creacion) - new Date(a.fecha_creacion);
      });
      setObras(sortedObras);
    } catch (error) {
      toast.error("Error al cargar obras");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateObra = () => {
    setEditingObra(null);
    setShowModal(true);
  };

  const handleEditObra = (e, obra) => {
    e.stopPropagation();
    setEditingObra(obra);
    setShowModal(true);
  };

  const handleDeleteObra = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("¿Está seguro de eliminar esta obra? Se eliminarán también sus presupuestos.")) return;

    try {
      await obrasAPI.delete(id);
      toast.success("Obra eliminada exitosamente");
      loadObras();
    } catch (error) {
      toast.error("Error al eliminar obra");
    }
  };

  const handleModalClose = (reload) => {
    setShowModal(false);
    setEditingObra(null);
    if (reload) loadObras();
  };

  const filteredObras = filterEstado === "Todos"
    ? obras
    : obras.filter(o => o.estado === filterEstado);

  // Paginación
  const totalPages = Math.ceil(filteredObras.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedObras = filteredObras.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-slate-900">Obras</h1>
        <Button
          onClick={handleCreateObra}
          className="bg-deflorencia hover:bg-[#ff9100]"
        >
          + Nueva Obra
        </Button>
      </div>

      {/* Filtro por Estado */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-2">
            <Button
              variant={filterEstado === "Todos" ? "default" : "outline"}
              onClick={() => setFilterEstado("Todos")}
              size="sm"
            >
              Todas ({obras.length})
            </Button>
            <Button
              variant={filterEstado === "Activa" ? "default" : "outline"}
              onClick={() => setFilterEstado("Activa")}
              size="sm"
            >
              Activas ({obras.filter(o => o.estado === "Activa").length})
            </Button>
            <Button
              variant={filterEstado === "Pausada" ? "default" : "outline"}
              onClick={() => setFilterEstado("Pausada")}
              size="sm"
            >
              Pausadas ({obras.filter(o => o.estado === "Pausada").length})
            </Button>
            <Button
              variant={filterEstado === "Finalizada" ? "default" : "outline"}
              onClick={() => setFilterEstado("Finalizada")}
              size="sm"
            >
              Finalizadas ({obras.filter(o => o.estado === "Finalizada").length})
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Obras */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-12 text-center text-slate-600">Cargando obras...</div>
          ) : filteredObras.length === 0 ? (
            <div className="py-12 text-center text-slate-600">
              {filterEstado === "Todos" ? "No hay obras creadas" : `No hay obras ${filterEstado.toLowerCase()}s`}
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Ubicación</TableHead>
                    <TableHead>M²</TableHead>
                    <TableHead>Arquitecto</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedObras.map((obra) => (
                    <TableRow key={obra.id}>
                      <TableCell className="font-medium">{obra.nombre}</TableCell>
                      <TableCell>
                        <div>
                          <div>{obra.cliente}</div>
                          {obra.contacto_cliente && (
                            <div className="text-xs text-slate-500">{obra.contacto_cliente}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{obra.ubicacion || '-'}</TableCell>
                      <TableCell>{obra.m2 ? `${obra.m2} m²` : '-'}</TableCell>
                      <TableCell className="text-sm text-slate-600">{obra.arquitecto || '-'}</TableCell>
                      <TableCell>
                        <Badge variant={obra.estado === 'Activa' ? 'default' : 'secondary'}>
                          {obra.estado}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDate(obra.fecha_creacion)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => handleEditObra(e, obra)}
                        >
                          Editar
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => handleDeleteObra(e, obra.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          Eliminar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                totalItems={filteredObras.length}
                itemsPerPage={ITEMS_PER_PAGE}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Modal Crear/Editar */}
      {showModal && (
        <ObraModal
          obra={editingObra}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
}

// Modal para Crear/Editar Obra
function ObraModal({ obra, onClose }) {
  const [formData, setFormData] = useState({
    nombre: obra?.nombre || "",
    cliente: obra?.cliente || "",
    contacto_cliente: obra?.contacto_cliente || "",
    ubicacion: obra?.ubicacion || "",
    m2: obra?.m2 || "",
    arquitecto: obra?.arquitecto || "",
    fecha_inicio: obra?.fecha_inicio || "",
    notas: obra?.notas || "",
    estado: obra?.estado || "Activa"
  });
  const [selectedRubros, setSelectedRubros] = useState(obra?.rubros_ids || []);
  const [availableRubros, setAvailableRubros] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingRubros, setLoadingRubros] = useState(true);
  const [showNewRubroForm, setShowNewRubroForm] = useState(false);
  const [newRubro, setNewRubro] = useState({ nombre: "", descripcion: "" });
  const [creatingRubro, setCreatingRubro] = useState(false);

  useEffect(() => {
    loadRubros();
  }, []);

  const loadRubros = async () => {
    try {
      const res = await rubrosAPI.getAll();
      setAvailableRubros(res.data);
    } catch (error) {
      toast.error("Error al cargar rubros");
    } finally {
      setLoadingRubros(false);
    }
  };

  const toggleRubro = (rubroId) => {
    setSelectedRubros(prev =>
      prev.includes(rubroId)
        ? prev.filter(id => id !== rubroId)
        : [...prev, rubroId]
    );
  };

  const handleCreateRubro = async () => {
    if (!newRubro.nombre.trim()) {
      toast.error("El nombre del rubro es requerido");
      return;
    }

    setCreatingRubro(true);
    try {
      const res = await rubrosAPI.create({
        nombre: newRubro.nombre,
        descripcion: newRubro.descripcion,
        es_predefinido: false
      });

      // Agregar el nuevo rubro a la lista
      const createdRubro = res.data;
      setAvailableRubros(prev => [...prev, createdRubro]);

      // Seleccionarlo automáticamente
      setSelectedRubros(prev => [...prev, createdRubro.id]);

      // Limpiar y cerrar formulario
      setNewRubro({ nombre: "", descripcion: "" });
      setShowNewRubroForm(false);
      toast.success("Rubro creado y agregado exitosamente");
    } catch (error) {
      toast.error("Error al crear rubro");
    } finally {
      setCreatingRubro(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Limpiar campos vacíos
      const cleanData = { ...formData };
      Object.keys(cleanData).forEach(key => {
        if (cleanData[key] === "" || cleanData[key] === null || cleanData[key] === undefined) {
          delete cleanData[key];
        }
      });

      if (obra) {
        await obrasAPI.update(obra.id, cleanData, selectedRubros);
        toast.success("Obra actualizada exitosamente");
      } else {
        await obrasAPI.create(cleanData, selectedRubros);
        toast.success("Obra creada exitosamente");
      }
      onClose(true);
    } catch (error) {
      console.error("Error al guardar obra:", error);
      console.error("Response:", error.response?.data);
      toast.error(obra ? "Error al actualizar obra" : "Error al crear obra");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg max-w-4xl w-full my-8">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-2xl font-bold text-slate-900">
            {obra ? "Editar Obra" : "Nueva Obra"}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
          {/* Datos Básicos */}
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">Datos Básicos</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Nombre de la Obra *</Label>
                <Input
                  required
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  placeholder="Ej: Edificio Solanas"
                />
              </div>

              <div>
                <Label>Cliente *</Label>
                <Input
                  required
                  value={formData.cliente}
                  onChange={(e) => setFormData({ ...formData, cliente: e.target.value })}
                  placeholder="Nombre del cliente"
                />
              </div>

              <div>
                <Label>Contacto del Cliente</Label>
                <Input
                  value={formData.contacto_cliente}
                  onChange={(e) => setFormData({ ...formData, contacto_cliente: e.target.value })}
                  placeholder="Teléfono o email"
                />
              </div>

              <div>
                <Label>Ubicación</Label>
                <Input
                  value={formData.ubicacion}
                  onChange={(e) => setFormData({ ...formData, ubicacion: e.target.value })}
                  placeholder="Dirección o localidad"
                />
              </div>

              <div>
                <Label>Superficie (m²)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.m2}
                  onChange={(e) => setFormData({ ...formData, m2: parseFloat(e.target.value) || "" })}
                  placeholder="0.00"
                />
              </div>

              <div>
                <Label>Arquitecto</Label>
                <Input
                  value={formData.arquitecto}
                  onChange={(e) => setFormData({ ...formData, arquitecto: e.target.value })}
                  placeholder="Nombre del arquitecto"
                />
              </div>

              <div>
                <Label>Fecha de Inicio</Label>
                <Input
                  type="date"
                  value={formData.fecha_inicio}
                  onChange={(e) => setFormData({ ...formData, fecha_inicio: e.target.value })}
                />
              </div>

              <div>
                <Label>Estado</Label>
                <select
                  value={formData.estado}
                  onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md"
                >
                  <option value="Activa">Activa</option>
                  <option value="Pausada">Pausada</option>
                  <option value="Finalizada">Finalizada</option>
                  <option value="Cancelada">Cancelada</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <Label>Notas</Label>
                <textarea
                  value={formData.notas}
                  onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md"
                  rows="3"
                  placeholder="Notas o comentarios adicionales"
                />
              </div>
            </div>
          </div>

          {/* Rubros */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Rubros de la Obra</h3>
                <p className="text-sm text-slate-600">
                  Seleccione los rubros que formarán parte de esta obra
                </p>
              </div>
              <Button
                type="button"
                onClick={() => setShowNewRubroForm(!showNewRubroForm)}
                variant="outline"
                size="sm"
                className="text-deflorencia border-deflorencia hover:bg-orange-50"
              >
                + Crear Rubro Nuevo
              </Button>
            </div>

            {/* Formulario inline para crear nuevo rubro */}
            {showNewRubroForm && (
              <div className="mb-4 p-4 border-2 border-deflorencia rounded-lg bg-orange-50">
                <h4 className="font-semibold text-slate-900 mb-2">Nuevo Rubro</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>Nombre del Rubro *</Label>
                    <Input
                      value={newRubro.nombre}
                      onChange={(e) => setNewRubro({ ...newRubro, nombre: e.target.value })}
                      placeholder="Ej: Cielorrasos especiales"
                    />
                  </div>
                  <div>
                    <Label>Descripción</Label>
                    <Input
                      value={newRubro.descripcion}
                      onChange={(e) => setNewRubro({ ...newRubro, descripcion: e.target.value })}
                      placeholder="Opcional"
                    />
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button
                    type="button"
                    onClick={handleCreateRubro}
                    disabled={creatingRubro || !newRubro.nombre.trim()}
                    className="bg-deflorencia hover:bg-[#ff9100]"
                    size="sm"
                  >
                    {creatingRubro ? "Creando..." : "Crear y Agregar"}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => {
                      setShowNewRubroForm(false);
                      setNewRubro({ nombre: "", descripcion: "" });
                    }}
                    variant="outline"
                    size="sm"
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            )}

            {loadingRubros ? (
              <div className="py-8 text-center text-slate-600">Cargando rubros...</div>
            ) : availableRubros.length === 0 ? (
              <div className="py-8 text-center text-slate-600">
                No hay rubros disponibles. Cree uno nuevo arriba para comenzar.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-64 overflow-y-auto p-2 border border-slate-200 rounded-lg">
                {availableRubros.map((rubro) => (
                  <label
                    key={rubro.id}
                    className={`
                      border rounded-lg p-3 cursor-pointer transition-colors
                      ${selectedRubros.includes(rubro.id)
                        ? 'border-deflorencia bg-orange-50'
                        : 'border-slate-200 hover:border-slate-300'}
                    `}
                  >
                    <div className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        checked={selectedRubros.includes(rubro.id)}
                        onChange={() => toggleRubro(rubro.id)}
                        className="mt-1 rounded border-slate-300"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1">
                          <div className="font-medium text-sm truncate">{rubro.nombre}</div>
                          {rubro.es_predefinido && (
                            <Badge variant="outline" className="text-xs shrink-0">Pre</Badge>
                          )}
                        </div>
                        {rubro.descripcion && (
                          <div className="text-xs text-slate-600 truncate">{rubro.descripcion}</div>
                        )}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
            <p className="text-xs text-slate-500 mt-2">
              {selectedRubros.length} rubro{selectedRubros.length !== 1 ? 's' : ''} seleccionado{selectedRubros.length !== 1 ? 's' : ''}
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
            <Button
              type="button"
              variant="outline"
              onClick={() => onClose(false)}
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="bg-deflorencia hover:bg-[#ff9100]"
              disabled={saving}
            >
              {saving ? "Guardando..." : (obra ? "Actualizar" : "Crear Obra")}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ==================== PRESUPUESTOS ====================
function Presupuestos() {
  const navigate = useNavigate();
  const [presupuestos, setPresupuestos] = useState([]);
  const [obras, setObras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterEstado, setFilterEstado] = useState("Todos");
  const [filterObra, setFilterObra] = useState("Todas");
  const [showModal, setShowModal] = useState(false);
  const [selectedPresupuesto, setSelectedPresupuesto] = useState(null);
  const [expandedObras, setExpandedObras] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 25;


  useEffect(() => {
    setCurrentPage(1);
  }, [filterEstado, filterObra]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [resPresupuestos, resObras] = await Promise.all([
        presupuestosAPI.getAll(),
        obrasAPI.getAll()
      ]);
      // Ordenar por fecha más reciente
      const sorted = resPresupuestos.data.sort((a, b) =>
        new Date(b.fecha_creacion) - new Date(a.fecha_creacion)
      );
      setPresupuestos(sorted);
      setObras(resObras.data);
    } catch (error) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePresupuesto = () => {
    setSelectedPresupuesto(null);
    setShowModal(true);
  };

  const handleViewPresupuesto = (presupuesto) => {
    navigate(`/presupuestos/${presupuesto.id}`);
  };

  const handleDeletePresupuesto = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("¿Está seguro de eliminar este presupuesto?")) return;

    try {
      await presupuestosAPI.delete(id);
      toast.success("Presupuesto eliminado exitosamente");
      loadData();
    } catch (error) {
      toast.error("Error al eliminar presupuesto");
    }
  };

  const handleDuplicatePresupuesto = async (e, presupuesto) => {
    e.stopPropagation();
    // TODO: Implementar duplicación
    toast.info("Funcionalidad en desarrollo");
  };


  const toggleObraExpanded = (obraId) => {
    setExpandedObras(prev => ({
      ...prev,
      [obraId]: !prev[obraId]
    }));
  };



  const filteredPresupuestos = presupuestos.filter(p => {
    const matchEstado = filterEstado === "Todos" || p.estado === filterEstado;
    const matchObra = filterObra === "Todas" || p.obra_id === filterObra;
    return matchEstado && matchObra;
  });

  // Agrupar presupuestos por obra
  const presupuestosPorObra = filteredPresupuestos.reduce((acc, presupuesto) => {
    const obraId = presupuesto.obra_id;
    if (!acc[obraId]) {
      acc[obraId] = [];
    }
    acc[obraId].push(presupuesto);
    return acc;
  }, {});

  // Convertir a array para paginación
  const obrasConPresupuestos = Object.entries(presupuestosPorObra).map(([obraId, pres]) => ({
    obraId,
    obra: obras.find(o => o.id === obraId),
    presupuestos: pres.sort((a, b) => a.version - b.version)
  }));

  // Paginación
  const totalPages = Math.ceil(obrasConPresupuestos.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedObras = obrasConPresupuestos.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h1 className="text-3xl font-bold text-slate-900">Presupuestos</h1>
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-3 py-1 rounded-full font-bold text-xs shadow-lg">
            v2.14.12 🔥
          </div>
        </div>
        <Button
          onClick={handleCreatePresupuesto}
          className="bg-deflorencia hover:bg-[#ff9100]"
        >
          + Nuevo Presupuesto
        </Button>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-slate-600 mb-2 block">Filtrar por Estado</Label>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={filterEstado === "Todos" ? "default" : "outline"}
                  onClick={() => setFilterEstado("Todos")}
                  size="sm"
                >
                  Todos ({presupuestos.length})
                </Button>
                <Button
                  variant={filterEstado === "Borrador" ? "default" : "outline"}
                  onClick={() => setFilterEstado("Borrador")}
                  size="sm"
                >
                  Borrador ({presupuestos.filter(p => p.estado === "Borrador").length})
                </Button>
                <Button
                  variant={filterEstado === "Listo" ? "default" : "outline"}
                  onClick={() => setFilterEstado("Listo")}
                  size="sm"
                >
                  Listo ({presupuestos.filter(p => p.estado === "Listo").length})
                </Button>
                <Button
                  variant={filterEstado === "Cerrado" ? "default" : "outline"}
                  onClick={() => setFilterEstado("Cerrado")}
                  size="sm"
                >
                  Cerrado ({presupuestos.filter(p => p.estado === "Cerrado").length})
                </Button>
              </div>
            </div>

            <div>
              <Label className="text-sm text-slate-600 mb-2 block">Filtrar por Obra</Label>
              <select
                value={filterObra}
                onChange={(e) => setFilterObra(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-md"
              >
                <option value="Todas">Todas las obras</option>
                {obras.map(obra => (
                  <option key={obra.id} value={obra.id}>{obra.nombre}</option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Presupuestos Agrupados por Obra */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-12 text-center text-slate-600">Cargando presupuestos...</div>
          ) : obrasConPresupuestos.length === 0 ? (
            <div className="py-12 text-center text-slate-600">
              No hay presupuestos que coincidan con los filtros
            </div>
          ) : (
            <>
              <div className="divide-y divide-slate-200">
                {paginatedObras.map(({ obraId, obra, presupuestos: presupuestosObra }) => {
                  const ultimoPresupuesto = presupuestosObra[presupuestosObra.length - 1];
                  const multiplePresupuestos = presupuestosObra.length > 1;
                  const isExpanded = expandedObras[obraId] || false;

                  return (
                    <div key={obraId} className="bg-white hover:bg-slate-50 transition-colors">
                      {/* Fila de Obra (Header) */}
                      <div
                        className="flex items-center p-4 cursor-pointer"
                        onClick={() => toggleObraExpanded(obraId)}
                      >
                        <div className="flex-1 grid grid-cols-12 gap-4 items-center">
                          {/* Columna 1: Obra + Expandir */}
                          <div className="col-span-3 flex items-center gap-3">
                            <span className={`text-slate-400 transform transition-transform duration-500 ease-in-out ${isExpanded ? 'rotate-90' : 'rotate-0'
                              }`}>
                              ▶
                            </span>
                            <div>
                              <div className="font-semibold text-slate-900">
                                {obra?.nombre || "Sin obra"}
                              </div>
                              <div className="text-xs text-slate-500">
                                {presupuestosObra.length} presupuesto{presupuestosObra.length !== 1 ? 's' : ''}
                              </div>
                            </div>
                          </div>

                          {/* Columna 2: Info del último presupuesto */}
                          <div className="col-span-2">
                            <div className="font-mono text-xs text-slate-500">{ultimoPresupuesto.numero}</div>
                            <div className="text-sm">V{ultimoPresupuesto.version}</div>
                          </div>

                          <div className="col-span-1">
                            <Badge
                              variant={
                                ultimoPresupuesto.estado === 'Borrador' ? 'outline' :
                                  ultimoPresupuesto.estado === 'Listo' ? 'default' :
                                    'secondary'
                              }
                            >
                              {ultimoPresupuesto.estado}
                            </Badge>
                          </div>

                          <div className="col-span-2 text-right text-sm text-slate-600">
                            {formatCurrency(ultimoPresupuesto.total_materiales)}
                          </div>

                          <div className="col-span-2 text-right text-sm text-slate-600">
                            {formatCurrency(ultimoPresupuesto.total_mano_obra)}
                          </div>

                          <div className="col-span-2 text-right font-semibold text-slate-900">
                            {formatCurrency(ultimoPresupuesto.total_general)}
                          </div>
                        </div>
                      </div>

                      {/* Presupuestos expandidos con animación */}
                      <div
                        className={`overflow-hidden transition-all duration-500 ease-in-out ${isExpanded ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
                          }`}
                      >
                        {presupuestosObra.map((presupuesto, idx) => (
                          <div
                            key={presupuesto.id}
                            className={`pl-12 pr-4 py-3 border-t border-slate-100 hover:bg-blue-50 cursor-pointer transition-colors duration-200 ${idx === presupuestosObra.length - 1 && multiplePresupuestos ? 'bg-slate-50' : ''
                              }`}
                            onClick={() => handleViewPresupuesto(presupuesto)}
                          >
                            <div className="grid grid-cols-12 gap-4 items-center">
                              <div className="col-span-3">
                                <div className="font-mono text-xs text-slate-500 mb-1">{presupuesto.numero}</div>
                                <div className="font-medium text-sm">{presupuesto.nombre}</div>
                                <div className="text-xs text-slate-500">Versión {presupuesto.version}</div>
                              </div>

                              <div className="col-span-2 text-sm text-slate-600">
                                {formatDate(presupuesto.fecha_creacion)}
                              </div>

                              <div className="col-span-1">
                                <Badge
                                  variant={
                                    presupuesto.estado === 'Borrador' ? 'outline' :
                                      presupuesto.estado === 'Listo' ? 'default' :
                                        'secondary'
                                  }
                                >
                                  {presupuesto.estado}
                                </Badge>
                              </div>

                              <div className="col-span-2 text-right text-sm">
                                {formatCurrency(presupuesto.total_materiales)}
                              </div>

                              <div className="col-span-2 text-right text-sm">
                                {formatCurrency(presupuesto.total_mano_obra)}
                              </div>

                              <div className="col-span-2 text-right font-semibold">
                                {formatCurrency(presupuesto.total_general)}
                              </div>
                            </div>

                            {/* Acciones */}
                            <div className="mt-2 flex gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewPresupuesto(presupuesto);
                                }}
                              >
                                Ver Detalle
                              </Button>
                              {presupuesto.estado === "Borrador" && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => handleDeletePresupuesto(e, presupuesto.id)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  Eliminar
                                </Button>
                              )}
                              {presupuesto.cotizacion_id && (
                                <Badge
                                  className="bg-green-600 hover:bg-green-700 cursor-pointer ml-2"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/cotizaciones/${presupuesto.cotizacion_id}`);
                                  }}
                                >
                                  ✓ Cotización
                                </Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Paginación */}
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                totalItems={obrasConPresupuestos.length}
                itemsPerPage={ITEMS_PER_PAGE}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Modal Crear Presupuesto */}
      {showModal && (
        <CrearPresupuestoModal
          onClose={(reload) => {
            setShowModal(false);
            if (reload) loadData();
          }}
          obras={obras}
        />
      )}
    </div>
  );
}

// Modal para Crear Presupuesto
function CrearPresupuestoModal({ onClose, obras, presupuestoExistente = null }) {
  const navigate = useNavigate();
  const modoEdicion = !!presupuestoExistente;

  const [formData, setFormData] = useState(modoEdicion ? {
    obra_id: presupuestoExistente.obra_id,
    nombre: presupuestoExistente.nombre,
    porcentaje_costos_indirectos: presupuestoExistente.porcentaje_costos_indirectos,
    porcentaje_beneficios: presupuestoExistente.porcentaje_beneficios,
    porcentaje_leyes_sociales: presupuestoExistente.porcentaje_leyes_sociales,
    porcentaje_iva: presupuestoExistente.porcentaje_iva,
    notas: presupuestoExistente.notas || ""
  } : {
    obra_id: "",
    nombre: "",
    porcentaje_costos_indirectos: 15.0,
    porcentaje_beneficios: 10.0,
    porcentaje_leyes_sociales: 76.0,
    porcentaje_iva: 22.0,
    notas: ""
  });

  const [fechaInicio, setFechaInicio] = useState("");
  const [saving, setSaving] = useState(false);

  // Pre-cargar fecha de inicio de la obra cuando se selecciona una obra
  useEffect(() => {
    const cargarFechaObra = async () => {
      if (formData.obra_id) {
        try {
          const response = await obrasAPI.getById(formData.obra_id);
          const obra = response.data;
          if (obra.fecha_inicio) {
            setFechaInicio(obra.fecha_inicio);
          }
        } catch (error) {
          console.error("Error al cargar fecha de obra:", error);
        }
      }
    };

    cargarFechaObra();
  }, [formData.obra_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (modoEdicion) {
        // Si cambió la fecha de inicio, verificar cronogramas
        if (fechaInicio) {
          const obra = await obrasAPI.getById(formData.obra_id);
          const fechaActual = obra.data.fecha_inicio;

          // Si la fecha cambió, verificar cronogramas
          if (fechaActual !== fechaInicio) {
            const resCronogramas = await cronogramasAPI.getAll();
            const cronogramasObra = resCronogramas.data.filter(c => c.obra_id === formData.obra_id);

            if (cronogramasObra.length > 0) {
              const confirmar = window.confirm(
                `⚠️ ADVERTENCIA: Esta obra tiene ${cronogramasObra.length} cronograma(s) activo(s).\n\n` +
                `Fecha actual: ${fechaActual}\n` +
                `Nueva fecha: ${fechaInicio}\n\n` +
                `¿Deseas recalcular automáticamente todas las tareas de los cronogramas para mantener la consistencia?\n\n` +
                `Si aceptas, las tareas se moverán proporcionalmente manteniendo sus duraciones.\n` +
                `Si cancelas, los cronogramas quedarán con las fechas antiguas (tendrás que ajustarlos manualmente).`
              );

              if (confirmar) {
                // Recalcular todos los cronogramas de la obra
                for (const cronograma of cronogramasObra) {
                  await cronogramasAPI.recalcularFechas(cronograma.id, fechaInicio);
                }
                toast.success(`✅ ${cronogramasObra.length} cronograma(s) recalculado(s)`);
              } else {
                toast.warning("⚠️ Cronogramas NO actualizados - ajústalos manualmente");
              }
            }

            // Actualizar la fecha de inicio de la obra
            await api.patch(`/obras/${formData.obra_id}/fecha-inicio`, { fecha_inicio: fechaInicio });
          }
        }

        // Actualizar presupuesto existente
        await presupuestosAPI.update(presupuestoExistente.id, formData);

        toast.success("Presupuesto actualizado exitosamente");
        onClose(true); // true = refrescar datos
      } else {
        // Crear nuevo presupuesto
        const res = await presupuestosAPI.create(formData);

        // Si se especificó fecha de inicio, actualizar la obra
        if (fechaInicio) {
          await api.patch(`/obras/${formData.obra_id}/fecha-inicio`, { fecha_inicio: fechaInicio });
        }

        toast.success("Presupuesto creado exitosamente");
        onClose(false);
        setTimeout(() => {
          navigate(`/presupuestos/${res.data.id}`);
        }, 100);
      }
    } catch (error) {
      console.error("Error en handleSubmit:", error);
      toast.error(modoEdicion ? "Error al actualizar presupuesto" : "Error al crear presupuesto");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-2xl font-bold text-slate-900">
            {modoEdicion ? "Editar Presupuesto" : "Nuevo Presupuesto"}
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            {modoEdicion
              ? "Modificar los parámetros del presupuesto. Los cambios afectarán los cálculos."
              : "Crear un nuevo presupuesto para una obra. Luego podrás agregar items y rubros."
            }
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <Label>Obra *</Label>
            <select
              required
              value={formData.obra_id}
              onChange={(e) => setFormData({ ...formData, obra_id: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded-md"
            >
              <option value="">Seleccionar obra...</option>
              {obras.filter(o => o.estado === 'Activa').map(obra => (
                <option key={obra.id} value={obra.id}>
                  {obra.nombre} - {obra.cliente}
                </option>
              ))}
            </select>
            <p className="text-xs text-slate-500 mt-1">
              Solo se muestran obras activas
            </p>
          </div>

          <div>
            <Label>Nombre del Presupuesto *</Label>
            <Input
              required
              value={formData.nombre}
              onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              placeholder="Ej: Presupuesto Inicial, Presupuesto Ajustado..."
            />
          </div>

          <div>
            <Label>Fecha de Inicio de Obra</Label>
            <Input
              type="date"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
            />
            <p className="text-xs text-slate-500 mt-1">
              Esta fecha se sincronizará con la obra y afectará los cronogramas
            </p>
          </div>

          <div className="border-t border-slate-200 pt-4">
            <h3 className="font-semibold text-slate-900 mb-3">Porcentajes de Cálculo</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Beneficios (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.porcentaje_beneficios}
                  onChange={(e) => setFormData({ ...formData, porcentaje_beneficios: parseFloat(e.target.value) })}
                  required
                />
                <p className="text-xs text-slate-500 mt-1">Sobre costos base (sin leyes sociales)</p>
              </div>
              <div>
                <Label>Leyes Sociales (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.porcentaje_leyes_sociales}
                  onChange={(e) => setFormData({ ...formData, porcentaje_leyes_sociales: parseFloat(e.target.value) })}
                />
              </div>
              <div>
                <Label>IVA (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.porcentaje_iva}
                  onChange={(e) => setFormData({ ...formData, porcentaje_iva: parseFloat(e.target.value) })}
                />
              </div>
            </div>
          </div>

          <div>
            <Label>Notas</Label>
            <textarea
              value={formData.notas}
              onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded-md"
              rows="3"
              placeholder="Notas o comentarios sobre este presupuesto"
            />
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
            <Button
              type="button"
              variant="outline"
              onClick={() => onClose(false)}
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="bg-deflorencia hover:bg-[#ff9100]"
              disabled={saving}
            >
              {saving
                ? (modoEdicion ? "Guardando..." : "Creando...")
                : (modoEdicion ? "Guardar Cambios" : "Crear y Continuar")
              }
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}


// Modal para Clonar Presupuesto (crear nueva versión)
function ClonarPresupuestoModal({ onClose, obras, presupuestoOriginal }) {
  const [formData, setFormData] = useState({
    obra_id: presupuestoOriginal?.obra_id || "",
    nombre: `${presupuestoOriginal?.nombre || "Presupuesto"} - v${(presupuestoOriginal?.version || 1) + 1}`,
    porcentaje_costos_indirectos: presupuestoOriginal?.porcentaje_costos_indirectos || 15,
    porcentaje_beneficios: presupuestoOriginal?.porcentaje_beneficios || 10,
    porcentaje_leyes_sociales: presupuestoOriginal?.porcentaje_leyes_sociales || 76,
    porcentaje_iva: presupuestoOriginal?.porcentaje_iva || 22,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Crear nueva versión en backend
      const response = await presupuestosAPI.crearNuevaVersion(presupuestoOriginal.id);
      const nuevoPresupuesto = response.data;

      // Actualizar con los datos del formulario si cambiaron
      const hasChanges =
        formData.nombre !== `${presupuestoOriginal.nombre} - v${nuevoPresupuesto.version}` ||
        formData.porcentaje_costos_indirectos !== presupuestoOriginal.porcentaje_costos_indirectos ||
        formData.porcentaje_beneficios !== presupuestoOriginal.porcentaje_beneficios ||
        formData.porcentaje_leyes_sociales !== presupuestoOriginal.porcentaje_leyes_sociales ||
        formData.porcentaje_iva !== presupuestoOriginal.porcentaje_iva;

      if (hasChanges) {
        await presupuestosAPI.update(nuevoPresupuesto.id, {
          obra_id: formData.obra_id,
          nombre: formData.nombre,
          version: nuevoPresupuesto.version,
          porcentaje_costos_indirectos: formData.porcentaje_costos_indirectos,
          porcentaje_beneficios: formData.porcentaje_beneficios,
          porcentaje_leyes_sociales: formData.porcentaje_leyes_sociales,
          porcentaje_iva: formData.porcentaje_iva
        });
      }

      toast.success(`Nueva versión creada: v${nuevoPresupuesto.version}\n✅ Se copiaron los items de rubros actuales de la obra`);
      onClose(nuevoPresupuesto.id); // Pasar ID para redirect
    } catch (error) {
      console.error("Error al clonar presupuesto:", error);
      toast.error("Error al crear nueva versión");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit} className="p-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-2xl font-bold text-slate-900">
                Crear Nueva Versión
              </h2>
              <p className="text-sm text-slate-600 mt-1">
                Basado en: <span className="font-semibold">{presupuestoOriginal?.numero}</span> (v{presupuestoOriginal?.version})
              </p>
            </div>
            <button
              type="button"
              onClick={() => onClose(null)}
              className="text-slate-400 hover:text-slate-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Info Box */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex gap-2">
              <div className="text-blue-600 mt-0.5">ℹ️</div>
              <div className="text-sm text-blue-900">
                <p className="font-semibold mb-1">Se copiará automáticamente:</p>
                <ul className="list-disc ml-5 space-y-0.5">
                  <li>Todos los items con sus cantidades y precios</li>
                  <li>Organización por rubros</li>
                  <li>El nuevo presupuesto quedará en estado <strong>Borrador</strong></li>
                </ul>
                <p className="mt-2">Puedes ajustar los datos antes de crear la versión:</p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {/* Obra */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Obra
              </label>
              <select
                value={formData.obra_id}
                onChange={(e) => setFormData({ ...formData, obra_id: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-deflorencia focus:border-transparent"
                required
              >
                {obras.map((obra) => (
                  <option key={obra.id} value={obra.id}>
                    {obra.nombre}
                  </option>
                ))}
              </select>
            </div>

            {/* Nombre */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Nombre del Presupuesto
              </label>
              <input
                type="text"
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-deflorencia focus:border-transparent"
                placeholder="Ej: Presupuesto Inicial, Presupuesto Ajustado..."
                required
              />
            </div>

            {/* Porcentajes en Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Beneficios (%)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.porcentaje_beneficios}
                  onChange={(e) => setFormData({ ...formData, porcentaje_beneficios: parseFloat(e.target.value) })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-deflorencia focus:border-transparent"
                  required
                />
                <p className="text-xs text-slate-500 mt-1">Se aplica sobre Materiales + MO (sin leyes sociales)</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Leyes Sociales (%)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.porcentaje_leyes_sociales}
                  onChange={(e) => setFormData({ ...formData, porcentaje_leyes_sociales: parseFloat(e.target.value) })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-deflorencia focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  IVA (%)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.porcentaje_iva}
                  onChange={(e) => setFormData({ ...formData, porcentaje_iva: parseFloat(e.target.value) })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-deflorencia focus:border-transparent"
                  required
                />
              </div>
            </div>
          </div>

          {/* Botones */}
          <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-slate-200">
            <Button
              type="button"
              variant="outline"
              onClick={() => onClose(null)}
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="bg-deflorencia hover:bg-[#ff9100]"
              disabled={saving}
            >
              {saving ? "Creando versión..." : `Crear Versión ${(presupuestoOriginal?.version || 1) + 1}`}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}


// Componente Detalle Presupuesto - Vista y Edición Completa
// Componente para mostrar un item del presupuesto (con soporte para items compuestos)
function PresupuestoItemRow({ item, presupuesto, onEdit, onDelete }) {
  const [itemCatalogo, setItemCatalogo] = useState(null);
  const [showDesglose, setShowDesglose] = useState(false);
  const [desglose, setDesglose] = useState(null);

  useEffect(() => {
    if (item.item_id) {
      // Cargar info del catálogo para saber si es compuesto
      catalogoItemsAPI.getById(item.item_id)
        .then(res => {
          setItemCatalogo(res.data);
          // Si es compuesto, cargar el desglose
          if (res.data.tipo === "Compuesto") {
            return catalogoItemsAPI.getDesglose(item.item_id, item.cantidad);
          }
        })
        .then(desgloseRes => {
          if (desgloseRes) {
            setDesglose(desgloseRes.data);
          }
        })
        .catch(err => console.error("Error cargando item:", err));
    }
  }, [item.item_id, item.cantidad]);

  return (
    <>
      <TableRow>
        <TableCell className="font-mono text-sm">{item.codigo}</TableCell>
        <TableCell>
          <div className="flex items-center gap-2">
            <span className="font-medium">{item.nombre}</span>
            {item.comentarios && item.comentarios.trim() && (
              <span
                className="text-blue-600 cursor-help text-sm"
                title={item.comentarios}
              >
                💬
              </span>
            )}
            {itemCatalogo?.tipo === "Compuesto" && (
              <>
                <Badge variant="secondary" className="bg-purple-100 text-purple-700 text-xs">
                  Compuesto
                </Badge>
                <button
                  onClick={() => setShowDesglose(!showDesglose)}
                  className="text-xs text-blue-600 hover:text-blue-800 underline"
                >
                  {showDesglose ? "Ocultar" : "Ver desglose"}
                </button>
              </>
            )}
          </div>
        </TableCell>
        <TableCell>
          <Badge variant="outline">{item.categoria}</Badge>
        </TableCell>
        <TableCell className="text-right">{item.cantidad}</TableCell>
        <TableCell>{item.unidad}</TableCell>
        <TableCell className="text-right">{formatCurrency(item.precio_unitario)}</TableCell>
        <TableCell className="text-right font-semibold">
          {formatCurrency(item.cantidad * item.precio_unitario)}
        </TableCell>
        {presupuesto.estado === 'Borrador' && (
          <TableCell>
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEdit(item)}
              >
                Editar
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDelete(item.id)}
                className="text-red-600 hover:text-red-700"
              >
                Eliminar
              </Button>
            </div>
          </TableCell>
        )}
      </TableRow>

      {/* Fila de desglose (solo para items compuestos) */}
      {showDesglose && desglose && (
        <TableRow className="bg-purple-50/50">
          <TableCell colSpan={presupuesto.estado === 'Borrador' ? 8 : 7} className="p-4">
            <div className="ml-8">
              <h4 className="text-sm font-semibold text-purple-900 mb-2">
                📦 Desglose de Componentes ({item.cantidad} {item.unidad})
              </h4>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-600 border-b border-purple-200">
                    <th className="pb-2">Componente</th>
                    <th className="pb-2 text-right">Cant. Unitaria</th>
                    <th className="pb-2 text-right">Cant. Total</th>
                    <th className="pb-2 text-right">Precio Unit.</th>
                    <th className="pb-2 text-right">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {desglose.desglose.map((comp, idx) => (
                    <tr key={idx} className="border-b border-purple-100">
                      <td className="py-2">
                        <span className="font-mono text-xs text-slate-500 mr-2">{comp.codigo}</span>
                        <span>{comp.nombre}</span>
                      </td>
                      <td className="text-right text-slate-600">
                        {comp.cantidad_unitaria} {comp.unidad}
                      </td>
                      <td className="text-right font-medium">
                        {comp.cantidad_total.toFixed(2)} {comp.unidad}
                      </td>
                      <td className="text-right">{formatCurrency(comp.precio_unitario)}</td>
                      <td className="text-right font-semibold">{formatCurrency(comp.subtotal)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="font-semibold text-purple-900">
                    <td colSpan="4" className="pt-2 text-right">Total del item:</td>
                    <td className="pt-2 text-right">{formatCurrency(desglose.total)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function DetallePresupuesto() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [presupuesto, setPresupuesto] = useState(null);
  const [obra, setObra] = useState(null);
  const [items, setItems] = useState([]);
  const [rubros, setRubros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [selectedRubro, setSelectedRubro] = useState(null);
  const [showEditItemModal, setShowEditItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showGenerarCotizacionModal, setShowGenerarCotizacionModal] = useState(false);
  const [showEditPresupuestoModal, setShowEditPresupuestoModal] = useState(false);
  const [showClonarPresupuestoModal, setShowClonarPresupuestoModal] = useState(false);
  const [obras, setObras] = useState([]);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [resPresupuesto, resItems, resRubros, resObras] = await Promise.all([
        presupuestosAPI.getById(id),
        presupuestosAPI.getItems(id),
        rubrosAPI.getAll(),
        obrasAPI.getAll()
      ]);

      setPresupuesto(resPresupuesto.data);
      setItems(resItems.data);
      setRubros(resRubros.data);
      setObras(resObras.data);

      // Cargar datos de la obra
      if (resPresupuesto.data.obra_id) {
        const resObra = await obrasAPI.getById(resPresupuesto.data.obra_id);
        setObra(resObra.data);
      }
    } catch (error) {
      toast.error("Error al cargar presupuesto");
      navigate('/presupuestos');
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = (rubroId) => {
    setSelectedRubro(rubroId);
    setShowAddItemModal(true);
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm("¿Está seguro de eliminar este item?")) return;

    try {
      await presupuestosAPI.deleteItem(itemId);
      toast.success("Item eliminado exitosamente");
      loadData(); // Recargar para ver totales actualizados
    } catch (error) {
      toast.error("Error al eliminar item");
    }
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setShowEditItemModal(true);
  };

  const handleChangeEstado = async (nuevoEstado) => {
    if (!presupuesto) {
      toast.error("No se puede cambiar el estado: presupuesto no cargado");
      return;
    }

    // Validación: No permitir marcar como "Listo" un presupuesto sin items
    if (nuevoEstado === 'Listo' && items.length === 0) {
      toast.error("No se puede marcar como Listo un presupuesto sin items. Agregue al menos un item.");
      return;
    }

    // Validación: No permitir marcar como "Listo" un presupuesto con total en 0
    if (nuevoEstado === 'Listo' && presupuesto.total_general === 0) {
      toast.error("No se puede marcar como Listo un presupuesto con total $0. Agregue items con cantidades y precios.");
      return;
    }

    if (!window.confirm(`¿Cambiar estado a ${nuevoEstado}?`)) return;

    try {
      // Enviar todos los campos del presupuesto más el nuevo estado
      const updateData = {
        obra_id: presupuesto.obra_id,
        nombre: presupuesto.nombre,
        version: presupuesto.version,
        estado: nuevoEstado,
        notas: presupuesto.notas || null,
        porcentaje_costos_indirectos: presupuesto.porcentaje_costos_indirectos || 0,
        porcentaje_beneficios: presupuesto.porcentaje_beneficios || 0,
        porcentaje_leyes_sociales: presupuesto.porcentaje_leyes_sociales || 0,
        porcentaje_iva: presupuesto.porcentaje_iva || 22
      };

      console.log("Enviando actualización:", updateData);

      await presupuestosAPI.update(id, updateData);
      toast.success(`Presupuesto marcado como ${nuevoEstado.toLowerCase()}`);
      loadData();
    } catch (error) {
      console.error("Error al cambiar estado:", error);
      toast.error("Error al cambiar estado");
    }
  };

  const handleCrearNuevaVersion = () => {
    // Abrir modal para revisar y ajustar datos antes de clonar
    setShowClonarPresupuestoModal(true);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="py-12 text-center text-slate-600">Cargando presupuesto...</div>
      </div>
    );
  }

  if (!presupuesto) {
    return (
      <div className="space-y-6">
        <div className="py-12 text-center text-slate-600">
          <p className="mb-4">No se pudo cargar el presupuesto</p>
          <Button onClick={() => navigate('/presupuestos')}>Volver a Presupuestos</Button>
        </div>
      </div>
    );
  }

  // Agrupar items por rubro ID (NO por nombre)
  const itemsPorRubro = items.reduce((acc, item) => {
    const rubroId = item.rubro_id;
    if (!acc[rubroId]) acc[rubroId] = [];
    acc[rubroId].push(item);
    return acc;
  }, {});

  // Calcular subtotales por rubro ID
  const subtotalesPorRubro = {};
  Object.keys(itemsPorRubro).forEach(rubroId => {
    subtotalesPorRubro[rubroId] = itemsPorRubro[rubroId].reduce((sum, item) =>
      sum + (item.cantidad * item.precio_unitario), 0
    );
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/presupuestos')}
            className="mb-2"
          >
            ← Volver a Presupuestos
          </Button>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-3xl font-bold text-slate-900">{presupuesto?.nombre || 'Sin nombre'}</h1>
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-3 py-1 rounded-full font-bold text-xs shadow-lg">
              v2.14.12 🔥
            </div>
          </div>
          <p className="text-slate-600">
            Versión {presupuesto?.version || 1} • {obra?.nombre || 'Sin obra'} • {obra?.cliente || ''}
          </p>
          {presupuesto?.version_de_numero && (
            <div className="mt-2 inline-flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5">
              <span className="text-sm text-blue-700">
                📋 Basado en: <span className="font-semibold">{presupuesto.version_de_numero}</span>
              </span>
              {presupuesto?.estado === 'Borrador' && (
                <button
                  onClick={async () => {
                    if (window.confirm("¿Quitar la referencia al presupuesto original? Esta acción no se puede deshacer.")) {
                      try {
                        await presupuestosAPI.quitarReferencia(presupuesto.id);
                        toast.success("Referencia eliminada");
                        loadData();
                      } catch (error) {
                        toast.error("Error al quitar referencia");
                      }
                    }
                  }}
                  className="text-blue-600 hover:text-blue-800 text-xs underline"
                >
                  Quitar referencia
                </button>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={
            presupuesto?.estado === 'Borrador' ? 'outline' :
              presupuesto?.estado === 'Listo' ? 'default' :
                'secondary'
          } className="text-lg px-3 py-1">
            {presupuesto?.estado || 'Desconocido'}
          </Badge>

          {presupuesto?.estado === 'Borrador' && (
            <>
              <Button
                onClick={() => setShowEditPresupuestoModal(true)}
                variant="outline"
                className="border-blue-600 text-blue-600 hover:bg-blue-50"
              >
                ⚙️ Editar Presupuesto
              </Button>
              <Button
                onClick={() => handleChangeEstado('Listo')}
                className="bg-green-600 hover:bg-green-700"
              >
                Marcar como Listo
              </Button>
            </>
          )}

          {presupuesto?.estado === 'Listo' && (
            <>
              <Button
                onClick={handleCrearNuevaVersion}
                variant="outline"
                className="border-blue-600 text-blue-600 hover:bg-blue-50"
              >
                📝 Crear Nueva Versión
              </Button>

              {presupuesto?.cotizacion_id ? (
                <>
                  <Badge variant="default" className="text-lg px-3 py-1 bg-blue-600">
                    Con Cotización
                  </Badge>
                  <Button
                    onClick={() => navigate(`/cotizaciones/${presupuesto.cotizacion_id}`)}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Ver Cotización
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => setShowGenerarCotizacionModal(true)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  📄 Generar Cotización
                </Button>
              )}
              <Button
                onClick={() => handleChangeEstado('Cerrado')}
                variant="outline"
              >
                Cerrar Presupuesto
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Resumen Financiero */}
      <Card>
        <CardHeader>
          <CardTitle>Resumen Financiero</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-sm text-slate-600">Total Materiales</div>
              <div className="text-2xl font-bold text-blue-600">{formatCurrency(presupuesto.total_materiales)}</div>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="text-sm text-slate-600">Total Mano de Obra</div>
              <div className="text-2xl font-bold text-purple-600">{formatCurrency(presupuesto.total_mano_obra)}</div>
            </div>
            <div className="text-center p-4 bg-amber-50 rounded-lg">
              <div className="text-sm text-slate-600">Costos Base</div>
              <div className="text-2xl font-bold text-amber-600">
                {formatCurrency(presupuesto.total_materiales + presupuesto.total_mano_obra)}
              </div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg border-2 border-green-500">
              <div className="text-sm text-slate-600">TOTAL CON IVA</div>
              <div className="text-2xl font-bold text-green-600">{formatCurrency(presupuesto.total_con_iva || presupuesto.total_general)}</div>
              {presupuesto.monto_iva > 0 && (
                <div className="text-xs text-slate-500 mt-1">
                  (IVA: {formatCurrency(presupuesto.monto_iva)})
                </div>
              )}
            </div>
          </div>

          {/* Desglose Detallado MEJORADO */}
          <div className="mt-6 bg-slate-50 p-4 rounded-lg space-y-2 text-sm">
            <div className="font-semibold text-slate-700 mb-3 border-b pb-2">📊 Desglose de Cálculo:</div>

            {/* PASO 1: Costos Base */}
            <div className="bg-white p-3 rounded border border-slate-200">
              <div className="text-xs font-semibold text-slate-500 mb-2">PASO 1: Costos Base</div>
              <div className="flex justify-between">
                <span className="text-slate-600">Materiales</span>
                <span className="font-medium">{formatCurrency(presupuesto.total_materiales)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Mano de Obra (base)</span>
                <span className="font-medium">{formatCurrency(presupuesto.total_mano_obra)}</span>
              </div>
              <div className="flex justify-between font-semibold border-t mt-2 pt-2">
                <span className="text-slate-700">= Costos Base</span>
                <span>{formatCurrency(presupuesto.total_materiales + presupuesto.total_mano_obra)}</span>
              </div>
            </div>

            {/* PASO 2: Beneficios */}
            <div className="bg-white p-3 rounded border border-amber-200">
              <div className="text-xs font-semibold text-amber-600 mb-2">PASO 2: Beneficios</div>
              <div className="flex justify-between text-amber-700">
                <span>+ Beneficios ({presupuesto.porcentaje_beneficios}% sobre costos base)</span>
                <span className="font-medium">{formatCurrency(presupuesto.monto_beneficios || 0)}</span>
              </div>
            </div>

            {/* PASO 3: Leyes Sociales */}
            <div className="bg-white p-3 rounded border border-purple-200">
              <div className="text-xs font-semibold text-purple-600 mb-2">PASO 3: Leyes Sociales</div>
              <div className="flex justify-between text-purple-700">
                <span>+ Leyes Sociales ({presupuesto.porcentaje_leyes_sociales}% solo sobre MO)</span>
                <span className="font-medium">{formatCurrency(presupuesto.monto_leyes_sociales || 0)}</span>
              </div>
            </div>

            {/* Subtotal sin IVA */}
            <div className="flex justify-between font-bold border-t-2 border-slate-300 pt-3 text-lg bg-slate-100 p-2 rounded">
              <span className="text-slate-900">SUBTOTAL SIN IVA</span>
              <span className="text-slate-900">{formatCurrency(presupuesto.subtotal_sin_iva || presupuesto.total_general)}</span>
            </div>

            {/* IVA */}
            <div className="bg-white p-3 rounded border border-blue-200">
              <div className="text-xs font-semibold text-blue-600 mb-2">PASO 4: IVA</div>
              <div className="flex justify-between text-blue-700">
                <span>+ IVA ({presupuesto.porcentaje_iva}% sobre items marcados)</span>
                <span className="font-medium">{formatCurrency(presupuesto.monto_iva || 0)}</span>
              </div>
            </div>

            {/* TOTAL FINAL */}
            <div className="flex justify-between font-bold border-t-2 border-green-500 pt-3 text-xl text-green-700 bg-green-50 p-3 rounded">
              <span>💰 TOTAL CON IVA</span>
              <span>{formatCurrency(presupuesto.total_con_iva || presupuesto.total_general)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notas del Presupuesto - Solo mostrar si hay contenido */}
      {presupuesto?.notas && presupuesto.notas.trim() && (
        <Card className="bg-amber-50 border-amber-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-900">
              <span>📝</span>
              <span>Notas del Presupuesto</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-700 whitespace-pre-wrap">{presupuesto.notas}</p>
          </CardContent>
        </Card>
      )}

      {/* Items por Rubro */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Items del Presupuesto</CardTitle>
            {presupuesto?.estado === 'Listo' && (
              <Badge variant="default" className="bg-slate-700 flex items-center gap-1">
                🔒 Presupuesto Bloqueado
              </Badge>
            )}
          </div>
          {presupuesto?.estado === 'Listo' && (
            <CardDescription className="mt-2">
              Este presupuesto está aprobado y no se puede modificar. Para hacer cambios, cree una nueva versión.
            </CardDescription>
          )}
          {presupuesto?.estado !== 'Listo' && (
            <CardDescription>
              {items.length} items en {Object.keys(itemsPorRubro).length} rubros
            </CardDescription>
          )}
        </CardHeader>
        <CardContent>
          {/* Detectar items huérfanos (sin rubro o con rubro no asociado a la obra) */}
          {(() => {
            const rubrosObra = obra?.rubros_ids || [];
            const itemsHuerfanos = items.filter(item =>
              !item.rubro_id || !rubrosObra.includes(item.rubro_id)
            );

            if (itemsHuerfanos.length > 0) {
              return (
                <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4">
                  <div className="flex items-start">
                    <div className="flex-1">
                      <p className="font-semibold text-red-900">⚠️ Items sin rubro válido detectados</p>
                      <p className="text-sm text-red-700 mt-1">
                        Este presupuesto tiene {itemsHuerfanos.length} item(s) que no están asociados a ningún rubro de la obra.
                        Estos items contribuyen al total pero no son visibles en las secciones de rubros.
                      </p>
                      <details className="mt-2">
                        <summary className="text-sm text-red-800 cursor-pointer font-medium">Ver items afectados</summary>
                        <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
                          {itemsHuerfanos.map(item => (
                            <li key={item.id}>{item.nombre} (rubro_id: {item.rubro_id || 'null'})</li>
                          ))}
                        </ul>
                      </details>
                      {presupuesto.estado === 'Borrador' && (
                        <p className="text-sm text-red-800 mt-2 font-medium">
                          💡 Recomendación: Elimina estos items y vuélvelos a agregar correctamente.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            }
            return null;
          })()}

          {obra?.rubros_ids && obra.rubros_ids.length > 0 ? (
            <div className="space-y-6">
              {obra.rubros_ids.map(rubroId => {
                const rubro = rubros.find(r => r.id === rubroId);
                const itemsRubro = itemsPorRubro[rubroId] || [];
                const subtotal = subtotalesPorRubro[rubroId] || 0;

                return (
                  <div key={rubroId} className="border border-slate-200 rounded-lg">
                    {/* Header del Rubro */}
                    <div className="bg-slate-50 px-4 py-3 flex items-center justify-between rounded-t-lg border-b border-slate-200">
                      <div>
                        <h3 className="font-semibold text-slate-900">
                          {rubro?.nombre || "Rubro desconocido"}
                        </h3>
                        <p className="text-sm text-slate-600">
                          {itemsRubro.length} items • Subtotal: {formatCurrency(subtotal)}
                        </p>
                      </div>
                      {presupuesto.estado === 'Borrador' && (
                        <Button
                          size="sm"
                          onClick={() => handleAddItem(rubroId)}
                          className="bg-deflorencia hover:bg-[#ff9100]"
                        >
                          + Agregar Item
                        </Button>
                      )}
                    </div>

                    {/* Items del Rubro */}
                    {itemsRubro.length === 0 ? (
                      <div className="p-8 text-center text-slate-500">
                        {presupuesto.estado === 'Borrador' ? (
                          <>No hay items en este rubro. Agrega uno para comenzar.</>
                        ) : (
                          <>No hay items en este rubro.</>
                        )}
                      </div>
                    ) : (
                      <div className="p-4">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Código</TableHead>
                              <TableHead>Item</TableHead>
                              <TableHead>Categoría</TableHead>
                              <TableHead className="text-right">Cantidad</TableHead>
                              <TableHead>Unidad</TableHead>
                              <TableHead className="text-right">Precio Unit.</TableHead>
                              <TableHead className="text-right">Subtotal</TableHead>
                              {presupuesto.estado === 'Borrador' && <TableHead></TableHead>}
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {itemsRubro.map(item => (
                              <PresupuestoItemRow
                                key={item.id}
                                item={item}
                                presupuesto={presupuesto}
                                onEdit={handleEditItem}
                                onDelete={handleDeleteItem}
                              />
                            ))}
                            {/* Fila de Subtotal por Rubro */}
                            <TableRow className="bg-slate-50 font-semibold border-t-2 border-slate-300">
                              <TableCell colSpan={6} className="text-right">
                                Subtotal {rubro?.nombre}:
                              </TableCell>
                              <TableCell className="text-right">
                                {formatCurrency(subtotal)}
                              </TableCell>
                              {presupuesto.estado === 'Borrador' && <TableCell></TableCell>}
                            </TableRow>
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-12 text-center text-slate-600">
              Esta obra no tiene rubros asignados. Edita la obra para agregar rubros.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal Agregar Item */}
      {showAddItemModal && (
        <AgregarItemModal
          presupuestoId={id}
          rubroId={selectedRubro}
          onClose={(reload) => {
            setShowAddItemModal(false);
            setSelectedRubro(null);
            if (reload) loadData();
          }}
        />
      )}

      {/* Modal Editar Item */}
      {showEditItemModal && editingItem && (
        <EditarItemModal
          item={editingItem}
          onClose={(reload) => {
            setShowEditItemModal(false);
            setEditingItem(null);
            if (reload) loadData();
          }}
        />
      )}

      {/* Modal Generar Cotización */}
      {showGenerarCotizacionModal && (
        <GenerarCotizacionModal
          presupuesto={presupuesto}
          onClose={(reload) => {
            setShowGenerarCotizacionModal(false);
            if (reload) loadData();
          }}
        />
      )}

      {showEditPresupuestoModal && (
        <CrearPresupuestoModal
          obras={obras}
          presupuestoExistente={presupuesto}
          onClose={(reload) => {
            setShowEditPresupuestoModal(false);
            if (reload) loadData();
          }}
        />
      )}

      {showClonarPresupuestoModal && (
        <ClonarPresupuestoModal
          obras={obras}
          presupuestoOriginal={presupuesto}
          onClose={(nuevoPresupuestoId) => {
            setShowClonarPresupuestoModal(false);
            if (nuevoPresupuestoId) {
              // Redirigir al nuevo presupuesto creado
              navigate(`/presupuestos/${nuevoPresupuestoId}`);
            }
          }}
        />
      )}
    </div>
  );
}

// Modal para Agregar Item al Presupuesto
function AgregarItemModal({ presupuestoId, rubroId, onClose }) {
  const [catalogoItems, setCatalogoItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    item_id: "",
    cantidad: 1,
    precio_unitario: 0,
    tiene_iva: true,
    proveedor: "",
    comentarios: ""
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadCatalogo();
  }, []);

  const loadCatalogo = async () => {
    try {
      const res = await catalogoItemsAPI.getAll();
      setCatalogoItems(res.data.filter(i => i.activo));
    } catch (error) {
      toast.error("Error al cargar catálogo");
    }
  };

  const handleSelectItem = (item) => {
    setFormData({
      ...formData,
      item_id: item.id,
      precio_unitario: item.precio_referencia || 0,
      tiene_iva: item.tiene_iva ?? true,
      proveedor: item.proveedor || ""
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await presupuestosAPI.addItem(presupuestoId, {
        rubro_id: rubroId,
        ...formData
      });
      toast.success("Item agregado exitosamente");
      onClose(true);
    } catch (error) {
      toast.error("Error al agregar item");
    } finally {
      setSaving(false);
    }
  };

  const filteredItems = catalogoItems.filter(item =>
    item.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.codigo.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedItem = catalogoItems.find(i => i.id === formData.item_id);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-2xl font-bold text-slate-900">Agregar Item al Presupuesto</h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Buscador de Items */}
          <div>
            <Label>Buscar Item en el Catálogo</Label>
            <Input
              placeholder="Buscar por nombre o código..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && filteredItems.length > 0 && (
              <div className="mt-2 max-h-48 overflow-y-auto border border-slate-200 rounded-md">
                {filteredItems.slice(0, 10).map(item => (
                  <div
                    key={item.id}
                    onClick={() => handleSelectItem(item)}
                    className="p-3 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-0"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">{item.nombre}</div>
                        <div className="text-xs text-slate-500">
                          {item.codigo} • {item.unidad} • {item.categoria}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium">{formatCurrency(item.precio_referencia)}</div>
                        <div className="text-xs text-slate-500">{item.proveedor || '-'}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Item Seleccionado */}
          {selectedItem && (
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="font-semibold text-blue-900">{selectedItem.nombre}</div>
                  <div className="text-sm text-blue-700">
                    {selectedItem.codigo} • {selectedItem.unidad} • {selectedItem.categoria}
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setFormData({ ...formData, item_id: "" })}
                >
                  Cambiar
                </Button>
              </div>
            </div>
          )}

          {/* Cantidad y Precio */}
          {formData.item_id && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Cantidad *</Label>
                <Input
                  type="number"
                  step="0.01"
                  required
                  value={formData.cantidad}
                  onChange={(e) => setFormData({ ...formData, cantidad: parseFloat(e.target.value) })}
                />
              </div>
              <div>
                <Label>Precio Unitario *</Label>
                <Input
                  type="number"
                  step="0.01"
                  required
                  value={formData.precio_unitario}
                  onChange={(e) => setFormData({ ...formData, precio_unitario: parseFloat(e.target.value) })}
                />
              </div>
            </div>
          )}

          {/* Checkbox IVA */}
          {formData.item_id && (
            <div className="flex items-center space-x-2 p-3 bg-slate-50 rounded-md">
              <input
                type="checkbox"
                id="tiene_iva_item"
                checked={formData.tiene_iva}
                onChange={(e) => setFormData({ ...formData, tiene_iva: e.target.checked })}
                className="rounded border-slate-300"
              />
              <Label htmlFor="tiene_iva_item" className="cursor-pointer">
                Este item lleva IVA
              </Label>
            </div>
          )}

          {/* Comentarios */}
          {formData.item_id && (
            <div>
              <Label>Comentarios (opcional)</Label>
              <textarea
                value={formData.comentarios}
                onChange={(e) => setFormData({ ...formData, comentarios: e.target.value })}
                placeholder="Notas o comentarios sobre este item"
                rows="2"
                className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}

          {formData.item_id && formData.cantidad > 0 && formData.precio_unitario > 0 && (
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="text-center">
                <div className="text-sm text-green-700">Subtotal Item</div>
                <div className="text-2xl font-bold text-green-900">
                  {formatCurrency(formData.cantidad * formData.precio_unitario)}
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
            <Button
              type="button"
              variant="outline"
              onClick={() => onClose(false)}
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="bg-deflorencia hover:bg-[#ff9100]"
              disabled={saving || !formData.item_id}
            >
              {saving ? "Agregando..." : "Agregar Item"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Modal para Editar Item del Presupuesto
function EditarItemModal({ item, onClose }) {
  const [formData, setFormData] = useState({
    cantidad: item.cantidad,
    precio_unitario: item.precio_unitario,
    proveedor: item.proveedor || "",
    comentarios: item.comentarios || ""
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await presupuestosAPI.updateItem(item.id, {
        presupuesto_id: item.presupuesto_id,
        rubro_id: item.rubro_id,
        item_id: item.item_id,
        ...formData
      });
      toast.success("Item actualizado exitosamente");
      onClose(true);
    } catch (error) {
      toast.error("Error al actualizar item");
    } finally {
      setSaving(false);
    }
  };

  const subtotal = formData.cantidad * formData.precio_unitario;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-xl w-full">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-2xl font-bold text-slate-900">Editar Item</h2>
          <div className="mt-2 p-3 bg-slate-50 rounded">
            <div className="font-semibold">{item.nombre}</div>
            <div className="text-sm text-slate-600">
              {item.codigo} • {item.unidad} • {item.categoria}
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Cantidad *</Label>
              <Input
                type="number"
                step="0.01"
                required
                value={formData.cantidad}
                onChange={(e) => setFormData({ ...formData, cantidad: parseFloat(e.target.value) })}
              />
            </div>
            <div>
              <Label>Precio Unitario *</Label>
              <Input
                type="number"
                step="0.01"
                required
                value={formData.precio_unitario}
                onChange={(e) => setFormData({ ...formData, precio_unitario: parseFloat(e.target.value) })}
              />
            </div>
          </div>

          <div>
            <Label>Proveedor</Label>
            <Input
              value={formData.proveedor}
              onChange={(e) => setFormData({ ...formData, proveedor: e.target.value })}
              placeholder="Opcional"
            />
          </div>

          {/* Comentarios */}
          <div>
            <Label>Comentarios (opcional)</Label>
            <textarea
              value={formData.comentarios}
              onChange={(e) => setFormData({ ...formData, comentarios: e.target.value })}
              placeholder="Notas o comentarios sobre este item"
              rows="2"
              className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between">
              <span className="text-sm text-blue-700">Subtotal Item:</span>
              <span className="text-2xl font-bold text-blue-900">
                {formatCurrency(subtotal)}
              </span>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
            <Button
              type="button"
              variant="outline"
              onClick={() => onClose(false)}
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="bg-deflorencia hover:bg-[#ff9100]"
              disabled={saving}
            >
              {saving ? "Actualizando..." : "Actualizar Item"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Modal para Generar Cotización desde Presupuesto
function GenerarCotizacionModal({ presupuesto, onClose }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    incluir_detalle: true,
    descripcion_tecnica: "",
    exclusiones: "",
    plazo_entrega: "180 días corridos",
    forma_pago: "30% al inicio, 40% a mitad de obra, 30% al finalizar",
    validez_dias: 15
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const res = await cotizacionesAPI.create({
        presupuesto_id: presupuesto.id,
        ...formData
      });
      toast.success("Cotización generada exitosamente");
      onClose(true); // Recargar datos del presupuesto
      // NO navegar, quedarse en el presupuesto para ver el cambio
    } catch (error) {
      toast.error("Error al generar cotización");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg max-w-3xl w-full my-8">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-2xl font-bold text-slate-900">Generar Cotización</h2>
          <p className="text-sm text-slate-600 mt-1">
            Crear cotización para el cliente desde el presupuesto <span className="font-semibold">{presupuesto.nombre}</span>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Resumen del Presupuesto */}
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <div className="text-slate-600">Total Materiales</div>
                <div className="font-semibold">{formatCurrency(presupuesto.total_materiales)}</div>
              </div>
              <div>
                <div className="text-slate-600">Total M.O.</div>
                <div className="font-semibold">{formatCurrency(presupuesto.total_mano_obra)}</div>
              </div>
              <div>
                <div className="text-slate-600">TOTAL GENERAL</div>
                <div className="text-lg font-bold text-green-600">{formatCurrency(presupuesto.total_general)}</div>
              </div>
            </div>
          </div>

          {/* Incluir Detalle */}
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="incluir_detalle"
              checked={formData.incluir_detalle}
              onChange={(e) => setFormData({ ...formData, incluir_detalle: e.target.checked })}
              className="rounded border-slate-300"
            />
            <Label htmlFor="incluir_detalle">Incluir detalle de items por rubro en la cotización</Label>
          </div>

          {/* Descripción Técnica */}
          <div>
            <Label>Descripción Técnica</Label>
            <textarea
              value={formData.descripcion_tecnica}
              onChange={(e) => setFormData({ ...formData, descripcion_tecnica: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded-md"
              rows="3"
              placeholder="Descripción técnica del proyecto para el cliente..."
            />
          </div>

          {/* Exclusiones */}
          <div>
            <Label>Exclusiones</Label>
            <textarea
              value={formData.exclusiones}
              onChange={(e) => setFormData({ ...formData, exclusiones: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded-md"
              rows="3"
              placeholder="Ej: No incluye mobiliario, decoración, jardín exterior..."
            />
          </div>

          {/* Condiciones */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Plazo de Entrega</Label>
              <Input
                value={formData.plazo_entrega}
                onChange={(e) => setFormData({ ...formData, plazo_entrega: e.target.value })}
                placeholder="Ej: 180 días corridos"
              />
            </div>
            <div>
              <Label>Validez de la Cotización (días)</Label>
              <Input
                type="number"
                value={formData.validez_dias}
                onChange={(e) => setFormData({ ...formData, validez_dias: parseInt(e.target.value) })}
              />
            </div>
          </div>

          {/* Forma de Pago */}
          <div>
            <Label>Forma de Pago</Label>
            <Input
              value={formData.forma_pago}
              onChange={(e) => setFormData({ ...formData, forma_pago: e.target.value })}
              placeholder="Ej: 50% adelanto, 50% finalización"
            />
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
            <Button
              type="button"
              variant="outline"
              onClick={() => onClose(false)}
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700"
              disabled={saving}
            >
              {saving ? "Generando..." : "Generar Cotización"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ==================== COTIZACIONES ====================
function Cotizaciones() {
  const navigate = useNavigate();
  const [cotizaciones, setCotizaciones] = useState([]);
  const [obras, setObras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterEstado, setFilterEstado] = useState("Todos");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [resCotizaciones, resObras] = await Promise.all([
        cotizacionesAPI.getAll(),
        obrasAPI.getAll()
      ]);
      setCotizaciones(resCotizaciones.data.sort((a, b) =>
        new Date(b.fecha_creacion) - new Date(a.fecha_creacion)
      ));
      setObras(resObras.data);
    } catch (error) {
      toast.error("Error al cargar cotizaciones");
    } finally {
      setLoading(false);
    }
  };

  const copiarLink = (link_publico) => {
    const url = `${window.location.origin}/cotizacion-publica/${link_publico}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copiado al portapapeles");
  };

  const filteredCotizaciones = filterEstado === "Todos"
    ? cotizaciones
    : cotizaciones.filter(c => c.estado === filterEstado);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-slate-900">Cotizaciones</h1>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-2">
            <Button
              variant={filterEstado === "Todos" ? "default" : "outline"}
              onClick={() => setFilterEstado("Todos")}
              size="sm"
            >
              Todas ({cotizaciones.length})
            </Button>
            <Button
              variant={filterEstado === "Enviada" ? "default" : "outline"}
              onClick={() => setFilterEstado("Enviada")}
              size="sm"
            >
              Enviada ({cotizaciones.filter(c => c.estado === "Enviada").length})
            </Button>
            <Button
              variant={filterEstado === "Recibida" ? "default" : "outline"}
              onClick={() => setFilterEstado("Recibida")}
              size="sm"
            >
              Recibida ({cotizaciones.filter(c => c.estado === "Recibida").length})
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Lista */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-12 text-center text-slate-600">Cargando cotizaciones...</div>
          ) : filteredCotizaciones.length === 0 ? (
            <div className="py-12 text-center text-slate-600">
              No hay cotizaciones {filterEstado !== "Todos" && `en estado ${filterEstado}`}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Obra</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Validez</TableHead>
                  <TableHead>Fecha Creación</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCotizaciones.map((cotizacion) => {
                  const obra = obras.find(o => o.id === cotizacion.obra_id);
                  return (
                    <TableRow key={cotizacion.id}>
                      <TableCell>
                        <div className="font-medium">{obra?.nombre || "Obra desconocida"}</div>
                        <div className="text-xs text-slate-500">{obra?.cliente}</div>
                      </TableCell>
                      <TableCell className="font-semibold">{formatCurrency(cotizacion.total_general)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Badge
                            variant={
                              cotizacion.estado === 'Enviada' ? 'outline' :
                                cotizacion.estado === 'Recibida' ? 'default' :
                                  'secondary'
                            }
                            className={
                              cotizacion.estado === 'Recibida' ? 'bg-blue-600' :
                                ''
                            }
                          >
                            {cotizacion.estado}
                          </Badge>
                          {cotizacion.tiene_comentario && (
                            <span className="text-amber-600" title="Tiene comentarios">💬</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {cotizacion.validez_dias} días
                      </TableCell>
                      <TableCell>{formatDate(cotizacion.fecha_creacion)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copiarLink(cotizacion.link_publico)}
                        >
                          Copiar Link
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/cotizaciones/${cotizacion.id}`)}
                        >
                          Ver
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== DETALLE COTIZACIÓN ====================
function DetalleCotizacion() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cotizacion, setCotizacion] = useState(null);
  const [presupuesto, setPresupuesto] = useState(null);
  const [obra, setObra] = useState(null);
  const [items, setItems] = useState([]);
  const [rubros, setRubros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const resCotizacion = await cotizacionesAPI.getById(id);
      setCotizacion(resCotizacion.data);

      const resPresupuesto = await presupuestosAPI.getById(resCotizacion.data.presupuesto_id);
      setPresupuesto(resPresupuesto.data);

      const resObra = await obrasAPI.getById(resCotizacion.data.obra_id);
      setObra(resObra.data);

      const [resItems, resRubros] = await Promise.all([
        presupuestoItemsAPI.getByPresupuesto(resCotizacion.data.presupuesto_id),
        rubrosAPI.getAll()
      ]);
      setItems(resItems.data);
      setRubros(resRubros.data);
    } catch (error) {
      toast.error("Error al cargar cotización");
      navigate("/cotizaciones");
    } finally {
      setLoading(false);
    }
  };

  const marcarEnviada = async () => {
    try {
      setEnviando(true);
      // Actualizar estado a Enviada (ya lo está, pero se podría cambiar lógica)
      toast.success("Cotización marcada como enviada");
      loadData();
    } catch (error) {
      toast.error("Error al marcar cotización");
    } finally {
      setEnviando(false);
    }
  };

  const descargarPDF = async () => {
    try {
      toast.info("Generando PDF...");
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cotizaciones/${id}/pdf`, {
        method: 'GET'
      });

      if (!response.ok) throw new Error('Error al generar PDF');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Cotizacion_${obra?.nombre || 'obra'}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success("PDF descargado exitosamente");
    } catch (error) {
      toast.error("Error al descargar PDF");
    }
  };

  const copiarLinkPublico = () => {
    const url = `${window.location.origin}/cotizacion-publica/${cotizacion.link_publico}`;
    navigator.clipboard.writeText(url);
    toast.success("Link público copiado al portapapeles");
  };

  const eliminarCotizacion = async () => {
    if (!window.confirm("¿Está seguro de eliminar esta cotización?")) return;

    try {
      await cotizacionesAPI.delete(id);
      toast.success("Cotización eliminada");
      navigate("/cotizaciones");
    } catch (error) {
      toast.error("Error al eliminar cotización");
    }
  };

  if (loading) {
    return (
      <div className="py-12 text-center text-slate-600">
        Cargando cotización...
      </div>
    );
  }

  if (!cotizacion || !presupuesto || !obra) {
    return (
      <div className="py-12 text-center text-slate-600">
        No se encontró la cotización
      </div>
    );
  }

  // Agrupar items por rubro ID (NO por nombre)
  const itemsPorRubro = items.reduce((acc, item) => {
    const rubroId = item.rubro_id;
    if (!acc[rubroId]) acc[rubroId] = [];
    acc[rubroId].push(item);
    return acc;
  }, {});

  // Calcular subtotales por rubro ID
  const subtotalesPorRubro = {};
  Object.keys(itemsPorRubro).forEach(rubroId => {
    subtotalesPorRubro[rubroId] = itemsPorRubro[rubroId].reduce((sum, item) =>
      sum + (item.cantidad * item.precio_unitario), 0
    );
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Button variant="ghost" onClick={() => navigate("/cotizaciones")} className="mb-2">
            ← Volver a Cotizaciones
          </Button>
          <h1 className="text-3xl font-bold text-slate-900">Detalle de Cotización</h1>
        </div>
        <div className="flex gap-2">
          <Button onClick={descargarPDF} variant="outline">
            📄 Descargar PDF
          </Button>
          <Button onClick={copiarLinkPublico} variant="outline">
            🔗 Copiar Link Público
          </Button>
          <Button onClick={eliminarCotizacion} variant="destructive">
            Eliminar
          </Button>
        </div>
      </div>

      {/* Información General */}
      <Card>
        <CardHeader>
          <CardTitle>Información General</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-slate-500">Obra</Label>
              <p className="font-medium">{obra.nombre}</p>
            </div>
            <div>
              <Label className="text-slate-500">Cliente</Label>
              <p className="font-medium">{obra.cliente}</p>
            </div>
            <div>
              <Label className="text-slate-500">Estado</Label>
              <div className="mt-1">
                <Badge
                  className={
                    cotizacion.estado === 'Aceptada' ? 'bg-green-600' :
                      cotizacion.estado === 'Rechazada' ? 'bg-red-600' :
                        cotizacion.estado === 'Recibida' ? 'bg-blue-600' :
                          cotizacion.estado === 'Vencida' ? 'bg-gray-600' :
                            'bg-amber-600'
                  }
                >
                  {cotizacion.estado}
                </Badge>
              </div>
            </div>
            <div>
              <Label className="text-slate-500">Fecha Creación</Label>
              <p className="font-medium">{formatDate(cotizacion.fecha_creacion)}</p>
            </div>
            <div>
              <Label className="text-slate-500">Validez</Label>
              <p className="font-medium">{cotizacion.validez_dias} días</p>
            </div>
            <div>
              <Label className="text-slate-500">Vencimiento</Label>
              <p className="font-medium">{formatDate(cotizacion.fecha_vencimiento)}</p>
            </div>
            {cotizacion.fecha_recibida && (
              <div>
                <Label className="text-slate-500">Fecha Recibida</Label>
                <p className="font-medium">{formatDate(cotizacion.fecha_recibida)}</p>
              </div>
            )}
            {cotizacion.fecha_respuesta && (
              <div>
                <Label className="text-slate-500">Fecha Respuesta</Label>
                <p className="font-medium">{formatDate(cotizacion.fecha_respuesta)}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Parámetros de Cotización */}
      <Card>
        <CardHeader>
          <CardTitle>Parámetros de la Cotización</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {cotizacion.descripcion_tecnica && (
            <div>
              <Label className="text-slate-500">Descripción Técnica</Label>
              <p className="mt-1 whitespace-pre-wrap">{cotizacion.descripcion_tecnica}</p>
            </div>
          )}
          {cotizacion.exclusiones && (
            <div>
              <Label className="text-slate-500">Exclusiones</Label>
              <p className="mt-1 whitespace-pre-wrap">{cotizacion.exclusiones}</p>
            </div>
          )}
          {cotizacion.plazo_entrega && (
            <div>
              <Label className="text-slate-500">Plazo de Entrega</Label>
              <p className="mt-1">{cotizacion.plazo_entrega}</p>
            </div>
          )}
          {cotizacion.forma_pago && (
            <div>
              <Label className="text-slate-500">Forma de Pago</Label>
              <p className="mt-1">{cotizacion.forma_pago}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Items por Rubro */}
      {cotizacion.incluir_detalle && Object.keys(itemsPorRubro).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Detalle de Items</CardTitle>
          </CardHeader>
          <CardContent>
            {Object.entries(itemsPorRubro).map(([rubroId, rubroItems]) => {
              const rubro = rubros.find(r => r.id === rubroId);
              const rubroNombre = rubro ? rubro.nombre : 'Sin Rubro';

              return (
                <div key={rubroId} className="mb-6 last:mb-0">
                  <h3 className="font-semibold text-lg mb-3 text-[#FFA726]">{rubroNombre}</h3>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item</TableHead>
                        <TableHead className="text-right">Cantidad</TableHead>
                        <TableHead className="text-right">Precio Unit.</TableHead>
                        <TableHead className="text-right">Subtotal</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rubroItems.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <div className="font-medium">{item.nombre}</div>
                            <div className="text-xs text-slate-500">{item.unidad}</div>
                          </TableCell>
                          <TableCell className="text-right">{item.cantidad}</TableCell>
                          <TableCell className="text-right">{formatCurrency(item.precio_unitario)}</TableCell>
                          <TableCell className="text-right font-semibold">{formatCurrency(item.subtotal)}</TableCell>
                        </TableRow>
                      ))}
                      {/* Fila de Subtotal por Rubro */}
                      <TableRow className="bg-slate-100 font-bold border-t-2 border-slate-300">
                        <TableCell colSpan={3} className="text-right">
                          Subtotal {rubroNombre}:
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(subtotalesPorRubro[rubroId])}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Totales */}
      <Card>
        <CardHeader>
          <CardTitle>Resumen de Totales</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex justify-between text-slate-600">
              <span>Total Materiales</span>
              <span className="font-medium">{formatCurrency(cotizacion.total_materiales)}</span>
            </div>
            <div className="flex justify-between text-slate-600">
              <span>Total Mano de Obra (base)</span>
              <span className="font-medium">{formatCurrency(cotizacion.total_mano_obra)}</span>
            </div>
            <div className="flex justify-between text-purple-700 text-sm">
              <span className="pl-4">+ Leyes Sociales ({presupuesto.porcentaje_leyes_sociales}% sobre MO)</span>
              <span className="font-medium">{formatCurrency(cotizacion.total_mano_obra * presupuesto.porcentaje_leyes_sociales / 100)}</span>
            </div>
            <div className="flex justify-between text-slate-600 text-sm pt-2 border-t">
              <span>Costos Indirectos + Beneficios + IVA</span>
              <span className="font-medium text-xs">({presupuesto.porcentaje_costos_indirectos}% + {presupuesto.porcentaje_beneficios}% + {presupuesto.porcentaje_iva}%)</span>
            </div>
            <div className="flex justify-between text-lg font-bold text-slate-900 pt-3 border-t-2">
              <span>Total General</span>
              <span className="text-[#FFA726]">{formatCurrency(cotizacion.total_general)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Comentarios del Cliente */}
      {cotizacion.comentarios_recibida && (
        <Card className="border-l-4 border-l-blue-500">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              💬 Comentarios del Cliente
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-blue-50 p-4 rounded-md">
              <p className="whitespace-pre-wrap text-slate-700">{cotizacion.comentarios_recibida}</p>
              {cotizacion.fecha_recibida && (
                <p className="text-xs text-slate-500 mt-3">
                  Recibido el {formatDate(cotizacion.fecha_recibida)}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ==================== COTIZACIÓN PÚBLICA ====================
function CotizacionPublica() {
  const { link } = useParams();
  const [cotizacion, setCotizacion] = useState(null);
  const [presupuesto, setPresupuesto] = useState(null);
  const [obra, setObra] = useState(null);
  const [items, setItems] = useState([]);
  const [rubros, setRubros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [procesando, setProcesando] = useState(false);
  const [comentarios, setComentarios] = useState("");

  useEffect(() => {
    loadData();
  }, [link]);

  const loadData = async () => {
    try {
      const resCotizacion = await cotizacionesAPI.getByLinkPublico(link);
      setCotizacion(resCotizacion.data);

      const resPresupuesto = await presupuestosAPI.getById(resCotizacion.data.presupuesto_id);
      setPresupuesto(resPresupuesto.data);

      const resObra = await obrasAPI.getById(resCotizacion.data.obra_id);
      setObra(resObra.data);

      if (resCotizacion.data.incluir_detalle) {
        const [resItems, resRubros] = await Promise.all([
          presupuestoItemsAPI.getByPresupuesto(resCotizacion.data.presupuesto_id),
          rubrosAPI.getAll()
        ]);
        setItems(resItems.data);
        setRubros(resRubros.data);
      }
    } catch (error) {
      toast.error("Cotización no encontrada o inválida");
    } finally {
      setLoading(false);
    }
  };

  const marcarRecibida = async () => {
    try {
      setProcesando(true);
      await cotizacionesAPI.marcarRecibida(link, comentarios || undefined);
      toast.success("✅ Cotización marcada como recibida");
      loadData();
      setComentarios("");
    } catch (error) {
      toast.error("Error al marcar como recibida");
    } finally {
      setProcesando(false);
    }
  };

  const descargarPDF = async () => {
    try {
      toast.info("Generando PDF...");
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cotizaciones/publico/${link}/pdf`, {
        method: 'GET'
      });

      if (!response.ok) throw new Error('Error al generar PDF');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Cotizacion_${obra?.nombre || 'obra'}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success("PDF descargado exitosamente");
    } catch (error) {
      toast.error("Error al descargar PDF");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-xl text-slate-600">Cargando cotización...</div>
        </div>
      </div>
    );
  }

  if (!cotizacion || !presupuesto || !obra) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="py-12 text-center">
            <div className="text-6xl mb-4">❌</div>
            <h2 className="text-2xl font-bold mb-2">Cotización no encontrada</h2>
            <p className="text-slate-600">El link proporcionado no es válido o ha expirado</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Verificar si está vencida
  const estaVencida = cotizacion.estado === 'Vencida';
  const puedeInteractuar = !estaVencida && cotizacion.estado !== 'Recibida';

  // Agrupar items por rubro ID (NO por nombre)
  const itemsPorRubro = items.reduce((acc, item) => {
    const rubroId = item.rubro_id;
    if (!acc[rubroId]) acc[rubroId] = [];
    acc[rubroId].push(item);
    return acc;
  }, {});

  // Calcular subtotales por rubro ID
  const subtotalesPorRubro = {};
  Object.keys(itemsPorRubro).forEach(rubroId => {
    subtotalesPorRubro[rubroId] = itemsPorRubro[rubroId].reduce((sum, item) =>
      sum + (item.cantidad * item.precio_unitario), 0
    );
  });

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-5xl mx-auto px-4 space-y-6">
        {/* Header con Logo */}
        <div className="bg-white rounded-lg shadow-sm p-6 text-center border-b-4 border-[#FFA726]">
          <img src="./logo-deflorencia.png" alt="Deflorencia" className="h-16 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-slate-900">Cotización de Proyecto</h1>
          <p className="text-slate-600 mt-2">Propuesta comercial para {obra.cliente}</p>
        </div>

        {/* Estado y Acciones */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <Label className="text-slate-500">Estado de la Cotización</Label>
                <div className="mt-1">
                  <Badge
                    className={`text-lg px-4 py-1 ${cotizacion.estado === 'Aceptada' ? 'bg-green-600' :
                      cotizacion.estado === 'Rechazada' ? 'bg-red-600' :
                        cotizacion.estado === 'Recibida' ? 'bg-blue-600' :
                          cotizacion.estado === 'Vencida' ? 'bg-gray-600' :
                            'bg-amber-600'
                      }`}
                  >
                    {cotizacion.estado}
                  </Badge>
                </div>
              </div>
              <Button onClick={descargarPDF} variant="outline" size="lg">
                📄 Descargar PDF
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Información del Proyecto */}
        <Card>
          <CardHeader>
            <CardTitle>Información del Proyecto</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-500">Proyecto</Label>
                <p className="font-medium text-lg">{obra.nombre}</p>
              </div>
              <div>
                <Label className="text-slate-500">Cliente</Label>
                <p className="font-medium text-lg">{obra.cliente}</p>
              </div>
              <div>
                <Label className="text-slate-500">Ubicación</Label>
                <p className="font-medium">{obra.ubicacion || 'No especificada'}</p>
              </div>
              <div>
                <Label className="text-slate-500">Superficie</Label>
                <p className="font-medium">{obra.m2 ? `${obra.m2} m²` : 'No especificada'}</p>
              </div>
              <div>
                <Label className="text-slate-500">Fecha de Emisión</Label>
                <p className="font-medium">{formatDate(cotizacion.fecha_creacion)}</p>
              </div>
              <div>
                <Label className="text-slate-500">Validez</Label>
                <p className="font-medium">{cotizacion.validez_dias} días (hasta {formatDate(cotizacion.fecha_vencimiento)})</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Descripción Técnica */}
        {cotizacion.descripcion_tecnica && (
          <Card>
            <CardHeader>
              <CardTitle>Descripción Técnica</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="whitespace-pre-wrap">{cotizacion.descripcion_tecnica}</p>
            </CardContent>
          </Card>
        )}

        {/* Items Detallados */}
        {cotizacion.incluir_detalle && Object.keys(itemsPorRubro).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Detalle de Items</CardTitle>
            </CardHeader>
            <CardContent>
              {Object.entries(itemsPorRubro).map(([rubroId, rubroItems]) => {
                const rubro = rubros.find(r => r.id === rubroId);
                const rubroNombre = rubro ? rubro.nombre : 'Sin Rubro';

                return (
                  <div key={rubroId} className="mb-6 last:mb-0">
                    <h3 className="font-semibold text-lg mb-3 text-[#FFA726] bg-orange-50 px-3 py-2 rounded">
                      {rubroNombre}
                    </h3>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Item</TableHead>
                          <TableHead className="text-right">Cantidad</TableHead>
                          <TableHead className="text-right">Precio Unit.</TableHead>
                          <TableHead className="text-right">Subtotal</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {rubroItems.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell>
                              <div className="font-medium">{item.nombre}</div>
                              <div className="text-xs text-slate-500">{item.unidad}</div>
                            </TableCell>
                            <TableCell className="text-right">{item.cantidad}</TableCell>
                            <TableCell className="text-right">{formatCurrency(item.precio_unitario)}</TableCell>
                            <TableCell className="text-right font-semibold">{formatCurrency(item.subtotal)}</TableCell>
                          </TableRow>
                        ))}
                        {/* Fila de Subtotal por Rubro */}
                        <TableRow className="bg-orange-50 font-bold border-t-2 border-orange-200">
                          <TableCell colSpan={3} className="text-right text-[#FFA726]">
                            Subtotal {rubroNombre}:
                          </TableCell>
                          <TableCell className="text-right text-[#FFA726]">
                            {formatCurrency(subtotalesPorRubro[rubroId])}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Resumen de Totales */}
        <Card className="border-2 border-[#FFA726]">
          <CardHeader className="bg-orange-50">
            <CardTitle className="text-2xl">Resumen de Inversión</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-3">
              <div className="flex justify-between text-lg">
                <span className="text-slate-600">Total Materiales</span>
                <span className="font-semibold">{formatCurrency(cotizacion.total_materiales)}</span>
              </div>
              <div className="flex justify-between text-lg">
                <span className="text-slate-600">Total Mano de Obra (base)</span>
                <span className="font-semibold">{formatCurrency(cotizacion.total_mano_obra)}</span>
              </div>
              <div className="flex justify-between text-purple-700">
                <span className="pl-6 text-sm">+ Leyes Sociales ({presupuesto.porcentaje_leyes_sociales}%)</span>
                <span className="font-semibold">{formatCurrency(cotizacion.total_mano_obra * presupuesto.porcentaje_leyes_sociales / 100)}</span>
              </div>
              <div className="flex justify-between text-sm text-slate-500 pt-2 border-t">
                <span>Incluye: Costos Indirectos, Beneficios e IVA</span>
              </div>
              <div className="flex justify-between text-2xl font-bold text-slate-900 pt-3 border-t-2 border-[#FFA726]">
                <span>TOTAL GENERAL</span>
                <span className="text-[#FFA726]">{formatCurrency(cotizacion.total_general)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Condiciones */}
        <Card>
          <CardHeader>
            <CardTitle>Condiciones Comerciales</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {cotizacion.plazo_entrega && (
              <div>
                <Label className="text-slate-500 font-semibold">Plazo de Entrega</Label>
                <p className="mt-1">{cotizacion.plazo_entrega}</p>
              </div>
            )}
            {cotizacion.forma_pago && (
              <div>
                <Label className="text-slate-500 font-semibold">Forma de Pago</Label>
                <p className="mt-1">{cotizacion.forma_pago}</p>
              </div>
            )}
            {cotizacion.exclusiones && (
              <div>
                <Label className="text-slate-500 font-semibold">Exclusiones</Label>
                <p className="mt-1 whitespace-pre-wrap">{cotizacion.exclusiones}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Sección de Interacción del Cliente */}
        {puedeInteractuar && (
          <Card className="border-2 border-blue-500">
            <CardHeader className="bg-blue-50">
              <CardTitle>Confirmar Recepción de Cotización</CardTitle>
              <CardDescription>
                Por favor confirme la recepción y agregue sus comentarios si lo desea
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div>
                <Label htmlFor="comentarios">Comentarios (opcional)</Label>
                <textarea
                  id="comentarios"
                  className="w-full mt-2 px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FFA726]"
                  rows={4}
                  placeholder="Agregue aquí cualquier comentario, consulta o inquietud sobre la cotización..."
                  value={comentarios}
                  onChange={(e) => setComentarios(e.target.value)}
                />
              </div>

              <Button
                onClick={marcarRecibida}
                disabled={procesando}
                className="bg-blue-600 hover:bg-blue-700 w-full"
                size="lg"
              >
                📬 Marcar como Recibida
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Mensaje de Estado Final */}
        {!puedeInteractuar && (
          <Card className="border-2 border-slate-300">
            <CardContent className="py-8 text-center">
              {cotizacion.estado === 'Recibida' && (
                <>
                  <div className="text-6xl mb-4">✅</div>
                  <h2 className="text-2xl font-bold text-blue-600 mb-2">Cotización Recibida</h2>
                  <p className="text-slate-600">
                    Gracias por confirmar la recepción. Nos pondremos en contacto a la brevedad.
                  </p>
                  {cotizacion.fecha_recibida && (
                    <p className="text-sm text-slate-500 mt-2">
                      Recibida el {formatDate(cotizacion.fecha_recibida)}
                    </p>
                  )}
                  {cotizacion.comentarios_recibida && (
                    <div className="mt-4 p-4 bg-slate-50 rounded-md text-left">
                      <p className="text-sm font-semibold text-slate-700 mb-2">Sus comentarios:</p>
                      <p className="text-sm text-slate-600">{cotizacion.comentarios_recibida}</p>
                    </div>
                  )}
                </>
              )}
              {estaVencida && (
                <>
                  <div className="text-6xl mb-4">⏰</div>
                  <h2 className="text-2xl font-bold text-slate-600 mb-2">Cotización Vencida</h2>
                  <p className="text-slate-600">
                    Esta cotización ha expirado. Por favor contacte con nosotros para solicitar una nueva.
                  </p>
                  {cotizacion.fecha_vencimiento && (
                    <p className="text-sm text-slate-500 mt-2">
                      Venció el {formatDate(cotizacion.fecha_vencimiento)}
                    </p>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center text-slate-500 text-sm py-6">
          <p>© 2024 Deflorencia - Gestión de Proyectos de Construcción</p>
          <p className="mt-1">Este documento es una cotización válida sujeta a los términos y condiciones especificados</p>
        </div>
      </div>
    </div>
  );
}


// ==================== CRONOGRAMAS ====================
function Cronogramas() {
  const navigate = useNavigate();
  const [cronogramas, setCronogramas] = useState([]);
  const [obras, setObras] = useState([]);
  const [obrasDisponibles, setObrasDisponibles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    obra_id: '',
    nombre: '',
    fecha_inicio: '',
    director_obra: '',
    notas: ''
  });


  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 25;

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [cronogramas.length]);


  const loadData = async () => {
    try {
      const [resCronogramas, resObras, resPresupuestos] = await Promise.all([
        cronogramasAPI.getAll(),
        obrasAPI.getAll(),
        presupuestosAPI.getAll()
      ]);

      setCronogramas(resCronogramas.data.sort((a, b) =>
        new Date(b.fecha_creacion) - new Date(a.fecha_creacion)
      ));
      setObras(resObras.data);

      // Filtrar obras que tienen al menos un presupuesto "Listo" y están activas
      const obrasConPresupuestoListo = resObras.data.filter(obra => {
        const tienePresupuestoListo = resPresupuestos.data.some(
          p => p.obra_id === obra.id && p.estado === "Listo"
        );
        return tienePresupuestoListo && obra.estado === "Activa";
      });

      setObrasDisponibles(obrasConPresupuestoListo);
    } catch (error) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCronograma = () => {
    setFormData({
      obra_id: '',
      nombre: '',
      fecha_inicio: new Date().toISOString().split('T')[0], // Fecha actual
      director_obra: '',
      notas: ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.obra_id || !formData.nombre || !formData.fecha_inicio) {
      toast.error("Por favor complete los campos obligatorios");
      return;
    }

    try {
      const result = await cronogramasAPI.create(formData);
      toast.success("Cronograma creado exitosamente con tareas basadas en los rubros de la obra");
      setShowModal(false);
      loadData();

      // Navegar al cronograma recién creado
      if (result.data && result.data.id) {
        setTimeout(() => {
          navigate(`/cronogramas/${result.data.id}`);
        }, 500);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Error al crear cronograma";
      toast.error(errorMsg);
    }
  };

  const handleDeleteCronograma = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("¿Está seguro de eliminar este cronograma y todas sus tareas?")) return;

    try {
      await cronogramasAPI.delete(id);
      toast.success("Cronograma eliminado exitosamente");
      loadData();
    } catch (error) {
      toast.error("Error al eliminar cronograma");
    }
  };

  const handleViewCronograma = (cronograma) => {
    navigate(`/cronogramas/${cronograma.id}`);
  };

  // Paginación
  const totalPages = Math.ceil(cronogramas.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedCronogramas = cronogramas.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-slate-900">Cronogramas</h1>
        <Button
          onClick={handleCreateCronograma}
          className="bg-deflorencia hover:bg-[#ff9100]"
        >
          + Nuevo Cronograma
        </Button>
      </div>

      {/* Lista de Cronogramas */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-12 text-center text-slate-600">Cargando cronogramas...</div>
          ) : cronogramas.length === 0 ? (
            <div className="py-12 text-center text-slate-600">
              No hay cronogramas creados
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cronograma</TableHead>
                    <TableHead>Obra</TableHead>
                    <TableHead>Director</TableHead>
                    <TableHead>Fecha Inicio</TableHead>
                    <TableHead>Fecha Fin Estimada</TableHead>
                    <TableHead>Fecha Creación</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedCronogramas.map((cronograma) => {
                    const obra = obras.find(o => o.id === cronograma.obra_id);
                    return (
                      <TableRow
                        key={cronograma.id}
                        onClick={() => handleViewCronograma(cronograma)}
                        className="cursor-pointer hover:bg-slate-50"
                      >
                        <TableCell>
                          <div className="font-medium">{cronograma.nombre}</div>
                          {cronograma.notas && (
                            <div className="text-xs text-slate-500">{cronograma.notas}</div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{obra?.nombre || "Sin obra"}</div>
                          <div className="text-xs text-slate-500">{obra?.cliente}</div>
                        </TableCell>
                        <TableCell>{cronograma.director_obra || '-'}</TableCell>
                        <TableCell>{formatDate(cronograma.fecha_inicio)}</TableCell>
                        <TableCell>
                          {cronograma.fecha_fin_estimada ?
                            formatDate(cronograma.fecha_fin_estimada) :
                            <span className="text-slate-400">No estimada</span>
                          }
                        </TableCell>
                        <TableCell>{formatDate(cronograma.fecha_creacion)}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewCronograma(cronograma);
                            }}
                          >
                            Ver
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => handleDeleteCronograma(e, cronograma.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            Eliminar
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                totalItems={cronogramas.length}
                itemsPerPage={ITEMS_PER_PAGE}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Modal Crear Cronograma */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Nuevo Cronograma</h3>

            {obrasDisponibles.length === 0 ? (
              <div className="py-6">
                <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-4">
                  <p className="text-amber-800 font-medium">No hay obras disponibles para crear cronograma</p>
                  <p className="text-amber-700 text-sm mt-2">
                    Para crear un cronograma, la obra debe:
                  </p>
                  <ul className="list-disc list-inside text-amber-700 text-sm mt-1 ml-2">
                    <li>Estar en estado “Activa”</li>
                    <li>Tener al menos un presupuesto en estado “Listo”</li>
                  </ul>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowModal(false)}
                  className="w-full"
                >
                  Cerrar
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="bg-blue-50 border-l-4 border-blue-500 p-3 mb-4">
                  <p className="text-sm text-blue-800">
                    💡 Las tareas se crearán automáticamente basándose en los rubros de la obra seleccionada.
                  </p>
                </div>

                <div>
                  <Label htmlFor="obra_id">Obra *</Label>
                  <select
                    id="obra_id"
                    value={formData.obra_id}
                    onChange={(e) => setFormData({ ...formData, obra_id: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md"
                    required
                  >
                    <option value="">Seleccionar obra</option>
                    {obrasDisponibles.map(obra => (
                      <option key={obra.id} value={obra.id}>
                        {obra.nombre} - {obra.cliente}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-slate-500 mt-1">
                    Solo se muestran obras activas con presupuesto listo
                  </p>
                </div>

                <div>
                  <Label htmlFor="nombre">Nombre del Cronograma *</Label>
                  <Input
                    id="nombre"
                    value={formData.nombre}
                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                    placeholder="ej: Cronograma General M5"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="fecha_inicio">Fecha de Inicio *</Label>
                  <Input
                    id="fecha_inicio"
                    type="date"
                    value={formData.fecha_inicio}
                    onChange={(e) => setFormData({ ...formData, fecha_inicio: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="director_obra">Director de Obra</Label>
                  <Input
                    id="director_obra"
                    value={formData.director_obra}
                    onChange={(e) => setFormData({ ...formData, director_obra: e.target.value })}
                    placeholder="ej: Francisco Mateuci"
                  />
                </div>

                <div>
                  <Label htmlFor="notas">Notas</Label>
                  <textarea
                    id="notas"
                    value={formData.notas}
                    onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md"
                    rows={3}
                    placeholder="Notas adicionales del cronograma"
                  />
                </div>

                <div className="flex justify-end space-x-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowModal(false)}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    className="bg-deflorencia hover:bg-[#ff9100]"
                  >
                    Crear Cronograma
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== DETALLE CRONOGRAMA ====================
function DetalleCronograma() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cronograma, setCronograma] = useState(null);
  const [obra, setObra] = useState(null);

  // ==================== VISTA GANTT SIMPLE ====================
  /* eslint-disable react/no-unstable-nested-components */
  function VistaGantt({ tareas, rubros, cronograma, obra }) {
    if (!tareas || tareas.length === 0) {
      return (
        <div className="py-12 text-center text-slate-600">
          No hay tareas para mostrar en el Gantt.
        </div>
      );
    }

    const handleExportPDF = async () => {
      const jsPDF = (await import('jspdf')).default;
      const html2canvas = (await import('html2canvas')).default;

      const element = document.getElementById('gantt-chart-content');
      const button = document.getElementById('export-gantt-button');

      if (!element) {
        toast.error('No se encontró el contenido del Gantt');
        return;
      }

      if (button) button.style.display = 'none';

      toast.info('Generando PDF completo del Gantt... esto puede tomar unos segundos');

      try {
        // Guardar el overflow original
        const scrollContainer = element.querySelector('.overflow-x-auto');
        const originalOverflow = scrollContainer ? scrollContainer.style.overflow : null;

        // Remover scroll temporalmente para capturar todo el contenido
        if (scrollContainer) {
          scrollContainer.style.overflow = 'visible';
        }

        // Capturar el canvas con todo el contenido (incluyendo scroll horizontal)
        const canvas = await html2canvas(element, {
          scale: 1.5, // Reducir un poco para mejor rendimiento
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          width: element.scrollWidth,
          height: element.scrollHeight,
          windowWidth: element.scrollWidth,
          windowHeight: element.scrollHeight,
          scrollX: 0,
          scrollY: 0
        });

        // Restaurar overflow
        if (scrollContainer && originalOverflow !== null) {
          scrollContainer.style.overflow = originalOverflow;
        }

        const imgData = canvas.toDataURL('image/png');

        // Calcular dimensiones para PDF (landscape)
        const pdf = new jsPDF('l', 'mm', 'a4');
        const pdfWidth = 297; // A4 landscape width
        const pdfHeight = 210; // A4 landscape height

        const imgWidth = canvas.width;
        const imgHeight = canvas.height;

        // Calcular escala para ajustar al ancho del PDF
        const scale = pdfWidth / imgWidth;
        const scaledHeight = imgHeight * scale;

        // Si cabe en una página
        if (scaledHeight <= pdfHeight) {
          pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, scaledHeight);
        } else {
          // Dividir en múltiples páginas verticalmente
          let heightLeft = scaledHeight;
          let position = 0;
          let pageNum = 0;

          while (heightLeft > 0) {
            if (pageNum > 0) {
              pdf.addPage();
            }

            const yOffset = -(pageNum * pdfHeight);
            pdf.addImage(imgData, 'PNG', 0, yOffset, pdfWidth, scaledHeight);

            heightLeft -= pdfHeight;
            pageNum++;
          }
        }

        pdf.save(`Cronograma_${obra?.nombre || 'Gantt'}_${new Date().toISOString().split('T')[0]}.pdf`);
        toast.success('PDF del Gantt completo descargado exitosamente');
      } catch (error) {
        console.error('Error al generar PDF:', error);
        toast.error('Error al generar el PDF del Gantt');
      } finally {
        if (button) button.style.display = 'flex';
      }
    };

    // Calcular fechas del proyecto
    const fechas = tareas.map(t => [new Date(t.fecha_inicio), new Date(t.fecha_fin)]).flat();
    const fechaInicio = new Date(Math.min(...fechas));
    const fechaFin = new Date(Math.max(...fechas));

    // Crear timeline por días (más profesional)
    const diasTotales = Math.ceil((fechaFin - fechaInicio) / (1000 * 60 * 60 * 24)) + 1;
    const dias = [];
    let currentDate = new Date(fechaInicio);

    for (let i = 0; i < diasTotales; i++) {
      const esPrimerDiaMes = currentDate.getDate() === 1;
      const esLunes = currentDate.getDay() === 1;

      dias.push({
        fecha: new Date(currentDate),
        dia: currentDate.getDate(),
        mes: currentDate.toLocaleDateString('es-ES', { month: 'short' }),
        esPrimerDiaMes,
        esLunes,
        mesCompleto: currentDate.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })
      });

      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Agrupar por meses para header
    const meses = [];
    let mesActual = null;
    let diasEnMes = 0;

    dias.forEach(dia => {
      const mesKey = `${dia.fecha.getMonth()}-${dia.fecha.getFullYear()}`;
      if (mesActual !== mesKey) {
        if (diasEnMes > 0) {
          meses.push({ label: dias[dias.indexOf(dia) - diasEnMes].mesCompleto, dias: diasEnMes });
        }
        mesActual = mesKey;
        diasEnMes = 1;
      } else {
        diasEnMes++;
      }
    });
    if (diasEnMes > 0) {
      meses.push({ label: dias[dias.length - diasEnMes].mesCompleto, dias: diasEnMes });
    }

    // Colores por rubro (paleta variada y profesional)
    const coloresPorRubro = [
      '#3B82F6', // Azul
      '#10B981', // Verde
      '#F59E0B', // Ámbar
      '#EF4444', // Rojo
      '#8B5CF6', // Púrpura
      '#06B6D4', // Cyan
      '#F97316', // Naranja
      '#EC4899', // Rosa
      '#14B8A6', // Teal
      '#6366F1', // Índigo
    ];

    const getColorPorRubro = (rubroId) => {
      if (!rubroId) return '#94A3B8'; // Gris por defecto

      // Crear un hash simple del rubroId para asignar color consistente
      const hash = rubroId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
      return coloresPorRubro[hash % coloresPorRubro.length];
    };

    // Calcular posición de barra
    const calcularPosicion = (tarea) => {
      const inicioTarea = new Date(tarea.fecha_inicio);
      const finTarea = new Date(tarea.fecha_fin);

      const diasDesdeInicio = Math.ceil((inicioTarea - fechaInicio) / (1000 * 60 * 60 * 24));
      const duracionTarea = Math.ceil((finTarea - inicioTarea) / (1000 * 60 * 60 * 24)) + 1;

      const left = (diasDesdeInicio / diasTotales) * 100;
      const width = (duracionTarea / diasTotales) * 100;

      return { left: `${left}%`, width: `${Math.max(width, 1)}%` };
    };

    return (
      <div className="bg-white border rounded-lg p-6" id="gantt-chart-content">
        {/* Header con botón de exportar */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="text-xl font-bold text-slate-900">Cronograma Gantt</h3>
            <p className="text-sm text-slate-600 mt-1">
              {obra?.nombre} • {tareas.length} tareas • {diasTotales} días
            </p>
          </div>
          <Button
            id="export-gantt-button"
            onClick={handleExportPDF}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
          >
            <Download className="w-4 h-4" />
            Exportar PDF
          </Button>
        </div>

        {/* Timeline profesional tipo calendario */}
        <div className="overflow-x-auto">
          <div style={{ minWidth: `${Math.max(diasTotales * 30, 1200)}px` }}>

            {/* Header de meses */}
            <div className="flex border-b-2 border-slate-400">
              <div className="w-64 bg-slate-100 border-r-2 border-slate-400 p-3">
                <div className="font-bold text-sm text-slate-800">ACTIVIDAD</div>
              </div>
              <div className="flex-1 flex">
                {meses.map((mes, idx) => (
                  <div
                    key={idx}
                    className="bg-slate-100 border-r border-slate-300 px-2 py-3 text-center font-bold text-sm text-slate-800 uppercase"
                    style={{ width: `${(mes.dias / diasTotales) * 100}%` }}
                  >
                    {mes.label}
                  </div>
                ))}
              </div>
            </div>

            {/* Header de días */}
            <div className="flex border-b border-slate-300 bg-slate-50">
              <div className="w-64 border-r-2 border-slate-400"></div>
              <div className="flex-1 flex">
                {dias.map((dia, idx) => (
                  <div
                    key={idx}
                    className={`flex-1 text-center py-2 text-xs border-r ${dia.esPrimerDiaMes
                      ? 'border-r-2 border-slate-400 bg-blue-50 font-bold text-blue-900'
                      : dia.esLunes
                        ? 'border-slate-300 font-semibold'
                        : 'border-slate-200'
                      }`}
                    style={{ minWidth: '30px' }}
                  >
                    <div>{dia.dia}</div>
                    {dia.esPrimerDiaMes && <div className="text-[10px] text-blue-700">{dia.mes}</div>}
                  </div>
                ))}
              </div>
            </div>

            {/* Tareas con barras */}
            <div>
              {tareas.map((tarea, index) => {
                const posicion = calcularPosicion(tarea);
                const rubro = rubros.find(r => r.id === tarea.rubro_id);

                return (
                  <div key={tarea.id} className="flex border-b border-slate-200 hover:bg-slate-50">
                    {/* Columna de tarea */}
                    <div className="w-64 border-r-2 border-slate-400 py-1 px-2 bg-white">
                      <div className="font-semibold text-xs text-slate-900 leading-tight">{tarea.nombre}</div>
                      <div className="text-[10px] text-slate-500 flex items-center gap-1 mt-0.5">
                        <span className="truncate">{formatDate(tarea.fecha_inicio)} → {formatDate(tarea.fecha_fin)}</span>
                      </div>
                      {rubro && (
                        <div className="text-[10px] text-slate-400">{rubro.nombre}</div>
                      )}
                    </div>

                    {/* Timeline con grid de días */}
                    <div className="flex-1 relative" style={{ minHeight: '36px' }}>
                      {/* Grid vertical completo */}
                      <div className="absolute inset-0 flex">
                        {dias.map((dia, idx) => (
                          <div
                            key={idx}
                            className={`flex-1 border-r ${dia.esPrimerDiaMes
                              ? 'border-r-2 border-slate-400 bg-slate-50'
                              : dia.esLunes
                                ? 'border-slate-300 bg-slate-50'
                                : 'border-slate-200'
                              }`}
                            style={{ minWidth: '30px' }}
                          ></div>
                        ))}
                      </div>

                      {/* Línea de HOY */}
                      {(() => {
                        const hoy = new Date();
                        hoy.setHours(0, 0, 0, 0);
                        if (hoy >= fechaInicio && hoy <= fechaFin) {
                          const diasDesdeInicio = Math.ceil((hoy - fechaInicio) / (1000 * 60 * 60 * 24));
                          const posicionHoy = (diasDesdeInicio / diasTotales) * 100;

                          return (
                            <div
                              className="absolute top-0 bottom-0 w-1 bg-red-500 z-20"
                              style={{ left: `${posicionHoy}%` }}
                              title="Hoy"
                            >
                              {index === 0 && (
                                <div className="absolute -top-8 -left-6 text-xs font-bold text-white bg-red-500 px-3 py-1 rounded shadow-lg whitespace-nowrap">
                                  HOY {hoy.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                                </div>
                              )}
                            </div>
                          );
                        }
                        return null;
                      })()}

                      {/* Barra de progreso de la tarea */}
                      <div
                        className="absolute top-1/2 -translate-y-1/2 h-5 rounded shadow-sm flex items-center justify-center text-white text-[10px] font-bold z-10 cursor-pointer hover:shadow-md transition-shadow"
                        style={{
                          ...posicion,
                          backgroundColor: getColorPorRubro(tarea.rubro_id),
                          opacity: tarea.estado === 'Completada' ? 0.8 : 1,
                          minWidth: '2%'
                        }}
                        title={`${tarea.nombre}\n${rubro?.nombre || 'Sin rubro'}\n${formatDate(tarea.fecha_inicio)} - ${formatDate(tarea.fecha_fin)}\nDuración: ${tarea.duracion_dias} días\nEstado: ${tarea.estado}`}
                      >
                        <span className="px-1.5 truncate text-[10px]">{tarea.duracion_dias}d</span>
                        {tarea.estado === 'Completada' && <span className="ml-0.5 text-[10px]">✓</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Leyenda */}
        <div className="mt-6 pt-4 border-t border-slate-300">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-6 h-4 bg-green-500 rounded"></div>
                <span className="font-medium">Completada</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-4 bg-blue-500 rounded"></div>
                <span className="font-medium">En Progreso</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-4 bg-slate-400 rounded"></div>
                <span className="font-medium">Pendiente</span>
              </div>
            </div>
            <div className="text-xs text-slate-500">
              Generado: {new Date().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}
            </div>
          </div>
        </div>
      </div>
    );
  }
  const [tareas, setTareas] = useState([]);
  const [rubros, setRubros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('lista'); // 'lista' o 'gantt'
  const [showTareaModal, setShowTareaModal] = useState(false);
  const [editingTarea, setEditingTarea] = useState(null);
  const [tareaFormData, setTareaFormData] = useState({
    nombre: '',
    rubro_id: '',
    duracion_dias: 1,
    fecha_inicio: '',
    orden: 0,
    notas: ''
  });

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const resCronograma = await cronogramasAPI.getById(id);
      setCronograma(resCronograma.data);

      const resObra = await obrasAPI.getById(resCronograma.data.obra_id);
      setObra(resObra.data);

      const [resTareas, resRubros] = await Promise.all([
        cronogramaTareasAPI.getByCronograma(id),
        rubrosAPI.getAll()
      ]);
      setTareas(resTareas.data);
      setRubros(resRubros.data);
    } catch (error) {
      toast.error("Error al cargar cronograma");
      navigate("/cronogramas");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTarea = () => {
    const fechaInicio = cronograma?.fecha_inicio || new Date().toISOString().split('T')[0];
    setTareaFormData({
      nombre: '',
      rubro_id: '',
      duracion_dias: 1,
      fecha_inicio: fechaInicio,
      orden: tareas.length,
      notas: ''
    });
    setEditingTarea(null);
    setShowTareaModal(true);
  };

  const handleEditTarea = (tarea) => {
    setTareaFormData({
      nombre: tarea.nombre,
      rubro_id: tarea.rubro_id,
      duracion_dias: tarea.duracion_dias,
      fecha_inicio: tarea.fecha_inicio,
      orden: tarea.orden,
      notas: tarea.notas || ''
    });
    setEditingTarea(tarea);
    setShowTareaModal(true);
  };

  const handleSubmitTarea = async (e) => {
    e.preventDefault();

    if (!tareaFormData.nombre || !tareaFormData.rubro_id || !tareaFormData.fecha_inicio) {
      toast.error("Por favor complete los campos obligatorios");
      return;
    }

    try {
      const dataToSubmit = {
        ...tareaFormData,
        cronograma_id: id,
        duracion_dias: parseInt(tareaFormData.duracion_dias)
      };

      if (editingTarea) {
        await cronogramaTareasAPI.update(editingTarea.id, dataToSubmit);
        toast.success("Tarea actualizada exitosamente");
      } else {
        await cronogramaTareasAPI.create(dataToSubmit);
        toast.success("Tarea creada exitosamente");
      }

      setShowTareaModal(false);
      loadData();
    } catch (error) {
      toast.error("Error al guardar tarea");
    }
  };

  const handleUpdateEstado = async (tareaId, nuevoEstado) => {
    try {
      await cronogramaTareasAPI.updateEstado(tareaId, nuevoEstado);
      toast.success("Estado actualizado");
      loadData();
    } catch (error) {
      toast.error("Error al actualizar estado");
    }
  };

  const handleDeleteTarea = async (tareaId) => {
    if (!window.confirm("¿Está seguro de eliminar esta tarea?")) return;

    try {
      await cronogramaTareasAPI.delete(tareaId);
      toast.success("Tarea eliminada");
      loadData();
    } catch (error) {
      toast.error("Error al eliminar tarea");
    }
  };

  if (loading) {
    return (
      <div className="py-12 text-center text-slate-600">
        Cargando cronograma...
      </div>
    );
  }

  if (!cronograma || !obra) {
    return (
      <div className="py-12 text-center text-slate-600">
        Cronograma no encontrado
      </div>
    );
  }

  // Agrupar tareas por rubro
  const tareasPorRubro = tareas.reduce((acc, tarea) => {
    const rubro = rubros.find(r => r.id === tarea.rubro_id);
    const rubroNombre = rubro ? rubro.nombre : 'Sin Rubro';
    if (!acc[rubroNombre]) acc[rubroNombre] = [];
    acc[rubroNombre].push(tarea);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Button variant="ghost" onClick={() => navigate("/cronogramas")} className="mb-2">
            ← Volver a Cronogramas
          </Button>
          <h1 className="text-3xl font-bold text-slate-900">
            {cronograma.nombre}
          </h1>
        </div>
        <Button
          onClick={handleCreateTarea}
          className="bg-deflorencia hover:bg-[#ff9100]"
        >
          + Nueva Tarea
        </Button>
      </div>

      {/* Información del Cronograma */}
      <Card>
        <CardHeader>
          <CardTitle>📅 {cronograma.nombre}</CardTitle>
          <CardDescription className="text-base">
            {obra.nombre} - {obra.cliente}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <div className="text-2xl font-bold text-slate-900">{tareas.length}</div>
              <div className="text-sm text-slate-500">Total Tareas</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                {tareas.filter(t => t.estado === 'Completada').length}
              </div>
              <div className="text-sm text-slate-500">Completadas</div>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">
                {tareas.length ? Math.round((tareas.filter(t => t.estado === 'Completada').length / tareas.length) * 100) : 0}%
              </div>
              <div className="text-sm text-slate-500">Progreso</div>
            </div>
          </div>

          {/* Barra de progreso visual */}
          <div className="mt-4">
            <div className="flex justify-between text-sm text-slate-600 mb-2">
              <span>Progreso General</span>
              <span>{tareas.length ? Math.round((tareas.filter(t => t.estado === 'Completada').length / tareas.length) * 100) : 0}%</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-3">
              <div
                className="bg-deflorencia h-3 rounded-full transition-all duration-500"
                style={{
                  width: `${tareas.length ? (tareas.filter(t => t.estado === 'Completada').length / tareas.length) * 100 : 0}%`
                }}
              ></div>
            </div>
          </div>

          {/* Información adicional */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6 pt-4 border-t border-slate-100">
            <div>
              <Label className="text-slate-500">Inicio del Proyecto</Label>
              <p className="font-medium">{formatDate(cronograma.fecha_inicio)}</p>
            </div>
            <div>
              <Label className="text-slate-500">Fin Estimado</Label>
              <p className="font-medium">
                {cronograma.fecha_fin_estimada ?
                  formatDate(cronograma.fecha_fin_estimada) :
                  'Por definir'
                }
              </p>
            </div>
            {cronograma.director_obra && (
              <div className="md:col-span-2">
                <Label className="text-slate-500">Director de Obra</Label>
                <p className="font-medium">{cronograma.director_obra}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Pestañas y Contenido */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-4">
            <Button
              variant={activeTab === 'lista' ? 'default' : 'outline'}
              onClick={() => setActiveTab('lista')}
              size="sm"
            >
              📋 Lista por Rubro
            </Button>
            <Button
              variant={activeTab === 'gantt' ? 'default' : 'outline'}
              onClick={() => setActiveTab('gantt')}
              size="sm"
            >
              📊 Vista Gantt
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {activeTab === 'lista' ? (
            // Vista Lista por Rubro (la vista original)
            Object.keys(tareasPorRubro).length === 0 ? (
              <div className="py-12 text-center text-slate-600">
                No hay tareas creadas. Haga clic en “Nueva Tarea” para comenzar.
              </div>
            ) : (
              <div className="space-y-6">
                {Object.entries(tareasPorRubro).map(([rubroNombre, rubroTareas]) => (
                  <div key={rubroNombre}>
                    <div className="mb-3">
                      <h3 className="text-lg font-semibold text-[#FFA726]">{rubroNombre}</h3>
                      <p className="text-sm text-slate-500">
                        {rubroTareas.length} tareas • {rubroTareas.filter(t => t.estado === 'Completada').length} completadas
                      </p>
                    </div>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Tarea</TableHead>
                          <TableHead>Duración</TableHead>
                          <TableHead>Fecha Inicio</TableHead>
                          <TableHead>Fecha Fin</TableHead>
                          <TableHead>Estado</TableHead>
                          <TableHead className="text-right">Acciones</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {rubroTareas.map((tarea) => (
                          <TableRow key={tarea.id}>
                            <TableCell>
                              <div className="font-medium">{tarea.nombre}</div>
                              {tarea.notas && (
                                <div className="text-xs text-slate-500">{tarea.notas}</div>
                              )}
                            </TableCell>
                            <TableCell>{tarea.duracion_dias} días</TableCell>
                            <TableCell>{formatDate(tarea.fecha_inicio)}</TableCell>
                            <TableCell>{formatDate(tarea.fecha_fin)}</TableCell>
                            <TableCell>
                              <select
                                value={tarea.estado}
                                onChange={(e) => handleUpdateEstado(tarea.id, e.target.value)}
                                className={`px-2 py-1 rounded text-xs font-medium border-0 ${tarea.estado === 'Completada' ? 'bg-green-100 text-green-700' :
                                  tarea.estado === 'En Progreso' ? 'bg-blue-100 text-blue-700' :
                                    'bg-slate-100 text-slate-700'
                                  }`}
                              >
                                <option value="Pendiente">Pendiente</option>
                                <option value="En Progreso">En Progreso</option>
                                <option value="Completada">Completada</option>
                              </select>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditTarea(tarea)}
                              >
                                Editar
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteTarea(tarea.id)}
                                className="text-red-600 hover:text-red-700"
                              >
                                Eliminar
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ))}
              </div>
            )
          ) : (
            // Vista Gantt
            <VistaGantt tareas={tareas} rubros={rubros} cronograma={cronograma} obra={obra} />
          )}
        </CardContent>
      </Card>

      {/* Modal Crear/Editar Tarea */}
      {showTareaModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold mb-4">
              {editingTarea ? 'Editar Tarea' : 'Nueva Tarea'}
            </h3>
            <form onSubmit={handleSubmitTarea} className="space-y-4">
              <div>
                <Label htmlFor="nombre">Nombre de la Tarea *</Label>
                <Input
                  id="nombre"
                  value={tareaFormData.nombre}
                  onChange={(e) => setTareaFormData({ ...tareaFormData, nombre: e.target.value })}
                  placeholder="ej: Movimientos de tierra y calles"
                  required
                />
              </div>

              <div>
                <Label htmlFor="rubro_id">Rubro *</Label>
                <select
                  id="rubro_id"
                  value={tareaFormData.rubro_id}
                  onChange={(e) => setTareaFormData({ ...tareaFormData, rubro_id: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md"
                  required
                >
                  <option value="">Seleccionar rubro</option>
                  {rubros
                    .filter(rubro => obra?.rubros_ids?.includes(rubro.id))
                    .map(rubro => (
                      <option key={rubro.id} value={rubro.id}>
                        {rubro.nombre}
                      </option>
                    ))}
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  Solo se muestran los rubros asignados a la obra
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="duracion_dias">Duración (días) *</Label>
                  <Input
                    id="duracion_dias"
                    type="number"
                    min="1"
                    value={tareaFormData.duracion_dias}
                    onChange={(e) => setTareaFormData({ ...tareaFormData, duracion_dias: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="fecha_inicio">Fecha Inicio *</Label>
                  <Input
                    id="fecha_inicio"
                    type="date"
                    value={tareaFormData.fecha_inicio}
                    onChange={(e) => setTareaFormData({ ...tareaFormData, fecha_inicio: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="notas">Notas</Label>
                <textarea
                  id="notas"
                  value={tareaFormData.notas}
                  onChange={(e) => setTareaFormData({ ...tareaFormData, notas: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md"
                  rows={3}
                  placeholder="Notas adicionales de la tarea"
                />
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowTareaModal(false)}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="bg-deflorencia hover:bg-[#ff9100]"
                >
                  {editingTarea ? 'Actualizar' : 'Crear'} Tarea
                </Button>
              </div>

            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== GASTOS ====================
function Gastos() {
  const [gastos, setGastos] = useState([]);
  const [obras, setObras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingGasto, setEditingGasto] = useState(null);
  const [filtros, setFiltros] = useState({
    obra: "",
    proveedor: "",
    formaPago: "",
    fechaDesde: "",
    fechaHasta: "",
    incluye_iva: ""
  });

  useEffect(() => {
    loadData();
  }, []);

  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 25;

  useEffect(() => {
    setCurrentPage(1);
  }, [filtros]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [resGastos, resObras] = await Promise.all([
        gastosAPI.getAll(),
        obrasAPI.getAll()
      ]);
      setGastos(resGastos.data);
      setObras(resObras.data);
    } catch (error) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (gastoId) => {
    if (!window.confirm("¿Estás seguro de eliminar este gasto?")) return;

    try {
      await gastosAPI.delete(gastoId);
      toast.success("Gasto eliminado exitosamente");
      loadData();
    } catch (error) {
      toast.error("Error al eliminar gasto");
    }
  };

  const handleEdit = (gasto) => {
    setEditingGasto(gasto);
    setShowModal(true);
  };

  const handleCloseModal = (reload) => {
    setShowModal(false);
    setEditingGasto(null);
    if (reload) loadData();
  };

  const limpiarFiltros = () => {
    setFiltros({
      obra: "",
      proveedor: "",
      formaPago: "",
      fechaDesde: "",
      fechaHasta: "",
      incluye_iva: ""
    });
  };

  // Filtrar gastos
  const gastosFiltrados = gastos.filter(g => {
    // Filtro por obra
    if (filtros.obra && g.obra_id !== filtros.obra) return false;

    // Filtro por proveedor
    if (filtros.proveedor && !g.proveedor?.toLowerCase().includes(filtros.proveedor.toLowerCase())) return false;

    // Filtro por forma de pago
    if (filtros.formaPago && g.forma_pago !== filtros.formaPago) return false;

    // Filtro por IVA
    if (filtros.incluye_iva !== "") {
      const incluye = filtros.incluye_iva === "true";
      if (g.tiene_iva !== incluye) return false;
    }

    // Filtro por rango de fechas
    if (filtros.fechaDesde && g.fecha < filtros.fechaDesde) return false;
    if (filtros.fechaHasta && g.fecha > filtros.fechaHasta) return false;

    return true;
  });

  // Calcular total
  const totalGastos = gastosFiltrados.reduce((sum, g) => sum + (g.monto || 0), 0);

  // Obtener proveedores únicos para el filtro
  const proveedoresUnicos = [...new Set(gastos.map(g => g.proveedor).filter(Boolean))];

  // Obtener formas de pago únicas
  const formasPagoUnicas = [...new Set(gastos.map(g => g.forma_pago).filter(Boolean))];

  // Paginación
  const totalPages = Math.ceil(gastosFiltrados.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedGastos = gastosFiltrados.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-slate-900">Gestión de Gastos</h1>
        <Card>
          <CardContent className="py-12 text-center text-slate-600">
            Cargando...
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-slate-900">Gestión de Gastos</h1>
        <Button onClick={() => setShowModal(true)}>
          <Receipt className="w-4 h-4 mr-2" />
          Nuevo Gasto
        </Button>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center mb-4">
            <CardTitle>Filtros</CardTitle>
            <Button variant="outline" size="sm" onClick={limpiarFiltros}>
              Limpiar Filtros
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {/* Filtro por Obra */}
            <div>
              <Label className="text-xs">Obra</Label>
              <select
                value={filtros.obra}
                onChange={(e) => setFiltros({ ...filtros, obra: e.target.value })}
                className="w-full mt-1 px-2 py-1.5 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todas</option>
                {obras.map(obra => (
                  <option key={obra.id} value={obra.id}>{obra.nombre}</option>
                ))}
              </select>
            </div>

            {/* Filtro por Proveedor */}
            <div>
              <Label className="text-xs">Proveedor</Label>
              <Input
                type="text"
                placeholder="Buscar..."
                value={filtros.proveedor}
                onChange={(e) => setFiltros({ ...filtros, proveedor: e.target.value })}
                className="mt-1 h-8 text-sm"
              />
            </div>

            {/* Filtro por Forma de Pago */}
            <div>
              <Label className="text-xs">Forma de Pago</Label>
              <select
                value={filtros.formaPago}
                onChange={(e) => setFiltros({ ...filtros, formaPago: e.target.value })}
                className="w-full mt-1 px-2 py-1.5 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todas</option>
                {formasPagoUnicas.map(fp => (
                  <option key={fp} value={fp}>{fp}</option>
                ))}
              </select>
            </div>

            {/* Filtro por IVA */}
            <div>
              <Label className="text-xs">IVA</Label>
              <select
                value={filtros.incluye_iva}
                onChange={(e) => setFiltros({ ...filtros, incluye_iva: e.target.value })}
                className="w-full mt-1 px-2 py-1.5 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todos</option>
                <option value="true">Con IVA</option>
                <option value="false">Sin IVA</option>
              </select>
            </div>

            {/* Filtro Fecha Desde */}
            <div>
              <Label className="text-xs">Fecha Desde</Label>
              <Input
                type="date"
                value={filtros.fechaDesde}
                onChange={(e) => setFiltros({ ...filtros, fechaDesde: e.target.value })}
                className="mt-1 h-8 text-sm"
              />
            </div>

            {/* Filtro Fecha Hasta */}
            <div>
              <Label className="text-xs">Fecha Hasta</Label>
              <Input
                type="date"
                value={filtros.fechaHasta}
                onChange={(e) => setFiltros({ ...filtros, fechaHasta: e.target.value })}
                className="mt-1 h-8 text-sm"
              />
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Resumen */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50">
        <CardContent className="py-4">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm text-slate-600">Total de gastos filtrados</p>
              <p className="text-3xl font-bold text-slate-900">{formatCurrency(totalGastos)}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-600">Registros</p>
              <p className="text-2xl font-semibold text-blue-600">{gastosFiltrados.length}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabla de gastos */}
      <Card>
        <CardContent className="p-0">
          {gastosFiltrados.length === 0 ? (
            <div className="py-12 text-center text-slate-600">
              No hay gastos registrados
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Obra</TableHead>
                      <TableHead>Descripción</TableHead>
                      <TableHead>Proveedor</TableHead>
                      <TableHead className="text-right">Monto</TableHead>
                      <TableHead>IVA</TableHead>
                      <TableHead>Forma de Pago</TableHead>
                      <TableHead>Comprobante</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedGastos.map((gasto) => {
                      const obra = obras.find(o => o.id === gasto.obra_id);
                      return (
                        <TableRow key={gasto.id}>
                          <TableCell className="font-medium">{formatDate(gasto.fecha)}</TableCell>
                          <TableCell>{obra?.nombre || '-'}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span>{gasto.descripcion || '-'}</span>
                              {gasto.notas && gasto.notas.trim() && (
                                <span
                                  className="text-blue-600 cursor-help"
                                  title={gasto.notas}
                                >
                                  💬
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>{gasto.proveedor || '-'}</TableCell>
                          <TableCell className="text-right font-semibold">{formatCurrency(gasto.monto)}</TableCell>
                          <TableCell>
                            {gasto.tiene_iva ? (
                              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Con IVA</Badge>
                            ) : (
                              <Badge variant="outline" className="bg-slate-50 text-slate-600">Sin IVA</Badge>
                            )}
                          </TableCell>
                          <TableCell>{gasto.forma_pago}</TableCell>
                          <TableCell>
                            {gasto.comprobantes && gasto.comprobantes.length > 0 ? (
                              <div className="flex flex-col gap-1">
                                {gasto.comprobantes.map((archivo, idx) => (
                                  <a
                                    key={idx}
                                    href={gastosAPI.getComprobante(gasto.id, archivo)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 text-sm underline flex items-center gap-1"
                                  >
                                    <Download className="w-3 h-3" />
                                    {archivo.length > 20 ? archivo.substring(0, 20) + '...' : archivo}
                                  </a>
                                ))}
                              </div>
                            ) : (
                              <span className="text-slate-400 text-sm">Sin comprobante</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right space-x-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(gasto)}
                            >
                              Editar
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(gasto.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              Eliminar
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                totalItems={gastosFiltrados.length}
                itemsPerPage={ITEMS_PER_PAGE}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Modal */}
      {showModal && (
        <AgregarGastoModal
          gasto={editingGasto}
          obras={obras}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
}

function AgregarGastoModal({ gasto, obras, onClose }) {
  const [formData, setFormData] = useState({
    obra_id: gasto?.obra_id || "",
    fecha: gasto?.fecha || new Date().toISOString().split('T')[0],
    monto: gasto?.monto || "",
    tiene_iva: gasto?.tiene_iva || false,
    proveedor: gasto?.proveedor || "",
    descripcion: gasto?.descripcion || "",
    forma_pago: gasto?.forma_pago || "Efectivo",
    notas: gasto?.notas || ""
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Handlers para drag & drop
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      // Validar tipo de archivo (imagen o PDF)
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf'];
      if (validTypes.includes(file.type)) {
        setSelectedFile(file);
      } else {
        toast.error("Solo se permiten archivos de imagen (JPG, PNG, GIF) o PDF");
      }
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Validar campos requeridos
      if (!formData.obra_id) {
        toast.error("Debe seleccionar una obra");
        setSaving(false);
        return;
      }

      if (!formData.monto || parseFloat(formData.monto) <= 0) {
        toast.error("El monto debe ser mayor a 0");
        setSaving(false);
        return;
      }

      // Preparar datos
      const dataToSend = {
        ...formData,
        monto: parseFloat(formData.monto)
      };

      let gastoId;

      if (gasto) {
        // Actualizar gasto existente
        await gastosAPI.update(gasto.id, dataToSend);
        gastoId = gasto.id;
        toast.success("Gasto actualizado exitosamente");
      } else {
        // Crear nuevo gasto
        const res = await gastosAPI.create(dataToSend);
        gastoId = res.data.id;
        toast.success("Gasto registrado exitosamente");
      }

      // Si hay archivo, subirlo
      if (selectedFile) {
        try {
          await gastosAPI.uploadComprobante(gastoId, selectedFile);
          toast.success("Comprobante subido exitosamente");
        } catch (error) {
          toast.error("Error al subir comprobante");
        }
      }

      onClose(true);
    } catch (error) {
      toast.error(gasto ? "Error al actualizar gasto" : "Error al registrar gasto");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg max-w-2xl w-full my-8">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-2xl font-bold text-slate-900">
            {gasto ? "Editar Gasto" : "Nuevo Gasto"}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
          {/* Obra */}
          <div>
            <Label>Obra *</Label>
            <select
              required
              value={formData.obra_id}
              onChange={(e) => setFormData({ ...formData, obra_id: e.target.value })}
              className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Seleccione una obra</option>
              {obras.map(obra => (
                <option key={obra.id} value={obra.id}>{obra.nombre}</option>
              ))}
            </select>
          </div>

          {/* Fecha y Monto */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Fecha *</Label>
              <Input
                type="date"
                required
                value={formData.fecha}
                onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
              />
            </div>

            <div>
              <Label>Monto *</Label>
              <Input
                type="number"
                step="0.01"
                required
                value={formData.monto}
                onChange={(e) => setFormData({ ...formData, monto: e.target.value })}
                placeholder="0.00"
              />
            </div>
          </div>

          {/* Checkbox IVA */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="tiene_iva"
              checked={formData.tiene_iva}
              onChange={(e) => setFormData({ ...formData, tiene_iva: e.target.checked })}
              className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            />
            <Label htmlFor="tiene_iva" className="cursor-pointer">
              Este gasto incluye IVA
            </Label>
          </div>

          {/* Proveedor */}
          <div>
            <Label>Proveedor</Label>
            <Input
              value={formData.proveedor}
              onChange={(e) => setFormData({ ...formData, proveedor: e.target.value })}
              placeholder="Nombre del proveedor"
            />
          </div>

          {/* Descripción */}
          <div>
            <Label>Descripción</Label>
            <textarea
              value={formData.descripcion}
              onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
              placeholder="Descripción del gasto"
              rows="3"
              className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Forma de Pago */}
          <div>
            <Label>Forma de Pago</Label>
            <select
              value={formData.forma_pago}
              onChange={(e) => setFormData({ ...formData, forma_pago: e.target.value })}
              className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="Efectivo">Efectivo</option>
              <option value="Tarjeta">Tarjeta</option>
              <option value="Transferencia">Transferencia</option>
              <option value="Cheque">Cheque</option>
            </select>
          </div>

          {/* Comprobante con Drag & Drop */}
          <div>
            <Label>Comprobante (imagen o PDF)</Label>
            <div
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              className={`mt-1 border-2 border-dashed rounded-lg p-6 text-center transition-colors ${isDragging
                ? 'border-blue-500 bg-blue-50'
                : 'border-slate-300 bg-slate-50 hover:border-slate-400'
                }`}
            >
              {selectedFile ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-center gap-2 text-green-600">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="font-medium">Archivo seleccionado</span>
                  </div>
                  <p className="text-sm text-slate-700">{selectedFile.name}</p>
                  <p className="text-xs text-slate-500">
                    {(selectedFile.size / 1024).toFixed(1)} KB
                  </p>
                  <button
                    type="button"
                    onClick={() => setSelectedFile(null)}
                    className="text-xs text-red-600 hover:text-red-700 underline"
                  >
                    Quitar archivo
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <svg
                    className="w-10 h-10 mx-auto text-slate-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                  <div className="text-sm text-slate-600">
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <span className="text-blue-600 hover:text-blue-700 font-medium">Click para seleccionar</span>
                      <span> o arrastra un archivo aquí</span>
                    </label>
                    <input
                      id="file-upload"
                      type="file"
                      accept="image/*,.pdf"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </div>
                  <p className="text-xs text-slate-500">
                    JPG, PNG, GIF o PDF (máx. 10MB)
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Notas */}
          <div>
            <Label>Notas adicionales</Label>
            <textarea
              value={formData.notas}
              onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
              placeholder="Notas internas (opcional)"
              rows="2"
              className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Botones */}
          <div className="flex gap-3 justify-end border-t pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onClose(false)}
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Guardando..." : (gasto ? "Actualizar" : "Guardar")}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
/* eslint-enable react/no-unstable-nested-components */

// ==================== CERTIFICADOS ====================
function Certificados() {
  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-slate-900">Certificados de Avance</h1>
      <Card>
        <CardContent className="py-12 text-center text-slate-600">
          Módulo de certificados en construcción
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== CATÁLOGO ====================
function Catalogo() {
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("Todos");
  const [statusFilter, setStatusFilter] = useState("Todos");

  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 25;


  useEffect(() => {
    loadItems();
  }, []);


  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, categoryFilter, statusFilter]);

  useEffect(() => {
    filterItems();
  }, [items, searchTerm, categoryFilter, statusFilter]);

  const loadItems = async () => {
    try {
      const res = await catalogoItemsAPI.getAll();
      setItems(res.data);
    } catch (error) {
      toast.error("Error al cargar catálogo");
    } finally {
      setLoading(false);
    }
  };

  const filterItems = () => {
    let filtered = [...items];

    // Búsqueda por texto
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.codigo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.proveedor && item.proveedor.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Filtro por categoría
    if (categoryFilter !== "Todos") {
      filtered = filtered.filter(item => item.categoria === categoryFilter);
    }

    // Filtro por estado
    if (statusFilter !== "Todos") {
      const isActive = statusFilter === "Activo";
      filtered = filtered.filter(item => item.activo === isActive);
    }

    setFilteredItems(filtered);
  };

  const handleCreateItem = () => {
    setEditingItem(null);
    setShowModal(true);
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const handleDeleteItem = async (id, force = false) => {
    if (!force && !window.confirm("¿Está seguro de eliminar este item del catálogo?")) return;

    try {
      await catalogoItemsAPI.delete(id, force);
      const message = force ? "Item del catálogo y sus usos eliminados exitosamente" : "Item eliminado exitosamente";
      toast.success(message);
      loadItems();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Error al eliminar item";

      // Si el error indica que está en uso, ofrecer eliminación en cascada
      if (errorMsg.includes("force=true") && !force) {
        const confirmMsg = `${errorMsg}\n\n¿Desea eliminar el item del catálogo junto con todos sus usos en presupuestos?\n\nADVERTENCIA: Esta acción afectará los totales de los presupuestos y no se puede deshacer.`;

        if (window.confirm(confirmMsg)) {
          // Llamar recursivamente con force=true
          await handleDeleteItem(id, true);
        }
      } else {
        toast.error(errorMsg);
      }
    }
  };

  const handleModalClose = (reload) => {
    setShowModal(false);
    setEditingItem(null);
    if (reload) loadItems();
  };

  // Paginación
  const totalPages = Math.ceil(filteredItems.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedItems = filteredItems.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-slate-900">Catálogo de Items</h1>
        <Button
          onClick={handleCreateItem}
          className="bg-deflorencia hover:bg-[#ff9100]"
        >
          + Nuevo Item
        </Button>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <Input
                placeholder="Buscar por nombre, código o proveedor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-md"
              >
                <option>Todos</option>
                <option>Material</option>
                <option>ManoObra</option>
                <option>Equipo</option>
                <option>Indirecto</option>
                <option>Producto</option>
              </select>
            </div>
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-md"
              >
                <option>Todos</option>
                <option>Activo</option>
                <option>Inactivo</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabla */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-12 text-center text-slate-600">Cargando items...</div>
          ) : filteredItems.length === 0 ? (
            <div className="py-12 text-center text-slate-600">No se encontraron items</div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Unidad</TableHead>
                    <TableHead>Categoría</TableHead>
                    <TableHead>Proveedor</TableHead>
                    <TableHead className="text-right">Precio Ref.</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-mono text-sm font-medium">{item.codigo}</TableCell>
                      <TableCell className="font-medium">{item.nombre}</TableCell>
                      <TableCell>
                        <Badge
                          variant={item.tipo === "Compuesto" ? "default" : "outline"}
                          className={item.tipo === "Compuesto" ? "bg-purple-600" : ""}
                        >
                          {item.tipo || "Simple"}
                        </Badge>
                      </TableCell>
                      <TableCell>{item.unidad}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{item.categoria}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">{item.proveedor || '-'}</TableCell>
                      <TableCell className="text-right">
                        {item.tipo === "Compuesto" && item.precio_calculado ? (
                          <div>
                            <div className="font-medium">{formatCurrency(item.precio_calculado)}</div>
                            <div className="text-xs text-slate-500">calculado</div>
                          </div>
                        ) : (
                          formatCurrency(item.precio_referencia)
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={item.activo ? "default" : "secondary"}>
                          {item.activo ? "Activo" : "Inactivo"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditItem(item)}
                        >
                          Editar
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteItem(item.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          Eliminar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                totalItems={filteredItems.length}
                itemsPerPage={ITEMS_PER_PAGE}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Modal Crear/Editar */}
      {showModal && (
        <CatalogoItemModal
          item={editingItem}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
}

// Modal para Crear/Editar Item
function CatalogoItemModal({ item, onClose }) {
  const [formData, setFormData] = useState({
    nombre: item?.nombre || "",
    unidad: item?.unidad || "",
    categoria: item?.categoria || "Material",
    descripcion: item?.descripcion || "",
    proveedor: item?.proveedor || "",
    precio_referencia: item?.precio_referencia || "",
    activo: item?.activo ?? true,
    tiene_iva: item?.tiene_iva ?? true,
    tipo: item?.tipo || "Simple",
    componentes: item?.componentes || []
  });
  const [saving, setSaving] = useState(false);
  const [itemsDisponibles, setItemsDisponibles] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loadingCategorias, setLoadingCategorias] = useState(true);
  const [showComponenteModal, setShowComponenteModal] = useState(false);
  const [componenteActual, setComponenteActual] = useState({ item_id: "", cantidad: "" });
  const [loadingItems, setLoadingItems] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    loadCategorias();
  }, []);

  useEffect(() => {
    if (formData.tipo === "Compuesto") {
      loadItemsDisponibles();
    }
  }, [formData.tipo]);

  const loadCategorias = async () => {
    try {
      const res = await categoriasAPI.getActivas();
      setCategorias(res.data);
    } catch (error) {
      toast.error("Error al cargar categorías");
    } finally {
      setLoadingCategorias(false);
    }
  };

  // También cargar items cuando se abre el modal de componentes
  useEffect(() => {
    if (showComponenteModal && formData.tipo === "Compuesto" && itemsDisponibles.length === 0) {
      loadItemsDisponibles();
    }
  }, [showComponenteModal]);

  const loadItemsDisponibles = async () => {
    setLoadingItems(true);
    try {
      const res = await catalogoItemsAPI.getAll();
      console.log("Total items obtenidos:", res.data.length);

      // Solo items simples y activos (items sin tipo se consideran simples por defecto)
      const simples = res.data.filter(i => {
        const esSimple = !i.tipo || i.tipo === "Simple";
        const esActivo = i.activo;
        return esSimple && esActivo;
      });

      console.log("Items simples activos filtrados:", simples.length);
      setItemsDisponibles(simples);

      if (simples.length === 0) {
        toast.warning("No hay items simples disponibles en el catálogo");
      }
    } catch (error) {
      toast.error("Error al cargar items del catálogo");
      console.error("Error cargando items:", error);
    } finally {
      setLoadingItems(false);
    }
  };

  const handleAgregarComponente = async () => {
    const modo = componenteActual.modo || 'seleccionar';

    // Validar cantidad siempre
    if (!componenteActual.cantidad) {
      toast.error("Ingrese la cantidad por unidad");
      return;
    }

    if (modo === 'seleccionar') {
      // MODO 1: Seleccionar item existente
      if (!componenteActual.item_id) {
        toast.error("Seleccione un item del catálogo");
        return;
      }

      const itemSeleccionado = itemsDisponibles.find(i => i.id === componenteActual.item_id);
      if (!itemSeleccionado) return;

      const nuevoComponente = {
        item_id: itemSeleccionado.id,
        cantidad: parseFloat(componenteActual.cantidad),
        codigo: itemSeleccionado.codigo,
        nombre: itemSeleccionado.nombre,
        unidad: itemSeleccionado.unidad,
        precio_referencia: itemSeleccionado.precio_referencia
      };

      setFormData({
        ...formData,
        componentes: [...formData.componentes, nuevoComponente]
      });

      setComponenteActual({ item_id: "", cantidad: "" });
      setSearchTerm("");
      setShowDropdown(false);
      setShowComponenteModal(false);

    } else {
      // MODO 2: Crear nuevo item simple
      if (!componenteActual.nuevo_nombre || !componenteActual.nuevo_unidad || !componenteActual.nuevo_precio) {
        toast.error("Complete todos los campos requeridos (nombre, unidad, precio)");
        return;
      }

      try {
        // Crear el item en el catálogo
        const nuevoItemData = {
          nombre: componenteActual.nuevo_nombre,
          unidad: componenteActual.nuevo_unidad,
          categoria: componenteActual.nuevo_categoria || 'Material',
          precio_referencia: parseFloat(componenteActual.nuevo_precio),
          proveedor: componenteActual.nuevo_proveedor || null,
          descripcion: componenteActual.nuevo_descripcion || null,
          activo: true,
          tipo: "Simple",
          componentes: []
        };

        const response = await catalogoItemsAPI.create(nuevoItemData);
        const itemCreado = response.data;

        // Agregar a items disponibles
        setItemsDisponibles([...itemsDisponibles, itemCreado]);

        // Agregar como componente
        const nuevoComponente = {
          item_id: itemCreado.id,
          cantidad: parseFloat(componenteActual.cantidad),
          codigo: itemCreado.codigo,
          nombre: itemCreado.nombre,
          unidad: itemCreado.unidad,
          precio_referencia: itemCreado.precio_referencia
        };

        setFormData({
          ...formData,
          componentes: [...formData.componentes, nuevoComponente]
        });

        toast.success(`Item "${itemCreado.nombre}" creado y agregado exitosamente`);
        setComponenteActual({ item_id: "", cantidad: "" });
        setSearchTerm("");
        setShowDropdown(false);
        setShowComponenteModal(false);

      } catch (error) {
        toast.error("Error al crear el nuevo item");
        console.error(error);
      }
    }
  };

  const handleEliminarComponente = (index) => {
    const nuevosComponentes = formData.componentes.filter((_, i) => i !== index);
    setFormData({ ...formData, componentes: nuevosComponentes });
  };

  const calcularPrecioEstimado = () => {
    if (formData.tipo !== "Compuesto") return null;
    return formData.componentes.reduce((total, comp) => {
      return total + (comp.cantidad * (comp.precio_referencia || 0));
    }, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.tipo === "Compuesto" && formData.componentes.length === 0) {
      toast.error("Los items compuestos deben tener al menos un componente");
      return;
    }

    setSaving(true);

    try {
      // Limpiar datos antes de enviar
      const dataToSend = {
        ...formData,
        precio_referencia: formData.precio_referencia === "" || formData.precio_referencia === null
          ? null
          : parseFloat(formData.precio_referencia),
        proveedor: formData.proveedor || null,
        descripcion: formData.descripcion || null
      };

      console.log("Datos a enviar:", dataToSend);

      if (item) {
        await catalogoItemsAPI.update(item.id, dataToSend);
        toast.success("Item actualizado exitosamente");
      } else {
        await catalogoItemsAPI.create(dataToSend);
        toast.success("Item creado exitosamente");
      }
      onClose(true);
    } catch (error) {
      console.error("Error completo:", error);
      console.error("Datos enviados:", formData);

      let errorMsg = item ? "Error al actualizar item" : "Error al crear item";

      if (error.response?.data?.detail) {
        // Si el backend envía detalles del error
        if (Array.isArray(error.response.data.detail)) {
          const errores = error.response.data.detail.map(e => `${e.loc.join('.')}: ${e.msg}`).join(', ');
          errorMsg = `${errorMsg}: ${errores}`;
        } else {
          errorMsg = `${errorMsg}: ${error.response.data.detail}`;
        }
      }

      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-2xl font-bold text-slate-900">
            {item ? "Editar Item" : "Nuevo Item"}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Selector de Tipo */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <Label className="text-base font-semibold mb-2 block">Tipo de Item *</Label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="tipo"
                  value="Simple"
                  checked={formData.tipo === "Simple"}
                  onChange={(e) => setFormData({ ...formData, tipo: e.target.value, componentes: [] })}
                  className="w-4 h-4"
                />
                <span className="text-sm font-medium">Simple</span>
                <span className="text-xs text-slate-500">(Item básico del catálogo)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="tipo"
                  value="Compuesto"
                  checked={formData.tipo === "Compuesto"}
                  onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                  className="w-4 h-4"
                />
                <span className="text-sm font-medium">Compuesto</span>
                <span className="text-xs text-slate-500">(Conjunto de items)</span>
              </label>
            </div>
          </div>

          {/* Información Básica */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>Nombre *</Label>
              <Input
                required
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                placeholder="Ej: Módulo Alero H21"
              />
            </div>

            <div>
              <Label>Unidad *</Label>
              <select
                required
                value={formData.unidad}
                onChange={(e) => setFormData({ ...formData, unidad: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md"
              >
                <option value="">Seleccione una unidad</option>
                {Object.entries(
                  UNIDADES_MEDIDA.reduce((acc, u) => {
                    if (!acc[u.categoria]) acc[u.categoria] = [];
                    acc[u.categoria].push(u);
                    return acc;
                  }, {})
                ).map(([categoria, unidades]) => (
                  <optgroup key={categoria} label={categoria}>
                    {unidades.map((u) => (
                      <option key={u.value} value={u.value}>
                        {u.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            <div>
              <Label>Categoría *</Label>
              {loadingCategorias ? (
                <div className="text-sm text-slate-500 py-2">Cargando categorías...</div>
              ) : (
                <select
                  required
                  value={formData.categoria}
                  onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md"
                >
                  <option value="">Seleccione una categoría</option>
                  {categorias.map((cat) => (
                    <option key={cat.id} value={cat.nombre}>
                      {cat.nombre} {cat.descripcion ? `- ${cat.descripcion}` : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {formData.tipo === "Simple" && (
              <>
                <div>
                  <Label>Proveedor</Label>
                  <Input
                    value={formData.proveedor}
                    onChange={(e) => setFormData({ ...formData, proveedor: e.target.value })}
                    placeholder="Opcional"
                  />
                </div>

                <div>
                  <Label>Precio Referencia</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.precio_referencia}
                    onChange={(e) => setFormData({ ...formData, precio_referencia: parseFloat(e.target.value) || "" })}
                    placeholder="0.00"
                  />
                </div>
              </>
            )}

            <div className="md:col-span-2">
              <Label>Descripción</Label>
              <textarea
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md"
                rows="3"
                placeholder="Descripción del item"
              />
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="activo"
                checked={formData.activo}
                onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                className="rounded border-slate-300"
              />
              <Label htmlFor="activo">Item activo</Label>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="tiene_iva"
                checked={formData.tiene_iva}
                onChange={(e) => setFormData({ ...formData, tiene_iva: e.target.checked })}
                className="rounded border-slate-300"
              />
              <Label htmlFor="tiene_iva">Tiene IVA</Label>
            </div>
          </div>

          {/* Sección de Componentes (solo para items compuestos) */}
          {formData.tipo === "Compuesto" && (
            <div className="border-t border-slate-200 pt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900">Componentes (Receta)</h3>
                <Button
                  type="button"
                  onClick={() => setShowComponenteModal(true)}
                  className="bg-green-600 hover:bg-green-700"
                  size="sm"
                >
                  + Agregar Componente
                </Button>
              </div>

              {formData.componentes.length === 0 ? (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-8 text-center">
                  <Package className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                  <p className="text-slate-600">No hay componentes agregados</p>
                  <p className="text-sm text-slate-500 mt-1">Agregue los items que componen este elemento</p>
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Código</TableHead>
                        <TableHead>Item</TableHead>
                        <TableHead>Cantidad x unidad</TableHead>
                        <TableHead>Precio Unit.</TableHead>
                        <TableHead>Subtotal</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {formData.componentes.map((comp, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-mono text-sm">{comp.codigo}</TableCell>
                          <TableCell>
                            <div>{comp.nombre}</div>
                            <div className="text-xs text-slate-500">{comp.unidad}</div>
                          </TableCell>
                          <TableCell>{comp.cantidad} {comp.unidad}</TableCell>
                          <TableCell>{formatCurrency(comp.precio_referencia || 0)}</TableCell>
                          <TableCell className="font-semibold">{formatCurrency((comp.cantidad * (comp.precio_referencia || 0)))}</TableCell>
                          <TableCell>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEliminarComponente(index)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-700">Precio Estimado por {formData.unidad || "unidad"}</p>
                        <p className="text-xs text-slate-500 mt-1">Calculado en base a los componentes</p>
                      </div>
                      <div className="text-2xl font-bold text-green-700">
                        {formatCurrency(calcularPrecioEstimado())}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Modal para agregar componente */}
          {showComponenteModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
              <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b border-slate-200">
                  <h3 className="text-xl font-semibold">Agregar Componente</h3>
                </div>

                <div className="p-6">
                  {/* Tabs para Seleccionar o Crear */}
                  <div className="flex gap-2 mb-6 border-b border-slate-200">
                    <button
                      type="button"
                      onClick={() => setComponenteActual({ ...componenteActual, modo: 'seleccionar' })}
                      className={`px-4 py-2 font-medium transition-colors border-b-2 ${(componenteActual.modo || 'seleccionar') === 'seleccionar'
                        ? 'border-deflorencia text-deflorencia'
                        : 'border-transparent text-slate-600 hover:text-slate-900'
                        }`}
                    >
                      Seleccionar Item Existente
                    </button>
                    <button
                      type="button"
                      onClick={() => setComponenteActual({ ...componenteActual, modo: 'crear' })}
                      className={`px-4 py-2 font-medium transition-colors border-b-2 ${componenteActual.modo === 'crear'
                        ? 'border-deflorencia text-deflorencia'
                        : 'border-transparent text-slate-600 hover:text-slate-900'
                        }`}
                    >
                      + Crear Nuevo Item
                    </button>
                  </div>

                  {/* TAB 1: Seleccionar Item Existente */}
                  {(componenteActual.modo || 'seleccionar') === 'seleccionar' && (
                    <div className="space-y-4">
                      <div>
                        <Label>Item del Catálogo *</Label>
                        <div className="relative">
                          <Input
                            value={searchTerm}
                            onChange={(e) => {
                              setSearchTerm(e.target.value);
                              setShowDropdown(true);
                            }}
                            onFocus={() => setShowDropdown(true)}
                            placeholder={loadingItems ? "Cargando..." : "Buscar item por código o nombre..."}
                            disabled={loadingItems}
                            className="w-full"
                          />

                          {showDropdown && !loadingItems && (
                            <div className="absolute z-10 w-full mt-1 bg-white border border-slate-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                              {itemsDisponibles
                                .filter(it => {
                                  const search = searchTerm.toLowerCase();
                                  return it.codigo.toLowerCase().includes(search) ||
                                    it.nombre.toLowerCase().includes(search);
                                })
                                .slice(0, 50)
                                .map((it) => (
                                  <button
                                    key={it.id}
                                    type="button"
                                    onClick={() => {
                                      setComponenteActual({
                                        ...componenteActual,
                                        item_id: it.id,
                                        item_unidad: it.unidad
                                      });
                                      setSearchTerm(`${it.codigo} - ${it.nombre}`);
                                      setShowDropdown(false);
                                    }}
                                    className="w-full text-left px-3 py-2 hover:bg-slate-100 border-b border-slate-100 last:border-b-0"
                                  >
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <span className="font-mono text-sm font-medium text-slate-700">{it.codigo}</span>
                                        <span className="text-sm text-slate-900 ml-2">{it.nombre}</span>
                                      </div>
                                      <div className="text-xs text-slate-500">
                                        {it.unidad} • {formatCurrency(it.precio_referencia || 0)}
                                      </div>
                                    </div>
                                  </button>
                                ))}

                              {itemsDisponibles.filter(it => {
                                const search = searchTerm.toLowerCase();
                                return it.codigo.toLowerCase().includes(search) ||
                                  it.nombre.toLowerCase().includes(search);
                              }).length === 0 && (
                                  <div className="px-3 py-4 text-center text-sm text-slate-500">
                                    No se encontraron items
                                  </div>
                                )}
                            </div>
                          )}
                        </div>

                        {!loadingItems && itemsDisponibles.length === 0 && (
                          <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <p className="text-sm text-amber-800">
                              <strong>⚠️ No hay items simples disponibles.</strong>
                            </p>
                            <p className="text-xs text-amber-700 mt-1">
                              Usa la pestaña “+ Crear Nuevo Item” para agregar componentes.
                            </p>
                          </div>
                        )}
                        {loadingItems && (
                          <p className="text-xs text-blue-600 mt-1">Cargando items del catálogo...</p>
                        )}
                      </div>

                      <div>
                        <Label>Cantidad por unidad *</Label>
                        <div className="flex gap-2">
                          <Input
                            type="number"
                            step="0.01"
                            value={componenteActual.cantidad || ''}
                            onChange={(e) => setComponenteActual({ ...componenteActual, cantidad: e.target.value })}
                            placeholder="Ej: 15"
                            className="flex-1"
                          />
                          {componenteActual.unidad && (
                            <div className="px-4 py-2 bg-slate-100 border border-slate-300 rounded-md text-sm font-medium text-slate-700">
                              {componenteActual.unidad}
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          Cantidad de este item necesaria por cada unidad del item compuesto
                        </p>
                      </div>
                    </div>
                  )}

                  {/* TAB 2: Crear Nuevo Item */}
                  {componenteActual.modo === 'crear' && (
                    <div className="space-y-4">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                        <p className="text-sm text-blue-800">
                          <strong>Nota:</strong> El item se creará como “Simple” y se agregará automáticamente al catálogo y como componente de este item compuesto.
                        </p>
                      </div>

                      <div>
                        <Label>Nombre *</Label>
                        <Input
                          value={componenteActual.nuevo_nombre || ''}
                          onChange={(e) => setComponenteActual({ ...componenteActual, nuevo_nombre: e.target.value })}
                          placeholder="Ej: Tornillo M8"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Unidad *</Label>
                          <select
                            value={componenteActual.nuevo_unidad || ''}
                            onChange={(e) => setComponenteActual({ ...componenteActual, nuevo_unidad: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-md"
                          >
                            <option value="">Seleccione unidad</option>
                            {Object.entries(
                              UNIDADES_MEDIDA.reduce((acc, u) => {
                                if (!acc[u.categoria]) acc[u.categoria] = [];
                                acc[u.categoria].push(u);
                                return acc;
                              }, {})
                            ).map(([categoria, unidades]) => (
                              <optgroup key={categoria} label={categoria}>
                                {unidades.map((u) => (
                                  <option key={u.value} value={u.value}>
                                    {u.label}
                                  </option>
                                ))}
                              </optgroup>
                            ))}
                          </select>
                        </div>

                        <div>
                          <Label>Categoría *</Label>
                          <select
                            value={componenteActual.nuevo_categoria || 'Material'}
                            onChange={(e) => setComponenteActual({ ...componenteActual, nuevo_categoria: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-md"
                          >
                            <option value="Material">Material - Insumos básicos</option>
                            <option value="ManoObra">Mano de Obra - Personal</option>
                            <option value="Equipo">Equipo - Maquinaria</option>
                            <option value="Indirecto">Indirecto - Costos generales</option>
                            <option value="Producto">Producto - Elementos constructivos</option>
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Precio Referencia *</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={componenteActual.nuevo_precio || ''}
                            onChange={(e) => setComponenteActual({ ...componenteActual, nuevo_precio: e.target.value })}
                            placeholder="0.00"
                          />
                        </div>

                        <div>
                          <Label>Proveedor</Label>
                          <Input
                            value={componenteActual.nuevo_proveedor || ''}
                            onChange={(e) => setComponenteActual({ ...componenteActual, nuevo_proveedor: e.target.value })}
                            placeholder="Opcional"
                          />
                        </div>
                      </div>

                      <div>
                        <Label>Descripción</Label>
                        <textarea
                          value={componenteActual.nuevo_descripcion || ''}
                          onChange={(e) => setComponenteActual({ ...componenteActual, nuevo_descripcion: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-md"
                          rows="2"
                          placeholder="Descripción opcional"
                        />
                      </div>

                      <div className="border-t border-slate-200 pt-4">
                        <Label>Cantidad por unidad del item compuesto *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={componenteActual.cantidad || ''}
                          onChange={(e) => setComponenteActual({ ...componenteActual, cantidad: e.target.value })}
                          placeholder="Ej: 15"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Cantidad de este nuevo item necesaria por cada unidad del item compuesto
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex gap-2 p-6 border-t border-slate-200">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowComponenteModal(false);
                      setComponenteActual({ item_id: "", cantidad: "" });
                      setSearchTerm("");
                      setShowDropdown(false);
                    }}
                    className="flex-1"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="button"
                    onClick={handleAgregarComponente}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    {componenteActual.modo === 'crear' ? 'Crear y Agregar' : 'Agregar'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Botones de acción */}
          <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
            <Button
              type="button"
              variant="outline"
              onClick={() => onClose(false)}
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="bg-deflorencia hover:bg-[#ff9100]"
              disabled={saving}
            >
              {saving ? "Guardando..." : (item ? "Actualizar" : "Crear")}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ==================== CONFIGURACIÓN (RUBROS) ====================
function Configuracion() {
  const [activeTab, setActiveTab] = useState("categorias"); // categorias o rubros
  const [rubrosPersonalizados, setRubrosPersonalizados] = useState([]);
  const [rubrosSugeridos, setRubrosSugeridos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showCategoriaModal, setShowCategoriaModal] = useState(false);
  const [editingRubro, setEditingRubro] = useState(null);
  const [editingCategoria, setEditingCategoria] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    await Promise.all([loadRubros(), loadCategorias()]);
  };

  const loadRubros = async () => {
    try {
      const [resSugeridos, resPersonalizados] = await Promise.all([
        rubrosAPI.getSugeridos(),
        rubrosAPI.getAll()
      ]);
      setRubrosSugeridos(resSugeridos.data.rubros || []);
      setRubrosPersonalizados(resPersonalizados.data);
    } catch (error) {
      toast.error("Error al cargar rubros");
    } finally {
      setLoading(false);
    }
  };

  const loadCategorias = async () => {
    try {
      const res = await categoriasAPI.getAll();
      setCategorias(res.data);
    } catch (error) {
      toast.error("Error al cargar categorías");
    }
  };

  const handleCreateRubro = () => {
    setEditingRubro(null);
    setShowModal(true);
  };

  const handleDeleteRubro = async (id, force = false) => {
    if (!force && !window.confirm("¿Está seguro de eliminar este rubro personalizado?")) return;

    try {
      await rubrosAPI.delete(id, force);
      const message = force ? "Rubro e items eliminados exitosamente" : "Rubro eliminado exitosamente";
      toast.success(message);
      loadRubros();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Error al eliminar rubro";

      // Si el error indica que hay items, ofrecer eliminación en cascada
      if (errorMsg.includes("force=true") && !force) {
        const confirmMsg = `${errorMsg}\n\n¿Desea eliminar el rubro junto con todos sus items en cascada?\n\nADVERTENCIA: Esta acción afectará los totales de los presupuestos y no se puede deshacer.`;

        if (window.confirm(confirmMsg)) {
          // Llamar recursivamente con force=true
          await handleDeleteRubro(id, true);
        }
      } else {
        toast.error(errorMsg);
      }
    }
  };

  const handleModalClose = (reload) => {
    setShowModal(false);
    setEditingRubro(null);
    if (reload) loadRubros();
  };

  const handleCreateCategoria = () => {
    setEditingCategoria(null);
    setShowCategoriaModal(true);
  };

  const handleEditCategoria = (categoria) => {
    setEditingCategoria(categoria);
    setShowCategoriaModal(true);
  };

  const handleDeleteCategoria = async (id) => {
    if (!window.confirm("¿Está seguro de eliminar esta categoría?")) return;

    try {
      await categoriasAPI.delete(id);
      toast.success("Categoría eliminada exitosamente");
      loadCategorias();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar categoría");
    }
  };

  const handleToggleCategoria = async (categoria) => {
    try {
      await categoriasAPI.update(categoria.id, { ...categoria, activa: !categoria.activa });
      toast.success(`Categoría ${!categoria.activa ? 'activada' : 'desactivada'}`);
      loadCategorias();
    } catch (error) {
      toast.error("Error al actualizar categoría");
    }
  };

  const handleCategoriaModalClose = (reload) => {
    setShowCategoriaModal(false);
    setEditingCategoria(null);
    if (reload) loadCategorias();
  };

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-slate-900">⚙️ Configuración</h1>

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <div className="flex gap-4">
          <button
            onClick={() => setActiveTab("categorias")}
            className={`px-4 py-2 font-medium transition-colors border-b-2 ${activeTab === "categorias"
              ? "border-deflorencia text-deflorencia"
              : "border-transparent text-slate-600 hover:text-slate-900"
              }`}
          >
            🏷️ Categorías
          </button>
          <button
            onClick={() => setActiveTab("rubros")}
            className={`px-4 py-2 font-medium transition-colors border-b-2 ${activeTab === "rubros"
              ? "border-deflorencia text-deflorencia"
              : "border-transparent text-slate-600 hover:text-slate-900"
              }`}
          >
            📋 Rubros
          </button>
        </div>
      </div>

      {loading ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-600">
            Cargando...
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Tab Categorías */}
          {activeTab === "categorias" && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Categorías de Items</CardTitle>
                    <CardDescription>
                      Gestiona las categorías para organizar los items de tu catálogo
                    </CardDescription>
                  </div>
                  <Button
                    onClick={handleCreateCategoria}
                    className="bg-deflorencia hover:bg-[#ff9100]"
                  >
                    + Nueva Categoría
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {categorias.length === 0 ? (
                  <div className="py-8 text-center text-slate-600">
                    No hay categorías creadas.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {categorias.map((categoria) => (
                      <div
                        key={categoria.id}
                        className="border border-slate-200 rounded-lg p-4 bg-white hover:bg-slate-50 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 flex-1">
                            <div
                              className="w-8 h-8 rounded-full border-2"
                              style={{ backgroundColor: categoria.color, borderColor: categoria.color }}
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h3 className="font-semibold text-slate-900">{categoria.nombre}</h3>
                                {categoria.es_sistema && (
                                  <Badge variant="outline" className="text-xs">Sistema</Badge>
                                )}
                                {!categoria.activa && (
                                  <Badge variant="outline" className="text-xs bg-slate-100">Inactiva</Badge>
                                )}
                              </div>
                              {categoria.descripcion && (
                                <p className="text-sm text-slate-600 mt-1">{categoria.descripcion}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleCategoria(categoria)}
                              className={categoria.activa ? "text-amber-600" : "text-green-600"}
                            >
                              {categoria.activa ? "Desactivar" : "Activar"}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditCategoria(categoria)}
                              className="text-blue-600"
                            >
                              Editar
                            </Button>
                            {!categoria.es_sistema && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteCategoria(categoria.id)}
                                className="text-red-600"
                              >
                                Eliminar
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Tab Rubros */}
          {activeTab === "rubros" && (
            <>
              {/* Rubros Sugeridos */}
              <Card>
                <CardHeader>
                  <CardTitle>Rubros Sugeridos del Sistema</CardTitle>
                  <CardDescription>
                    Estos son los rubros predefinidos que puede usar en sus proyectos
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {rubrosSugeridos.map((rubro, index) => (
                      <div
                        key={index}
                        className="border border-slate-200 rounded-lg p-3 bg-slate-50"
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-slate-900">{rubro}</span>
                          <Badge variant="outline" className="text-xs">Sugerido</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Rubros Personalizados */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Rubros Personalizados</CardTitle>
                      <CardDescription>
                        Cree rubros adicionales específicos para sus necesidades
                      </CardDescription>
                    </div>
                    <Button
                      onClick={handleCreateRubro}
                      className="bg-deflorencia hover:bg-[#ff9100]"
                    >
                      + Nuevo Rubro
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {rubrosPersonalizados.length === 0 ? (
                    <div className="py-8 text-center text-slate-600">
                      No hay rubros personalizados. Cree uno para comenzar.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {rubrosPersonalizados.map((rubro) => (
                        <div
                          key={rubro.id}
                          className="border border-slate-200 rounded-lg p-4 bg-white hover:bg-slate-50 transition-colors"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-semibold text-slate-900">{rubro.nombre}</h3>
                                {rubro.es_predefinido && (
                                  <Badge variant="outline" className="text-xs">Predefinido</Badge>
                                )}
                              </div>
                              {rubro.descripcion && (
                                <p className="text-sm text-slate-600">{rubro.descripcion}</p>
                              )}
                              <p className="text-xs text-slate-400 mt-1">
                                Creado: {formatDate(rubro.fecha_creacion)}
                              </p>
                            </div>
                            {!rubro.es_predefinido && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteRubro(rubro.id)}
                                className="text-red-600 hover:text-red-700"
                              >
                                Eliminar
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </>
      )}

      {/* Modal Crear Rubro */}
      {showModal && (
        <RubroModal
          rubro={editingRubro}
          onClose={handleModalClose}
        />
      )}

      {/* Modal Crear/Editar Categoría */}
      {showCategoriaModal && (
        <CategoriaModal
          categoria={editingCategoria}
          onClose={handleCategoriaModalClose}
        />
      )}
    </div>
  );
}

// Modal para Crear Rubro Personalizado
function RubroModal({ rubro, onClose }) {
  const [formData, setFormData] = useState({
    nombre: rubro?.nombre || "",
    descripcion: rubro?.descripcion || "",
    es_predefinido: rubro?.es_predefinido || false
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await rubrosAPI.create(formData);
      toast.success("Rubro creado exitosamente");
      onClose(true);
    } catch (error) {
      toast.error("Error al crear rubro");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-lg w-full">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-2xl font-bold text-slate-900">Nuevo Rubro Personalizado</h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <Label>Nombre del Rubro *</Label>
            <Input
              required
              value={formData.nombre}
              onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              placeholder="Ej: Cielorrasos especiales"
            />
          </div>

          <div>
            <Label>Descripción</Label>
            <textarea
              value={formData.descripcion}
              onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded-md"
              rows="3"
              placeholder="Descripción opcional del rubro"
            />
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
            <Button
              type="button"
              variant="outline"
              onClick={() => onClose(false)}
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="bg-deflorencia hover:bg-[#ff9100]"
              disabled={saving}
            >
              {saving ? "Guardando..." : "Crear Rubro"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ==================== MANUAL DE USUARIO ====================
// Modal para Crear/Editar Categoría
function CategoriaModal({ categoria, onClose }) {
  const [formData, setFormData] = useState({
    nombre: categoria?.nombre || "",
    color: categoria?.color || "#3B82F6",
    descripcion: categoria?.descripcion || "",
    activa: categoria?.activa ?? true
  });
  const [saving, setSaving] = useState(false);

  const coloresComunes = [
    { nombre: "Azul", valor: "#3B82F6" },
    { nombre: "Morado", valor: "#8B5CF6" },
    { nombre: "Naranja", valor: "#F59E0B" },
    { nombre: "Gris", valor: "#6B7280" },
    { nombre: "Verde", valor: "#10B981" },
    { nombre: "Rojo", valor: "#EF4444" },
    { nombre: "Amarillo", valor: "#EAB308" },
    { nombre: "Rosa", valor: "#EC4899" },
    { nombre: "Cyan", valor: "#06B6D4" },
    { nombre: "Indigo", valor: "#6366F1" }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nombre.trim()) {
      toast.error("El nombre es requerido");
      return;
    }

    setSaving(true);
    try {
      if (categoria) {
        await categoriasAPI.update(categoria.id, formData);
        toast.success("Categoría actualizada exitosamente");
      } else {
        await categoriasAPI.create(formData);
        toast.success("Categoría creada exitosamente");
      }
      onClose(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar categoría");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-2xl font-bold text-slate-900">
            {categoria ? "Editar Categoría" : "Nueva Categoría"}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <Label>Nombre *</Label>
            <Input
              value={formData.nombre}
              onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              placeholder="Ej: Materiales Eléctricos"
              required
            />
          </div>

          <div>
            <Label>Color</Label>
            <div className="space-y-3">
              <div className="grid grid-cols-5 gap-2">
                {coloresComunes.map((color) => (
                  <button
                    key={color.valor}
                    type="button"
                    onClick={() => setFormData({ ...formData, color: color.valor })}
                    className={`w-12 h-12 rounded-lg border-2 transition-all ${formData.color === color.valor
                      ? "border-slate-900 scale-110"
                      : "border-slate-200 hover:scale-105"
                      }`}
                    style={{ backgroundColor: color.valor }}
                    title={color.nombre}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Label>Color personalizado:</Label>
                <input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="w-16 h-10 rounded border border-slate-300 cursor-pointer"
                />
                <span className="text-sm text-slate-600">{formData.color}</span>
              </div>
            </div>
          </div>

          <div>
            <Label>Descripción (opcional)</Label>
            <textarea
              value={formData.descripcion}
              onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
              placeholder="Describe el tipo de items de esta categoría"
              rows="3"
              className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex items-center space-x-2 p-3 bg-slate-50 rounded-md">
            <input
              type="checkbox"
              id="activa_categoria"
              checked={formData.activa}
              onChange={(e) => setFormData({ ...formData, activa: e.target.checked })}
              className="rounded border-slate-300"
            />
            <Label htmlFor="activa_categoria" className="cursor-pointer">
              Categoría activa (visible en formularios)
            </Label>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
            <Button
              type="button"
              variant="outline"
              onClick={() => onClose(false)}
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="bg-deflorencia hover:bg-[#ff9100]"
              disabled={saving}
            >
              {saving ? "Guardando..." : (categoria ? "Actualizar" : "Crear")}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ManualDeUsuario() {
  const handleDownloadPDF = async () => {
    const jsPDF = (await import('jspdf')).default;
    const html2canvas = (await import('html2canvas')).default;

    const element = document.getElementById('manual-content');
    const button = document.getElementById('download-button');

    // Ocultar botón durante la captura
    if (button) button.style.display = 'none';

    toast.info('Generando PDF... esto puede tomar unos segundos');

    try {
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save('Manual_Deflorencia.pdf');
      toast.success('PDF descargado exitosamente');
    } catch (error) {
      console.error('Error al generar PDF:', error);
      toast.error('Error al generar el PDF');
    } finally {
      // Mostrar botón nuevamente
      if (button) button.style.display = 'flex';
    }
  };

  return (
    <div className="max-w-5xl mx-auto">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-6">
          <div>
            <CardTitle className="text-3xl font-bold flex items-center gap-3">
              <BookOpen className="w-8 h-8 text-blue-600" />
              Manual de Usuario - Deflorencia
            </CardTitle>
            <CardDescription className="mt-2">Sistema de Gestión de Obras de Construcción</CardDescription>
          </div>
          <Button
            id="download-button"
            onClick={handleDownloadPDF}
            className="flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Descargar PDF
          </Button>
        </CardHeader>
        <CardContent>
          <div id="manual-content" className="prose prose-slate max-w-none">
            {/* Introducción */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 border-b-2 border-blue-600 pb-2 mb-4">
                🎯 ¿Qué es este sistema?
              </h2>
              <p className="text-slate-700 leading-relaxed">
                Deflorencia es un sistema completo para gestionar tus proyectos de construcción, desde la cotización inicial hasta el seguimiento de la obra.
              </p>
              <div className="bg-blue-50 border-l-4 border-blue-600 p-4 my-4">
                <p className="font-semibold text-blue-900 mb-2">Te ayuda a:</p>
                <ul className="list-disc list-inside space-y-1 text-slate-700">
                  <li>Crear presupuestos profesionales rápidamente</li>
                  <li>Enviar cotizaciones a tus clientes</li>
                  <li>Hacer seguimiento de cada proyecto</li>
                  <li>Calcular costos y ganancias automáticamente</li>
                  <li>Mantener todo organizado en un solo lugar</li>
                </ul>
              </div>
            </section>

            {/* Conceptos Básicos */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 border-b-2 border-blue-600 pb-2 mb-4">
                🏗️ Conceptos Básicos
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                  <h3 className="font-bold text-lg text-slate-800 mb-2">Obra</h3>
                  <p className="text-slate-600">Es tu proyecto de construcción (ej: “Edificio Solanas”, “Casa Familia Rodríguez”)</p>
                </div>

                <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                  <h3 className="font-bold text-lg text-slate-800 mb-2">Presupuesto</h3>
                  <p className="text-slate-600">Es el documento interno donde calculás todos los costos de la obra. Podés tener varias versiones.</p>
                </div>

                <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                  <h3 className="font-bold text-lg text-slate-800 mb-2">Cotización</h3>
                  <p className="text-slate-600">Es el documento formal (PDF) que le enviás al cliente con el precio final.</p>
                </div>

                <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                  <h3 className="font-bold text-lg text-slate-800 mb-2">Rubros</h3>
                  <p className="text-slate-600">Son las etapas de la obra (ej: Preliminares, Estructura, Mampostería, etc.)</p>
                </div>

                <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                  <h3 className="font-bold text-lg text-slate-800 mb-2">Items</h3>
                  <p className="text-slate-600">Son los materiales y mano de obra que usás (ej: Cemento, Hierro, Oficial, etc.)</p>
                </div>

                <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                  <h3 className="font-bold text-lg text-slate-800 mb-2">Items Compuestos</h3>
                  <p className="text-slate-600">Son “recetas” de elementos constructivos compuestos por varios items simples.</p>
                </div>
              </div>
            </section>

            {/* Guía Paso a Paso */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 border-b-2 border-blue-600 pb-2 mb-4">
                🚀 Guía Paso a Paso
              </h2>

              {/* Paso 1 */}
              <div className="mb-6">
                <h3 className="text-xl font-bold text-slate-800 bg-blue-100 p-3 rounded-t-lg">
                  PASO 1: Configurar el Catálogo 📦
                </h3>
                <div className="bg-white border border-blue-100 p-4 rounded-b-lg">
                  <p className="mb-3 text-slate-700">Antes de empezar, necesitás tener tus materiales y mano de obra cargados.</p>

                  <h4 className="font-semibold text-slate-800 mt-4 mb-2">1.1. Crear Items Simples</h4>
                  <ol className="list-decimal list-inside space-y-2 text-slate-700 ml-4">
                    <li>Click en el menú lateral → Catálogo</li>
                    <li>Click en “+ Nuevo Item”</li>
                    <li>Seleccioná “Simple”</li>
                    <li>Completá: Nombre, Unidad, Categoría, Precio de Referencia</li>
                  </ol>

                  <h4 className="font-semibold text-slate-800 mt-4 mb-2">1.2. Crear Items Compuestos</h4>
                  <ol className="list-decimal list-inside space-y-2 text-slate-700 ml-4">
                    <li>Click en “+ Nuevo Item”</li>
                    <li>Seleccioná “Compuesto”</li>
                    <li>Agregá los componentes (podés seleccionar existentes o crear nuevos)</li>
                    <li>El precio se calcula automáticamente</li>
                  </ol>

                  <div className="bg-amber-50 border-l-4 border-amber-500 p-3 mt-4">
                    <p className="text-sm text-amber-800">
                      <span className="font-semibold">💡 Tip:</span> El sistema te muestra la unidad del item al lado de la cantidad y calcula el precio automáticamente.
                    </p>
                  </div>
                </div>
              </div>

              {/* Paso 2 */}
              <div className="mb-6">
                <h3 className="text-xl font-bold text-slate-800 bg-green-100 p-3 rounded-t-lg">
                  PASO 2: Crear una Obra 🏗️
                </h3>
                <div className="bg-white border border-green-100 p-4 rounded-b-lg">
                  <ol className="list-decimal list-inside space-y-2 text-slate-700">
                    <li>Ir a “Obras” en el menú lateral</li>
                    <li>Click en “+ Nueva Obra”</li>
                    <li>Completar: Cliente, Ubicación, Metros cuadrados</li>
                    <li>Seleccionar los Rubros que vas a usar</li>
                    <li>Guardar</li>
                  </ol>
                </div>
              </div>

              {/* Paso 3 */}
              <div className="mb-6">
                <h3 className="text-xl font-bold text-slate-800 bg-purple-100 p-3 rounded-t-lg">
                  PASO 3: Crear un Presupuesto 💰
                </h3>
                <div className="bg-white border border-purple-100 p-4 rounded-b-lg">
                  <ol className="list-decimal list-inside space-y-2 text-slate-700">
                    <li>Ir a “Presupuestos”</li>
                    <li>Click en “+ Nuevo Presupuesto”</li>
                    <li>Seleccionar la obra y completar información</li>
                    <li>Configurar porcentajes (Costos Indirectos, Beneficios, Leyes Sociales, IVA)</li>
                    <li>Agregar items por rubro desde el catálogo</li>
                  </ol>

                  <div className="bg-green-50 border border-green-200 p-4 mt-4 rounded">
                    <p className="font-semibold text-green-800 mb-2">🎯 Importante: Porcentaje de Beneficios</p>
                    <p className="text-sm text-green-700">
                      El porcentaje de “Beneficios” (15-20%) es tu ganancia neta. Ajustalo según el proyecto: proyectos grandes 10-15%, proyectos pequeños 15-20%.
                    </p>
                  </div>
                </div>
              </div>

              {/* Paso 4 */}
              <div className="mb-6">
                <h3 className="text-xl font-bold text-slate-800 bg-orange-100 p-3 rounded-t-lg">
                  PASO 4: Marcar como “Listo” ✅
                </h3>
                <div className="bg-white border border-orange-100 p-4 rounded-b-lg">
                  <p className="mb-3 text-slate-700">Cuando terminaste de armar el presupuesto:</p>
                  <ol className="list-decimal list-inside space-y-2 text-slate-700">
                    <li>Verificar que todo esté correcto</li>
                    <li>Click en “Marcar como Listo”</li>
                  </ol>

                  <div className="bg-red-50 border-l-4 border-red-500 p-3 mt-4">
                    <p className="text-sm text-red-800">
                      <span className="font-semibold">⚠️ El sistema NO te deja marcarlo como Listo si:</span>
                    </p>
                    <ul className="list-disc list-inside ml-4 text-red-700 text-sm mt-2">
                      <li>No tiene items cargados</li>
                      <li>El total es $0</li>
                    </ul>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 p-3 mt-4 rounded">
                    <p className="font-semibold text-blue-800 mb-1">Una vez “Listo”:</p>
                    <ul className="list-disc list-inside text-sm text-blue-700">
                      <li>El presupuesto queda bloqueado (no se puede modificar)</li>
                      <li>Podés generar la cotización para el cliente</li>
                      <li>Si necesitás modificarlo, usás “Crear Nueva Versión”</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Paso 5 */}
              <div className="mb-6">
                <h3 className="text-xl font-bold text-slate-800 bg-indigo-100 p-3 rounded-t-lg">
                  PASO 5: Generar Cotización 📄
                </h3>
                <div className="bg-white border border-indigo-100 p-4 rounded-b-lg">
                  <ol className="list-decimal list-inside space-y-2 text-slate-700">
                    <li>Dentro del presupuesto “Listo”</li>
                    <li>Click en “📄 Generar Cotización”</li>
                    <li>Completar: Parámetros, Exclusiones, Condiciones de pago, Validez</li>
                    <li>Click “Generar”</li>
                  </ol>

                  <div className="bg-indigo-50 border border-indigo-200 p-4 mt-4 rounded">
                    <p className="font-semibold text-indigo-800 mb-2">El sistema crea:</p>
                    <ul className="list-disc list-inside text-sm text-indigo-700 space-y-1">
                      <li>Un PDF profesional con logo de Deflorencia</li>
                      <li>Un link público que le enviás al cliente</li>
                      <li>El cliente puede ver, descargar el PDF, y marcar como recibida/aceptada/rechazada</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* Modificar Presupuestos */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 border-b-2 border-blue-600 pb-2 mb-4">
                🔄 Modificar Presupuestos (Negociación con Cliente)
              </h2>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-4">
                <p className="font-semibold text-yellow-900">Situación:</p>
                <p className="text-yellow-800">El cliente te pide cambios después de ver la cotización.</p>
              </div>

              <div className="bg-green-50 border border-green-200 p-4 rounded">
                <p className="font-semibold text-green-900 mb-3">Solución:</p>
                <ol className="list-decimal list-inside space-y-2 text-green-800">
                  <li>Abrí el presupuesto “Listo”</li>
                  <li>Click en “📝 Crear Nueva Versión”</li>
                  <li>El sistema crea automáticamente la v2, v3, etc.</li>
                  <li>La nueva versión queda en “Borrador” (editable)</li>
                  <li>Hacés los cambios necesarios</li>
                  <li>Marcás como “Listo” nuevamente</li>
                  <li>Generás nueva cotización</li>
                </ol>

                <div className="mt-4 pt-4 border-t border-green-300">
                  <p className="font-semibold text-green-900 mb-2">💡 Beneficios del versionado:</p>
                  <ul className="list-disc list-inside text-sm text-green-700 space-y-1">
                    <li>Mantenés historial completo de todas las versiones</li>
                    <li>Sabés exactamente qué presupuesto vio cada cliente</li>
                    <li>No perdés información nunca</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Estados del Presupuesto */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 border-b-2 border-blue-600 pb-2 mb-4">
                📋 Estados del Presupuesto
              </h2>

              <div className="space-y-4">
                <div className="border-l-4 border-yellow-500 bg-yellow-50 p-4 rounded-r">
                  <h3 className="font-bold text-yellow-900 mb-2">🟡 BORRADOR</h3>
                  <ul className="list-disc list-inside text-yellow-800 text-sm space-y-1">
                    <li>Estás trabajando en él</li>
                    <li>Podés agregar, editar, eliminar items</li>
                    <li>Podés cambiar porcentajes</li>
                    <li>Podés eliminar el presupuesto completo</li>
                  </ul>
                </div>

                <div className="border-l-4 border-green-500 bg-green-50 p-4 rounded-r">
                  <h3 className="font-bold text-green-900 mb-2">🟢 LISTO</h3>
                  <ul className="list-disc list-inside text-green-800 text-sm space-y-1">
                    <li>Ya está terminado y revisado</li>
                    <li>NO se puede modificar directamente</li>
                    <li>Podés generar cotización</li>
                    <li>Podés crear nueva versión si necesitás cambios</li>
                  </ul>
                </div>

                <div className="border-l-4 border-blue-500 bg-blue-50 p-4 rounded-r">
                  <h3 className="font-bold text-blue-900 mb-2">🔵 CERRADO</h3>
                  <ul className="list-disc list-inside text-blue-800 text-sm space-y-1">
                    <li>Proyecto finalizado o cancelado</li>
                    <li>Solo lectura (histórico)</li>
                    <li>No se puede modificar ni eliminar</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Cálculo de Precios */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 border-b-2 border-blue-600 pb-2 mb-4">
                🔢 ¿Cómo se Calcula el Precio Final?
              </h2>

              <div className="bg-slate-50 border border-slate-300 p-6 rounded-lg">
                <h3 className="font-bold text-slate-800 mb-4">Ejemplo práctico:</h3>

                <div className="space-y-3 text-slate-700">
                  <div className="flex justify-between items-center pb-2 border-b">
                    <span><span className="font-semibold">1. Costos Base</span> (items cargados)</span>
                    <span className="font-mono">$150.000</span>
                  </div>

                  <div className="flex justify-between items-center pb-2 border-b">
                    <span><span className="font-semibold">2. + Costos Indirectos</span> (10%)</span>
                    <span className="font-mono">$165.000</span>
                  </div>

                  <div className="flex justify-between items-center pb-2 border-b bg-green-100 px-2 rounded">
                    <span><span className="font-semibold">3. + Beneficios</span> (15%) <span className="text-green-700">← TU GANANCIA</span></span>
                    <span className="font-mono font-bold">$189.750</span>
                  </div>

                  <div className="flex justify-between items-center pb-2 border-b">
                    <span><span className="font-semibold">4. + Leyes Sociales</span> (30%)</span>
                    <span className="font-mono">$246.675</span>
                  </div>

                  <div className="flex justify-between items-center pb-2 border-b">
                    <span><span className="font-semibold">5. + IVA</span> (22%)</span>
                    <span className="font-mono font-bold text-lg">$300.944</span>
                  </div>

                  <div className="mt-4 pt-4 border-t-2 border-slate-400">
                    <div className="flex justify-between items-center">
                      <span className="text-xl font-bold text-slate-900">PRECIO FINAL PARA EL CLIENTE:</span>
                      <span className="text-2xl font-bold text-green-600">$300.944</span>
                    </div>
                    <p className="text-sm text-green-700 mt-2 text-right">
                      Tu ganancia neta: <span className="font-bold">$24.750</span> (el 15% de beneficios)
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Tips y Buenas Prácticas */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 border-b-2 border-blue-600 pb-2 mb-4">
                💡 Tips y Buenas Prácticas
              </h2>

              <div className="grid grid-cols-1 gap-4">
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
                  <h3 className="font-bold text-blue-900 mb-2">1. Mantené el Catálogo Actualizado</h3>
                  <p className="text-blue-800 text-sm">Actualizá precios regularmente y agregá nuevos materiales según los uses.</p>
                </div>

                <div className="bg-green-50 border-l-4 border-green-500 p-4">
                  <h3 className="font-bold text-green-900 mb-2">2. Nombres Claros</h3>
                  <p className="text-green-800 text-sm">Usá nombres descriptivos: “Edificio Solanas - Freddy Noria”, “Presupuesto Inicial”</p>
                </div>

                <div className="bg-purple-50 border-l-4 border-purple-500 p-4">
                  <h3 className="font-bold text-purple-900 mb-2">3. Versiones de Presupuestos</h3>
                  <p className="text-purple-800 text-sm">Cuando negociás con un cliente, usá versiones. No modifiques el “Listo” directamente.</p>
                </div>

                <div className="bg-orange-50 border-l-4 border-orange-500 p-4">
                  <h3 className="font-bold text-orange-900 mb-2">4. Configuración de Ganancias</h3>
                  <p className="text-orange-800 text-sm">Proyectos grandes: 10-15% de beneficios. Proyectos pequeños: 15-20%.</p>
                </div>

                <div className="bg-indigo-50 border-l-4 border-indigo-500 p-4">
                  <h3 className="font-bold text-indigo-900 mb-2">5. Items Compuestos</h3>
                  <p className="text-indigo-800 text-sm">Usá items compuestos para hormigón armado. Ahorrás tiempo y sabés exactamente qué materiales necesitás.</p>
                </div>
              </div>
            </section>

            {/* Flujo Completo */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 border-b-2 border-blue-600 pb-2 mb-4">
                🎯 Flujo Completo - Resumen
              </h2>

              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-300 p-6 rounded-lg">
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">1</div>
                    <div>
                      <p className="font-bold text-slate-800">CONFIGURACIÓN INICIAL</p>
                      <p className="text-sm text-slate-600">Cargar items en Catálogo (materiales, M.O., items compuestos)</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="bg-green-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">2</div>
                    <div>
                      <p className="font-bold text-slate-800">NUEVA OBRA</p>
                      <p className="text-sm text-slate-600">Crear Obra con cliente, ubicación y rubros</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="bg-purple-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">3</div>
                    <div>
                      <p className="font-bold text-slate-800">PRESUPUESTAR</p>
                      <p className="text-sm text-slate-600">Crear Presupuesto, agregar items por rubro, configurar porcentajes</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">4</div>
                    <div>
                      <p className="font-bold text-slate-800">APROBAR</p>
                      <p className="text-sm text-slate-600">Marcar como “Listo” (presupuesto se bloquea)</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="bg-indigo-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">5</div>
                    <div>
                      <p className="font-bold text-slate-800">ENVIAR AL CLIENTE</p>
                      <p className="text-sm text-slate-600">Generar Cotización (PDF + link público)</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="bg-yellow-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">6</div>
                    <div>
                      <p className="font-bold text-slate-800">NEGOCIAR (si es necesario)</p>
                      <p className="text-sm text-slate-600">Crear Nueva Versión, hacer ajustes, generar nueva cotización</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="bg-teal-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">7</div>
                    <div>
                      <p className="font-bold text-slate-800">EJECUTAR</p>
                      <p className="text-sm text-slate-600">Crear Cronograma, planificar tareas, hacer seguimiento</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Preguntas Frecuentes */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-800 border-b-2 border-blue-600 pb-2 mb-4">
                ❓ Preguntas Frecuentes
              </h2>

              <div className="space-y-4">
                <details className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <summary className="font-semibold text-slate-800 cursor-pointer">
                    ¿Puedo modificar un presupuesto después de marcarlo como Listo?
                  </summary>
                  <p className="text-slate-600 mt-2 text-sm">No directamente, pero podés crear una nueva versión que copia todo y te permite modificar.</p>
                </details>

                <details className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <summary className="font-semibold text-slate-800 cursor-pointer">
                    ¿Qué pasa si el cliente rechaza la cotización?
                  </summary>
                  <p className="text-slate-600 mt-2 text-sm">Creás una nueva versión del presupuesto, hacés los ajustes necesarios, y generás una nueva cotización.</p>
                </details>

                <details className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <summary className="font-semibold text-slate-800 cursor-pointer">
                    ¿Puedo tener varios presupuestos para la misma obra?
                  </summary>
                  <p className="text-slate-600 mt-2 text-sm">Sí! Podés tener diferentes versiones o enfoques para una misma obra.</p>
                </details>

                <details className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <summary className="font-semibold text-slate-800 cursor-pointer">
                    ¿Los precios del catálogo se actualizan automáticamente en presupuestos viejos?
                  </summary>
                  <p className="text-slate-600 mt-2 text-sm">No. Cada presupuesto guarda el precio que tenía al momento de crearlo. Esto mantiene consistencia.</p>
                </details>

                <details className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <summary className="font-semibold text-slate-800 cursor-pointer">
                    ¿Cómo sé cuánto voy a ganar en un proyecto?
                  </summary>
                  <p className="text-slate-600 mt-2 text-sm">Mirá el porcentaje de “Beneficios” en el resumen financiero del presupuesto. Ese es tu margen de ganancia.</p>
                </details>

                <details className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <summary className="font-semibold text-slate-800 cursor-pointer">
                    ¿Qué son los items compuestos?
                  </summary>
                  <p className="text-slate-600 mt-2 text-sm">Son “recetas” de elementos constructivos. Ej: Un panel de hormigón que está compuesto por cemento, arena, hierro, y horas de oficial.</p>
                </details>
              </div>
            </section>

            {/* Footer */}
            <section className="border-t-2 border-slate-300 pt-6 mt-8">
              <div className="text-center">
                <p className="text-2xl font-bold text-slate-800 mb-2">¡Éxitos con tus proyectos! 🎉</p>
                <p className="text-slate-600">Sistema Deflorencia - Gestión Profesional de Obras de Construcción</p>
              </div>
            </section>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== FINANZAS ====================
function Finanzas() {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      // Inicializar conceptos si es necesario
      // await conceptosFinancierosAPI.inicializar();
    } catch (error) {
      console.error("Error al cargar datos:", error);
      toast.error("Error al cargar datos de finanzas");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-slate-900">Finanzas</h1>
        <div className="py-12 text-center text-slate-600">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-slate-900">Finanzas</h1>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <div className="flex space-x-8">
          <button
            onClick={() => setActiveTab("dashboard")}
            className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === "dashboard"
              ? "border-blue-500 text-blue-600"
              : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
          >
            Dashboard
          </button>
          <button
            onClick={() => setActiveTab("movimientos")}
            className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === "movimientos"
              ? "border-blue-500 text-blue-600"
              : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
          >
            Movimientos
          </button>
          <button
            onClick={() => setActiveTab("conceptos")}
            className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === "conceptos"
              ? "border-blue-500 text-blue-600"
              : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
          >
            Conceptos
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="mt-6">
        {activeTab === "dashboard" && <FinanzasDashboard />}
        {activeTab === "movimientos" && <FinanzasMovimientos />}
        {activeTab === "conceptos" && <FinanzasConceptos />}
      </div>
    </div>
  );
}

// Sub-componente: Dashboard
function FinanzasDashboard() {
  const [resumen, setResumen] = useState({
    total_ingresos: 0,
    total_egresos: 0,
    balance: 0,
    por_obra: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadResumen();
  }, []);

  const loadResumen = async () => {
    try {
      const response = await movimientosFinancierosAPI.getDashboard();
      setResumen(response.data);
    } catch (error) {
      toast.error("Error al cargar resumen financiero");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="py-8 text-center text-slate-600">Cargando...</div>;

  return (
    <div className="space-y-6">
      {/* Resumen General */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Total Ingresos</CardDescription>
            <CardTitle className="text-3xl text-green-600">
              {formatCurrency(resumen.total_ingresos)}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Total Egresos</CardDescription>
            <CardTitle className="text-3xl text-red-600">
              {formatCurrency(resumen.total_egresos)}
            </CardTitle>
          </CardHeader>



        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Balance</CardDescription>
            <CardTitle className={`text-3xl ${resumen.balance >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
              {formatCurrency(resumen.balance)}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Resumen por Obra */}
      {resumen.por_obra.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Resumen por Obra</CardTitle>
            <CardDescription>Estado financiero de cada proyecto</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Obra</TableHead>
                    <TableHead className="text-right">Ingresos</TableHead>
                    <TableHead className="text-right">Egresos</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {resumen.por_obra.map(obra => (
                    <TableRow key={obra.obra_id}>
                      <TableCell className="font-medium">{obra.obra_nombre}</TableCell>
                      <TableCell className="text-right text-green-600">
                        {formatCurrency(obra.ingresos)}
                      </TableCell>
                      <TableCell className="text-right text-red-600">
                        {formatCurrency(obra.egresos)}
                      </TableCell>
                      <TableCell className={`text-right font-semibold ${obra.balance >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                        {formatCurrency(obra.balance)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {resumen.por_obra.length === 0 && (
        <Card>
          <CardContent className="py-8 text-center text-slate-500">
            No hay movimientos registrados aún.
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Sub-componente: Movimientos
function FinanzasMovimientos() {
  const [movimientos, setMovimientos] = useState([]);
  const [obras, setObras] = useState([]);
  const [presupuestos, setPresupuestos] = useState([]);
  const [conceptos, setConceptos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingMovimiento, setEditingMovimiento] = useState(null);
  const [expandedObras, setExpandedObras] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 25;
  const [formData, setFormData] = useState({
    tipo: "Egreso",
    monto: "",
    fecha: new Date().toISOString().split('T')[0],
    concepto_id: "",
    obra_id: "",
    presupuesto_id: "",
    metodo_pago: "Efectivo",
    es_no_registrado: false,
    comprobante: "",
    notas: ""
  });

  useEffect(() => {
    if (modalOpen) {
      // En mobile/desktop: asegurar que el usuario vea el inicio del modal
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [modalOpen]);

  useEffect(() => {
    // Si el movimiento no tiene obra, ocultar presupuesto seleccionado (no aplica)
    if (!formData.obra_id && formData.presupuesto_id) {
      setFormData(prev => ({ ...prev, presupuesto_id: "" }));
    }
  }, [formData.obra_id]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [movRes, obrasRes, presRes, concRes] = await Promise.all([
        movimientosFinancierosAPI.getAll(),
        obrasAPI.getAll(),
        presupuestosAPI.getAll(),
        conceptosFinancierosAPI.getAll()
      ]);
      setMovimientos(movRes.data);
      setObras(obrasRes.data);
      setPresupuestos(presRes.data.filter(p => p.estado === "Listo" || p.estado === "Cerrado"));
      setConceptos(concRes.data);
    } catch (error) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (movimiento = null) => {
    if (movimiento) {
      setEditingMovimiento(movimiento);
      setFormData({
        tipo: movimiento.tipo,
        monto: movimiento.monto,
        fecha: movimiento.fecha,
        concepto_id: movimiento.concepto_id,
        obra_id: movimiento.obra_id || "",
        presupuesto_id: movimiento.presupuesto_id || "",
        metodo_pago: movimiento.metodo_pago,
        es_no_registrado: movimiento.es_no_registrado,
        comprobante: movimiento.comprobante || "",
        notas: movimiento.notas || ""
      });
    } else {
      setEditingMovimiento(null);
      setFormData({
        tipo: "Egreso",
        monto: "",
        fecha: new Date().toISOString().split('T')[0],
        concepto_id: "",
        obra_id: "",
        presupuesto_id: "",
        metodo_pago: "Efectivo",
        es_no_registrado: false,
        comprobante: "",
        notas: ""
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingMovimiento(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData, monto: parseFloat(formData.monto) };
      if (!data.obra_id) delete data.obra_id;
      if (!data.presupuesto_id) delete data.presupuesto_id;
      if (!data.comprobante) delete data.comprobante;
      if (!data.notas) delete data.notas;

      if (editingMovimiento) {
        await movimientosFinancierosAPI.update(editingMovimiento.id, data);
        toast.success("Movimiento actualizado");
      } else {
        await movimientosFinancierosAPI.create(data);
        toast.success("Movimiento registrado");
      }
      handleCloseModal();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar movimiento");
    }
  };

  const toggleObraExpanded = (obraId) => {
    setExpandedObras(prev => ({
      ...prev,
      [obraId]: !prev[obraId]
    }));
  };

  useEffect(() => {
    setCurrentPage(1);
    setExpandedObras({});
  }, [movimientos.length]);


  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este movimiento?")) return;
    try {
      await movimientosFinancierosAPI.delete(id);
      toast.success("Movimiento eliminado");
      loadData();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const conceptosFiltrados = conceptos.filter(c => c.tipo === formData.tipo);

  // Agrupar movimientos por obra
  const movimientosOrdenados = [...movimientos].sort((a, b) => {
    return new Date(b.fecha) - new Date(a.fecha);
  });

  const movimientosPorObra = movimientosOrdenados.reduce((acc, mov) => {
    const key = mov.obra_id || "SIN_OBRA";
    if (!acc[key]) acc[key] = [];
    acc[key].push(mov);
    return acc;
  }, {});

  const grupos = Object.entries(movimientosPorObra).map(([obraId, movs]) => ({
    obraId,
    obraNombre: obraId === "SIN_OBRA" ? "Sin obra" : (obras.find(o => o.id === obraId)?.nombre || "Sin obra"),
    movimientos: movs
  }));

  // Ordenar grupos por nombre de obra
  grupos.sort((a, b) => a.obraNombre.localeCompare(b.obraNombre));

  // Paginación a nivel de obra (grupos)
  const totalPages = Math.ceil(grupos.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedGrupos = grupos.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  if (loading) return <div className="py-8 text-center text-slate-600">Cargando...</div>;

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Movimientos Financieros</CardTitle>
              <CardDescription>Registro de todos los ingresos y egresos</CardDescription>
            </div>
            <Button onClick={() => handleOpenModal()}>
              + Nuevo Movimiento
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {movimientos.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              No hay movimientos registrados. Creá el primero.
            </div>
          ) : (
            <>
              <div className="divide-y divide-slate-200 border border-slate-200 rounded-md overflow-hidden">
                {paginatedGrupos.map(({ obraId, obraNombre, movimientos: movs }) => {
                  const isExpanded = expandedObras[obraId] || false;
                  const totalIngresos = movs.filter(m => m.tipo === 'Ingreso').reduce((sum, m) => sum + (m.monto || 0), 0);
                  const totalEgresos = movs.filter(m => m.tipo === 'Egreso').reduce((sum, m) => sum + (m.monto || 0), 0);
                  const balance = totalIngresos - totalEgresos;

                  return (
                    <div key={obraId} className="bg-white">
                      {/* Header Obra */}
                      <div
                        className="flex items-center p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                        onClick={() => toggleObraExpanded(obraId)}
                      >
                        <div className="flex-1 grid grid-cols-12 gap-4 items-center">
                          <div className="col-span-4 flex items-center gap-3">
                            <span className={`text-slate-400 transform transition-transform duration-500 ease-in-out ${isExpanded ? 'rotate-90' : 'rotate-0'
                              }`}>
                              ▶
                            </span>
                            <div>
                              <div className="font-semibold text-slate-900">{obraNombre}</div>
                              <div className="text-xs text-slate-500">
                                {movs.length} movimiento{movs.length !== 1 ? 's' : ''}
                              </div>
                            </div>
                          </div>

                          <div className="col-span-3 text-right">
                            <div className="text-xs text-slate-500">Ingresos</div>
                            <div className="font-semibold text-green-700">{formatCurrency(totalIngresos)}</div>
                          </div>

                          <div className="col-span-3 text-right">
                            <div className="text-xs text-slate-500">Egresos</div>
                            <div className="font-semibold text-red-700">{formatCurrency(totalEgresos)}</div>
                          </div>

                          <div className="col-span-2 text-right">
                            <div className="text-xs text-slate-500">Balance</div>
                            <div className={`font-semibold ${balance >= 0 ? 'text-slate-900' : 'text-red-700'}`}>
                              {formatCurrency(balance)}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Movimientos expandido */}
                      <div
                        className={`overflow-hidden transition-all duration-500 ease-in-out ${isExpanded ? 'max-h-[900px] opacity-100' : 'max-h-0 opacity-0'
                          }`}
                      >
                        <div className="overflow-x-auto border-t border-slate-100">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Fecha</TableHead>
                                <TableHead>Tipo</TableHead>
                                <TableHead>Concepto</TableHead>
                                <TableHead>Método</TableHead>
                                <TableHead className="text-right">Monto</TableHead>
                                <TableHead className="text-center">Acciones</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {movs.map(mov => (
                                <TableRow key={mov.id} className="hover:bg-blue-50">
                                  <TableCell>{formatDate(mov.fecha)}</TableCell>
                                  <TableCell>
                                    <Badge className={mov.tipo === "Ingreso" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>
                                      {mov.tipo === "Ingreso" ? "Ingreso" : "Egreso"}
                                    </Badge>
                                  </TableCell>
                                  <TableCell>{mov.concepto_nombre || "N/A"}</TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-1">
                                      {mov.metodo_pago}
                                      {mov.es_no_registrado && (
                                        <Badge className="ml-1 bg-amber-100 text-amber-800 text-xs">No Reg.</Badge>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-right font-semibold">{formatCurrency(mov.monto)}</TableCell>
                                  <TableCell className="text-center">
                                    <div className="flex gap-1 justify-center">
                                      <Button size="sm" variant="ghost" onClick={() => handleOpenModal(mov)}>
                                        Editar
                                      </Button>
                                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDelete(mov.id)}>
                                        Eliminar
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                totalItems={grupos.length}
                itemsPerPage={ITEMS_PER_PAGE}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Modal Crear/Editar Movimiento */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 my-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              {editingMovimiento ? "Editar Movimiento" : "Nuevo Movimiento"}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Tipo *</Label>
                  <select
                    className="w-full border border-slate-300 rounded-md p-2"
                    value={formData.tipo}
                    onChange={(e) => setFormData({ ...formData, tipo: e.target.value, concepto_id: "" })}
                    required
                  >
                    <option value="Ingreso">📈 Ingreso</option>
                    <option value="Egreso">📉 Egreso</option>
                  </select>
                </div>
                <div>
                  <Label>Concepto *</Label>
                  <select
                    className="w-full border border-slate-300 rounded-md p-2"
                    value={formData.concepto_id}
                    onChange={(e) => setFormData({ ...formData, concepto_id: e.target.value })}
                    required
                  >
                    <option value="">Seleccionar...</option>
                    {conceptosFiltrados.map(c => (
                      <option key={c.id} value={c.id}>{c.nombre}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Monto *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.monto}
                    onChange={(e) => setFormData({ ...formData, monto: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Fecha *</Label>
                  <Input
                    type="date"
                    value={formData.fecha}
                    onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Obra (Opcional)</Label>
                  <select
                    className="w-full border border-slate-300 rounded-md p-2"
                    value={formData.obra_id}
                    onChange={(e) => setFormData({ ...formData, obra_id: e.target.value })}
                  >
                    <option value="">Sin obra</option>
                    {obras.map(o => (
                      <option key={o.id} value={o.id}>{o.nombre}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Presupuesto (Opcional)</Label>
                  <select
                    className="w-full border border-slate-300 rounded-md p-2"
                    value={formData.presupuesto_id}
                    onChange={(e) => setFormData({ ...formData, presupuesto_id: e.target.value })}
                  >
                    <option value="">Sin presupuesto</option>
                    {presupuestos.map(p => (
                      <option key={p.id} value={p.id}>{p.numero}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Método de Pago *</Label>
                  <select
                    className="w-full border border-slate-300 rounded-md p-2"
                    value={formData.metodo_pago}
                    onChange={(e) => setFormData({ ...formData, metodo_pago: e.target.value })}
                    required
                  >
                    <option value="Efectivo">Efectivo</option>
                    <option value="Transferencia">Transferencia</option>
                    <option value="Cheque">Cheque</option>
                  </select>
                </div>
                <div>
                  <Label>Comprobante (Opcional)</Label>
                  <Input
                    value={formData.comprobante}
                    onChange={(e) => setFormData({ ...formData, comprobante: e.target.value })}
                    placeholder="Nro. de factura, recibo..."
                  />
                </div>
              </div>
              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.es_no_registrado}
                    onChange={(e) => setFormData({ ...formData, es_no_registrado: e.target.checked })}
                  />
                  <span className="text-sm text-slate-700">Marcar como “No Registrado” (pago en negro)</span>
                </label>
              </div>
              <div>
                <Label>Notas (Opcional)</Label>
                <textarea
                  className="w-full border border-slate-300 rounded-md p-2"
                  rows="2"
                  value={formData.notas}
                  onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                  placeholder="Detalles adicionales del movimiento..."
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={handleCloseModal}>
                  Cancelar
                </Button>
                <Button type="submit">
                  {editingMovimiento ? "Actualizar" : "Registrar"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

// Sub-componente: Conceptos
function FinanzasConceptos() {
  const [conceptos, setConceptos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingConcepto, setEditingConcepto] = useState(null);
  const [formData, setFormData] = useState({
    nombre: "",
    tipo: "Egreso",
    descripcion: ""
  });

  useEffect(() => {
    loadConceptos();
  }, []);

  const loadConceptos = async () => {
    try {
      const response = await conceptosFinancierosAPI.getAll();
      setConceptos(response.data);
    } catch (error) {
      toast.error("Error al cargar conceptos");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (concepto = null) => {
    if (concepto) {
      setEditingConcepto(concepto);
      setFormData({
        nombre: concepto.nombre,
        tipo: concepto.tipo,
        descripcion: concepto.descripcion || ""
      });
    } else {
      setEditingConcepto(null);
      setFormData({
        nombre: "",
        tipo: "Egreso",
        descripcion: ""
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingConcepto(null);
    setFormData({ nombre: "", tipo: "Egreso", descripcion: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingConcepto) {
        await conceptosFinancierosAPI.update(editingConcepto.id, formData);
        toast.success("Concepto actualizado");
      } else {
        await conceptosFinancierosAPI.create(formData);
        toast.success("Concepto creado");
      }
      handleCloseModal();
      loadConceptos();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar concepto");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este concepto?")) return;
    try {
      await conceptosFinancierosAPI.delete(id);
      toast.success("Concepto eliminado");
      loadConceptos();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar");
    }
  };

  const conceptosIngresos = conceptos.filter(c => c.tipo === "Ingreso");
  const conceptosEgresos = conceptos.filter(c => c.tipo === "Egreso");

  if (loading) return <div className="py-8 text-center text-slate-600">Cargando...</div>;

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Conceptos Financieros</CardTitle>
              <CardDescription>Categorías personalizadas de ingresos y egresos</CardDescription>
            </div>
            <Button onClick={() => handleOpenModal()}>
              + Nuevo Concepto
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Ingresos */}
            <div>
              <h3 className="text-lg font-semibold text-green-700 mb-3">📈 Ingresos</h3>
              <div className="space-y-2">
                {conceptosIngresos.map(concepto => (
                  <div key={concepto.id} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium text-slate-800">{concepto.nombre}</p>
                      {concepto.descripcion && (
                        <p className="text-sm text-slate-600">{concepto.descripcion}</p>
                      )}
                      {concepto.es_sistema && (
                        <Badge className="mt-1 bg-blue-100 text-blue-800">Sistema</Badge>
                      )}
                    </div>
                    {!concepto.es_sistema && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleOpenModal(concepto)}
                        >
                          Editar
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDelete(concepto.id)}
                        >
                          Eliminar
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Egresos */}
            <div>
              <h3 className="text-lg font-semibold text-red-700 mb-3">📉 Egresos</h3>
              <div className="space-y-2">
                {conceptosEgresos.map(concepto => (
                  <div key={concepto.id} className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium text-slate-800">{concepto.nombre}</p>
                      {concepto.descripcion && (
                        <p className="text-sm text-slate-600">{concepto.descripcion}</p>
                      )}
                      {concepto.es_sistema && (
                        <Badge className="mt-1 bg-blue-100 text-blue-800">Sistema</Badge>
                      )}
                    </div>
                    {!concepto.es_sistema && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleOpenModal(concepto)}
                        >
                          Editar
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDelete(concepto.id)}
                        >
                          Eliminar
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Modal Crear/Editar Concepto */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              {editingConcepto ? "Editar Concepto" : "Nuevo Concepto"}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Nombre</Label>
                <Input
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Tipo</Label>
                <select
                  className="w-full border border-slate-300 rounded-md p-2"
                  value={formData.tipo}
                  onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                >
                  <option value="Ingreso">Ingreso</option>
                  <option value="Egreso">Egreso</option>
                </select>
              </div>
              <div>
                <Label>Descripción (Opcional)</Label>
                <Input
                  value={formData.descripcion}
                  onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={handleCloseModal}>
                  Cancelar
                </Button>
                <Button type="submit">
                  {editingConcepto ? "Actualizar" : "Crear"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

// ==================== APP MAIN ====================
function App() {

  return (
    <BrowserRouter basename="/backoffice">
      <Toaster position="top-right" />
      <Routes>
        {/* Ruta pública sin Layout */}
        <Route path="/cotizacion-publica/:link" element={<CotizacionPublica />} />

        {/* Rutas protegidas con Layout */}
        <Route path="/*" element={
          <Layout>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/obras" element={<Obras />} />
              <Route path="/presupuestos" element={<Presupuestos />} />
              <Route path="/presupuestos/:id" element={<DetallePresupuesto />} />
              <Route path="/cotizaciones" element={<Cotizaciones />} />
              <Route path="/cotizaciones/:id" element={<DetalleCotizacion />} />
              <Route path="/cronogramas" element={<Cronogramas />} />
              <Route path="/cronogramas/:id" element={<DetalleCronograma />} />
              <Route path="/gastos" element={<Gastos />} />
              <Route path="/finanzas" element={<Finanzas />} />
              <Route path="/certificados" element={<Certificados />} />
              <Route path="/catalogo" element={<Catalogo />} />
              <Route path="/manual" element={<ManualDeUsuario />} />
              <Route path="/configuracion" element={<Configuracion />} />
            </Routes>
          </Layout>
        } />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
